/**
 * PlaygroundView — React port of the Managed Agent runner Dashboard layout.
 *
 * Mirrors the dashboard's structure: left nav sidebar, Chat page (session sidebar +
 * chat area), Cards page (template sidebar + 3-col detail). Uses Handlebars
 * template renderer for card rendering via hot-updatable HTML templates.
 *
 * Sessions are loaded from api.getSessions() (shared with HistoryView).
 * Templates are loaded from Managed Agent runner dashboard API.
 *
 * NOTE: No camera/mic/LiveKit access — this is a desktop-only playground view.
 * Do NOT import useLiveKit, navigator.mediaDevices, or AudioContext here.
 */

import { useState, useEffect, useCallback, useRef, useMemo, useSyncExternalStore, Suspense, Fragment } from 'react';
import { Routes, Route, Navigate, NavLink, Link, Outlet, useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import MarkdownRenderer from './MarkdownRenderer';
import ThinkingPlanCard from './modules/ThinkingPlanCard';
import PersistentHtmlRenderer from './PersistentHtmlRenderer';
import { renderTemplate, clearTemplateCache, normalizeSessionDetailCards, listBundledTemplates } from '../services/templateRenderer';
import { normalizeMediaUrl } from '../utils/session';
import { sessionDetailToPlaygroundSession } from '../utils/playgroundSession';
import { api } from '../services/api';
import { IS_LABEL_RULES } from '../constants';
import BenchmarkPage from './BenchmarkPage';
import BatchProgressPage from './BatchProgressPage';
import BrowserHandoffPage from './BrowserHandoffPage';
import WidgetCatalog from './widget/WidgetCatalog';
import WidgetChatCover from './widget/WidgetChatCover';
import WidgetPresetGallery from './widget/WidgetPresetGallery';
// The NATIVE (ViewIR) studio — blueprints rendered by the native component
// kit, plus the component-registry browser. See backend/widget-native/README.md.
import WidgetNativeStudio from './widget-native/studio/WidgetNativeStudio';
import { bootstrapHref } from './widget-native/studio/studioNav';
import LogCard from './session/LogCard';
import SessionBlockCard from './session/SessionBlockCard';
import A2uiSurfaceCard from './session/A2uiSurfaceCard';
import { cardBodyFrameStyle } from './cardBodyFrame';
import { rendererCapabilities as a2uiRendererCapabilities } from '../a2ui/catalog';
import BatchProgressCard from './session/BatchProgressCard';
import WidgetSuggestionCard from './session/WidgetSuggestionCard';
import MailCard from './session/mail/MailCard';
import { isMailTemplate, mailLinkHref } from './session/mail/mailUtils';
import WidgetPanel from './session/WidgetPanel';
import { handlePlaygroundCardAction } from '../utils/playgroundCardActions';

// First fetchable image handle from a send's mediaUrls (string ref/url, or a
// {media_id|url} object). The widget-suggest endpoint resolves a media:// ref.
function firstImageHandle(mediaUrls) {
  const m = (mediaUrls || [])[0];
  if (!m) return undefined;
  if (typeof m === 'string') return m;
  if (m.url) return m.url;
  if (m.media_id) return `media://${m.media_id}`;
  return undefined;
}

const PLAYGROUND_CLIENT_FLOW_STORAGE_PREFIX = 'playground.clientFlow.';

// The Continue button no longer bakes the resume prompt — it sends `resume:true` with an empty prompt
// and the SERVER authors the continue instruction (#3490), choosing explicit ("you were cut off,
// resume") vs implicit (agent-agnostic — for a runner-decided credits/spend pause) by the prior
// round's exec_stopped.reason. See api-server services/continue_prompts.py.

function newClientFlowId(prefix = 'pg') {
  const id = globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `${prefix}-${id}`;
}

function writePlaygroundClientFlow(sessionId, payload) {
  if (!sessionId || !payload?.client_flow_id) return;
  try {
    localStorage.setItem(
      `${PLAYGROUND_CLIENT_FLOW_STORAGE_PREFIX}${sessionId}`,
      JSON.stringify({ ...payload, sessionId, updated_at_ms: Date.now() }),
    );
  } catch (_) { /* storage unavailable */ }
}

function readPlaygroundClientFlow(sessionId) {
  if (!sessionId) return null;
  try {
    const raw = localStorage.getItem(`${PLAYGROUND_CLIENT_FLOW_STORAGE_PREFIX}${sessionId}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (_) {
    return null;
  }
}

function isMeaningfulCardPayload(data) {
  if (!data || typeof data !== 'object') return false;
  for (const key of ['message', 'text', 'summary', 'html', 'markdown', 'content', 'body', 'title']) {
    const value = data[key];
    if (typeof value === 'string' && value.trim()) return true;
  }
  for (const key of ['items', 'blocks', 'cards', 'rows']) {
    if (Array.isArray(data[key]) && data[key].length) return true;
  }
  return false;
}

function isAnswerTemplate(template) {
  if (!template) return false;
  if (INFRASTRUCTURE_TEMPLATES.has(template)) return false;
  if (template === 'debug-html') return false;
  return true;
}

function isMeaningfulAnswerText(value) {
  if (typeof value !== 'string') return false;
  const text = value.trim();
  if (!text) return false;
  const genericStatuses = new Set([
    'completed',
    'done',
    'execution completed',
    'success',
    'task completed',
    'task failed',
  ]);
  return !genericStatuses.has(text.toLowerCase());
}

function isAnswerBearingSessionEvent(event, cardTemplateMap) {
  if (!event || typeof event !== 'object') return false;
  const opOrType = event.op || event.type;
  const template = event.template || (event.cardId ? cardTemplateMap[event.cardId] : null);
  if (event.cardId && event.template) {
    cardTemplateMap[event.cardId] = event.template;
  }
  if ((opOrType === 'create_card' || opOrType === 'replace_card' || opOrType === 'update_card') && isAnswerTemplate(template)) {
    return isMeaningfulCardPayload(event.data || event.updates);
  }
  if (opOrType === 'stream_to_card' && isAnswerTemplate(template)) {
    return typeof event.chunk === 'string' && event.chunk.trim().length > 0;
  }
  if (opOrType === 'append_to_card' && isAnswerTemplate(template)) {
    return Array.isArray(event.items) && event.items.length > 0;
  }
  if (opOrType === 'exec_result') {
    return [event.summary, event.result_summary, event.result].some(isMeaningfulAnswerText);
  }
  return false;
}

function sessionEventType(event) {
  return event?.eventType || event?.op || event?.type || event?.template || 'session_event';
}

// The widget panel's empty state (module-level so its identity is stable across
// renders — a session with no exec yet shows this).
const EMPTY_WIDGET_SUGGEST = { data: null, attention: false, loading: false, mediaUrls: [] };
import WidgetEditor from './widget/WidgetEditor';
import { widgetInstances } from '../services/widgetApi';
import { useSessionEventStream } from '../hooks/useSessionEventStream';
import MdStreamingRenderer, { STREAMABLE_TEMPLATES } from './MdStreamingRenderer';
import useComposioApps from '../hooks/useComposioApps';
import RoutinesPage from './RoutinesPage';
import CollapsibleBoundary from './CollapsibleBoundary';
import { mapBookToOptionCards } from './playgroundBookCards';
import {
  STATUS_COLORS,
  STATUS_FILTER_OPTIONS,
  filterSessions,
  getStatusBucket,
} from '../utils/sessionStatus';

// ── Model selector constants ──
// `auto` = defer to the SERVER: send no model, so the fast-runner resolves it
// (per-request > admin-configured runner model in Redis `agent:runner:model:*` >
// its DEFAULT_MODEL). So "Auto" always follows whatever model is set for the
// runner on admin web.
// `system` is NOT user-selectable; it only exists so ProducerBadge can label
// cards emitted by the runtime itself (e.g. fast-executor system-backstop).
//
// The real list is LIVE: `/api/config` returns `model_options` from the gateway
// `llm_models` catalog — the same source the admin web's runner-model dropdown
// reads — so the selector can never drift from the admin. DEFAULT_MODELS is only
// the offline fallback used before the fetch lands (or when it returns nothing).
const DEFAULT_MODELS = [
  { id: 'auto', label: 'Model: Auto' },
  { id: 'claude-opus-4-6', label: 'Opus 4.6' },
  { id: 'claude-sonnet-4-6', label: 'Sonnet 4.6' },
  { id: 'claude-haiku-4-5', label: 'Haiku 4.5' },
  { id: 'gemini-3-flash', label: 'Gemini 3 Flash' },
  { id: 'gemini-3.1-pro', label: 'Gemini 3.1 Pro' },
  { id: 'gemini-3.1-flash-lite', label: 'Gemini 3.1 Flash Lite' },
  { id: 'MiniMax-M2.7', label: 'MiniMax 2.7' },
  { id: 'kimi-k2.5', label: 'Kimi K2.5' },
  { id: 'system', label: 'System' },
];
// Entries hidden from the user-facing selector (kept in the catalog so
// ProducerBadge label lookup still resolves them).
const HIDDEN_SELECTOR_MODELS = new Set(['system']);
const DEFAULT_MODEL = 'auto';

// ── Live model catalog ──
// Module-level so every consumer (selector, locked badge, ProducerBadge, session
// rows) reads one list, fetched once per page load. Components that must re-render
// when it lands subscribe via `useModelCatalog()`; pure label lookups just call
// `catalogModelLabel()`.
let modelCatalog = DEFAULT_MODELS;
let modelCatalogPromise = null;
const modelCatalogSubs = new Set();

function loadModelCatalog() {
  if (modelCatalogPromise) return modelCatalogPromise;
  // Promise.resolve().then(...) so a synchronous throw (or a non-promise stub in
  // tests) degrades to the fallback instead of breaking the component's mount.
  modelCatalogPromise = Promise.resolve()
    .then(() => api.request('/api/config', {}, { retries: 0, timeout: 3000 }))
    .then(c => {
      schemeAutoModels = (c && typeof c.scheme_auto_models === 'object' && c.scheme_auto_models) || {};
      const opts = Array.isArray(c?.model_options) ? c.model_options : [];
      // Per-model reasoning default from the SAME catalog row the fast runner reads
      // (llm_models.config.default_reasoning_effort) — see modelDefaultEffort below.
      modelDefaultEfforts = Object.fromEntries(
        opts.filter(o => o?.id && o.default_reasoning_effort)
          .map(o => [o.id, String(o.default_reasoning_effort)]),
      );
      // ...and its reasoning CAPABILITY (#4787): the efforts it accepts — see reasoningSupported.
      modelReasoningEfforts = Object.fromEntries(
        opts.filter(o => o?.id && Array.isArray(o.reasoning_efforts) && o.reasoning_efforts.length)
          .map(o => [o.id, o.reasoning_efforts.map(String)]),
      );
      // `auto` (server-resolved) stays first and `system` (hidden, label-only)
      // stays last — the catalog fills the middle. An empty/absent catalog keeps
      // DEFAULT_MODELS, but still hands out a FRESH array: `useSyncExternalStore`
      // compares snapshots by reference and the `auto` label now depends on
      // `schemeAutoModels`, so the selector has to re-render either way.
      modelCatalog = opts.length
        ? [
          { id: 'auto', label: 'Model: Auto' },
          ...opts.filter(o => o?.id).map(o => ({ id: o.id, label: o.label || o.id })),
          { id: 'system', label: 'System' },
        ]
        : [...modelCatalog];
      modelCatalogSubs.forEach(fn => fn());
    })
    .catch(() => { /* offline / not deployed yet → DEFAULT_MODELS */ });
  return modelCatalogPromise;
}

// Subscribing IS what warms the catalog — the fetch fires on the first subscriber.
function subscribeModelCatalog(onChange) {
  modelCatalogSubs.add(onChange);
  loadModelCatalog();
  return () => { modelCatalogSubs.delete(onChange); };
}

// The live catalog, re-rendering the caller when the fetch lands.
// `useSyncExternalStore` over a module store: the snapshot is a stable array
// reference that only changes when the catalog is replaced.
function useModelCatalog() {
  return useSyncExternalStore(subscribeModelCatalog, () => modelCatalog);
}

// Display label for a model id, from whatever catalog is currently loaded.
function catalogModelLabel(id) {
  return modelCatalog.find(m => m.id === id)?.label ?? id ?? '';
}

// ── What "auto" actually resolves to, per render scheme ──
// `/api/config.scheme_auto_models` mirrors the fast runner's OWN ladder
// (per-scheme Redis HASH > global runner model > its DEFAULT_MODEL) — the exact
// path a request with no `model` takes — so the selector can NAME the model
// instead of showing a bare "Auto". Field absent (older api-server) → `{}` →
// every caller below degrades to today's "Auto".
let schemeAutoModels = {};

// The model `auto` resolves to for `scheme`; '' when it isn't knowable here.
// An explicit scheme reads its binding directly. `auto`/unset answers only when
// every scheme agrees — with a split binding the server picks the scheme per
// client, and naming one of them would be a guess, so it stays "Auto".
function autoModelForScheme(scheme) {
  if (scheme && scheme !== 'auto') return schemeAutoModels[scheme] || '';
  const bound = [...new Set(Object.values(schemeAutoModels).filter(Boolean))];
  return bound.length === 1 ? bound[0] : '';
}

// Selector entry for `auto` — `Model: gpt-5.4-mini · auto` once resolved.
function autoModelLabel(scheme) {
  const m = autoModelForScheme(scheme);
  return m ? `Model: ${catalogModelLabel(m)} · auto` : 'Model: Auto';
}

// Badge text for a session that ran on `auto` (which persists no context.model).
function autoModelBadge(scheme) {
  const m = autoModelForScheme(scheme);
  return m ? `${catalogModelLabel(m)} · auto` : 'Auto';
}

// Resolve the selected model for dispatch. `auto` → undefined: `dispatchExec`
// omits a falsy model, so the fast-runner resolves it server-side (admin-configured
// runner model, else its DEFAULT_MODEL). An explicit pick passes through verbatim.
function resolveAutoModel(selectedModel) {
  return selectedModel === 'auto' ? undefined : selectedModel;
}

// ── Render-scheme selector constants ──
// The transport the model authors in for a NEW session (session-bound thereafter).
// `auto` = defer to the server (admin default, which is native-ir for capable web);
// an explicit pick forces the scheme for a brand-new session so HTML and NativeIR can be
// compared side-by-side. The ids match services/render_scheme.py exactly.
const RENDER_SCHEMES = [
  { id: 'auto', label: 'Render: Auto' },
  { id: 'native-ir', label: 'Render: NativeIR' },
  { id: 'a2ui', label: 'Render: a2ui' },
  { id: 'freeform-html-inline', label: 'Render: HTML' },
];
const DEFAULT_RENDER_SCHEME = 'auto';
// `auto` → undefined: dispatchExec omits a falsy render_scheme, so the server resolves it.
function resolveAutoScheme(scheme) {
  return scheme && scheme !== 'auto' ? scheme : undefined;
}
// ── Worker-backend selector constants ──
// Which exec worker runtime a NEW session binds to (session-bound thereafter, exactly like
// render_scheme/model): `managed` = Anthropic Managed Agent runner, `harnessrouter` = the
// HR worker. `auto` = defer to the server (dispatchExec omits a falsy worker_backend, so the
// server resolves it exactly as today; released clients never send it). The ids match the
// api-server `worker_backend` request field.
const WORKER_BACKENDS = [
  { id: 'auto', label: 'Worker: Auto' },
  { id: 'managed', label: 'Worker: Managed' },
  { id: 'harnessrouter', label: 'Worker: HR' },
];
const DEFAULT_WORKER_BACKEND = 'auto';
// `auto` → undefined: dispatchExec omits a falsy worker_backend, so the server resolves it.
const resolveAutoWorker = (w) => (w && w !== 'auto' ? w : undefined);
// ── Reasoning-effort selector constants ──
// PER-TURN (unlike model/scheme, which are session-bound): every message may pick its own effort.
// Only a model whose catalog row declares `reasoning_efforts` reasons — for every other model the
// selector is disabled at "none", because the fast-runner sends no reasoning parameter for it
// (llm._build_agent) and reasoning + function tools would 400 on /v1/chat/completions anyway.
// The full set the runner accepts (llm._VALID_REASONING_EFFORTS) — a model's CATALOG default may
// be any of them, and an option missing here would render the pill blank for that model.
const REASONING_EFFORTS = [
  { id: 'low', label: 'Reason: Low' },
  { id: 'medium', label: 'Reason: Medium' },
  { id: 'high', label: 'Reason: High' },
  { id: 'xhigh', label: 'Reason: XHigh' },
  { id: 'max', label: 'Reason: Max' },
  { id: 'none', label: 'Reason: None' },
];
const DEFAULT_REASONING_EFFORT = 'auto';
// {modelId: effort} from `/api/config.model_options[].default_reasoning_effort` — the gateway
// `llm_models` catalog, which is the SINGLE SOURCE OF TRUTH the fast runner reads too. Filled by
// loadModelCatalog; `{}` on an older api-server (field absent) → every model falls back to 'none'.
let modelDefaultEfforts = {};
// The catalog default for `modelId`; 'none' when it has none (or the catalog hasn't landed).
function modelDefaultEffort(modelId) {
  return modelDefaultEfforts[modelId] || 'none';
}
// {modelId: [effort, …]} from `/api/config.model_options[].reasoning_efforts` (#4787) — the efforts
// a model accepts, i.e. the catalog CAPABILITY the fast runner routes on (llm._reasoning_efforts_for),
// so no model name is special-cased here. `{}` before the catalog lands → no model reasons.
let modelReasoningEfforts = {};
function modelAcceptedEfforts(modelId) {
  return modelReasoningEfforts[modelId] || [];
}
// Whether `modelId` is a reasoning model at all.
function reasoningSupported(modelId) {
  return modelAcceptedEfforts(modelId).length > 0;
}
// The model this bar will actually dispatch with: an explicit pick, else what `auto` resolves to
// for the scheme (same ladder the model selector labels with). '' when not knowable here.
function effectiveModelId(selectedModel, scheme) {
  return resolveAutoModel(selectedModel) || autoModelForScheme(scheme) || '';
}
// `auto`/unset → the PER-MODEL default, read from the model's catalog row
// (`/api/config.model_options[].default_reasoning_effort`) — the SAME value the fast runner
// resolves an override-less turn with (llm._reasoning_effort_for), so this can no longer drift
// from the server the way the old hardcoded `gpt-5.6 → low` guess had. Catalog not loaded yet,
// or a model with no default → 'none' (the chat path), which is what the runner does too.
// An explicit pick passes through. What the selector shows and what gets dispatched are computed
// identically, so the pill is never lying about the effort the turn ran at.
function resolveAutoEffort(modelId, effort) {
  const picked = effort && effort !== 'auto' ? effort : modelDefaultEffort(modelId);
  // Never an effort the model refuses (gpt-6-astra rejects "none"): fall back exactly like the
  // runner — the model's own default if it accepts that, else the lowest effort it accepts.
  const accepted = modelAcceptedEfforts(modelId);
  if (!accepted.length || accepted.includes(picked)) return picked;
  const fallback = modelDefaultEffort(modelId);
  return accepted.includes(fallback) ? fallback : accepted[0];
}

// Short badge label for a session's bound scheme (context.render_scheme). Legacy html rows
// (`freeform-html`) and the inline variant both read as "HTML".
function schemeBadgeLabel(scheme) {
  if (scheme === 'native-ir') return 'NativeIR';
  if (scheme === 'freeform-html-inline' || scheme === 'freeform-html') return 'HTML';
  return scheme || null;
}
// Short badge for a session's bound reasoning effort (context.reasoning_effort). Null for
// none/absent/auto — a non-reasoning session shows no badge. Mirrors schemeBadgeLabel.
function reasoningBadgeLabel(effort) {
  const e = String(effort || '').toLowerCase();
  if (!e || e === 'none' || e === 'auto') return null;
  return `Reason: ${e.charAt(0).toUpperCase()}${e.slice(1)}`;
}
// Short badge for a session's bound worker backend (context.worker_backend). Null for
// auto/absent — a default-worker session shows no badge. Mirrors schemeBadgeLabel.
function workerBadgeLabel(w) {
  if (w === 'managed') return 'Managed';
  if (w === 'harnessrouter') return 'HR';
  return null;
}

// Runner identity → display lane. The LANE is the RUNTIME that produced the card
// (stamped by the runner on the WAL delta as `producer`), NOT inferred from the model id —
// so a Gemini/GPT model run by the PydanticAI fast-runner still reads "PydanticAI · <model>".
const RUNNER_LANES = {
  pydantic_ai: { label: 'PydanticAI', emoji: '🔷' },
  exec_pydantic_ai: { label: 'PydanticAI', emoji: '🔷' },
  agno: { label: 'Agno', emoji: '🔶' },
  exec_agno: { label: 'Agno', emoji: '🔶' },
};

// ── ProducerBadge — pill showing which runtime (lane) + model produced a card ──
function ProducerBadge({ producerModel, producer }) {
  if (!producerModel && !producer) return null;
  const label = catalogModelLabel(producerModel);
  // Prefer the explicit runner identity; fall back to model-based inference for cards with
  // no `producer` (e.g. the managed runtime, which doesn't stamp one) so they keep their
  // existing Fast/System/SDK labels.
  const runnerLane = producer ? RUNNER_LANES[producer] : null;
  const isFast = !runnerLane && (producerModel?.startsWith('gemini') || producerModel?.startsWith('claude-haiku'));
  const isSystem = !runnerLane && producerModel === 'system';
  const emoji = runnerLane ? runnerLane.emoji : isFast ? '⚡' : isSystem ? '🛠' : '🔷';
  const lane = runnerLane ? runnerLane.label : isFast ? 'Fast' : isSystem ? 'System' : 'SDK';
  // Green = Fast, grey = System, indigo = everything else (runner lanes + SDK).
  const bg = isFast ? 'rgba(52,199,89,0.12)' : isSystem ? 'rgba(0,0,0,0.04)' : 'rgba(88,86,214,0.12)';
  const fg = isFast ? '#2f855a' : isSystem ? 'rgba(0,0,0,0.55)' : '#5856d6';
  return (
    <span style={{
      fontSize: 10, color: fg, fontWeight: 500,
      padding: '2px 6px', borderRadius: 4, background: bg,
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      pointerEvents: 'none', whiteSpace: 'nowrap', flexShrink: 0,
    }} title={`${lane} runtime${producerModel ? ` — ${producerModel}` : ''}`}>
      {emoji} {lane}{label ? ` · ${label}` : ''}
    </span>
  );
}

// ── Dashboard CSS variables (inline, to avoid class conflicts with main app) ──

const _isVideoUrl = (url) => /\.(mp4|mov|webm)($|\?)/i.test(url);

// Clipboard write that survives iOS Safari: `navigator.clipboard.writeText` must
// be issued in the SAME task as the user gesture — any `await` between the click
// and the write breaks the chain and the write silently fails. Falls back to a
// hidden textarea + execCommand where the async API is missing or blocked.
// Module scope so both the session header and the per-turn Copy action share it.
function copyWithFallback(text) {
  let ok = false;
  try {
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text);
      ok = true;
    }
  } catch (_) { /* fall through */ }
  if (!ok) {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      ok = true;
    } catch (_) { /* give up */ }
  }
  return ok;
}

// A timeline round stores its INPUT media in two lanes (`photos`, `videos`) plus
// a `media_order` kind sequence ('image'|'video') recording the user's own
// upload order. Flatten back to the single URL list the user turn was sent with
// so the bubble renders every medium — and Rewind can hand all of them back to
// the composer verbatim (the server re-canonicalizes any URL shape to media://).
function roundMediaUrls(round) {
  const pick = (m) => (typeof m === 'string' ? m : m?.url);
  const photos = (round?.photos || []).map(pick).filter(Boolean);
  const videos = (round?.videos || []).map(pick).filter(Boolean);
  if (!videos.length) return photos;
  const order = Array.isArray(round?.media_order) ? round.media_order : [];
  if (!order.length) return [...photos, ...videos];
  const qi = photos.slice();
  const qv = videos.slice();
  const out = [];
  for (const kind of order) {
    const next = kind === 'video' ? qv.shift() : qi.shift();
    if (next) out.push(next);
  }
  return [...out, ...qi, ...qv]; // anything `media_order` didn't account for
}

const V = {
  bg: '#f5f5f7',
  bgCard: '#ffffff',
  bgHover: '#eeeef0',
  border: '#d8d8dc',
  text: '#1d1d1f',
  textDim: '#6e6e73',
  accent: '#5856d6',
  accentDim: '#eeedf5',
  green: '#34c759',
  red: '#ff3b30',
  yellow: '#ff9500',
  blue: '#007aff',
  purple: '#af52de',
  font: "'SF Mono', 'Cascadia Code', 'JetBrains Mono', monospace",
  fontUi: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
};

// ── Card extraction: flatten cards from synthetic tasks for rendering ──
// NOTE: No Managed Agent runner dashboard REST calls — sessions come from api.getSessions().

function deduplicateCards(tasks) {
  const allCards = {};
  const cardTaskMap = {};
  const sorted = [...tasks].sort((a, b) => (a.requestTs || a.ts || 0) - (b.requestTs || b.ts || 0));
  for (const task of sorted) {
    const taskCards = task.cards || {};
    for (const [id, card] of Object.entries(taskCards)) {
      if (!allCards[id] || card.status === 'finalized') {
        allCards[id] = { cardId: id, ...card };
        cardTaskMap[id] = task;
      }
    }
  }
  return { allCards, cardTaskMap, sortedTasks: sorted };
}


// ── Infrastructure vs content-bearing card classification ──
// Infrastructure cards are system/internal (thinking, memory updates) — they don't
// represent user-visible results. Content cards are everything else (agent-speech,
// freeform-html, text-result, info-card, recipe, etc.). agent-speech IS content —
// it's the agent's primary reply. The sibling `debug-html` template renders the
// same as `freeform-html` but is bucketed into a collapsible Debug panel.
// Unknown templates default to content (safer to show than hide).
const INFRASTRUCTURE_TEMPLATES = new Set([
  'thinking', 'thinking-process',
  'memory-update',
  'session-summary',
  'package-activation',
]);

function hasContentCards(cards) {
  if (!cards) return false;
  return Object.values(cards).some(card => !INFRASTRUCTURE_TEMPLATES.has(card.template));
}

// ── Format helpers ──

function formatTime(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  const now = new Date();
  const diff = now - d;
  if (diff < 60000) return 'just now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function formatDateTime(ts) {
  if (!ts) return '';
  return new Date(ts).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function formatDuration(ms) {
  if (!ms || ms < 0) return '';
  if (ms < 1000) return `${Math.round(ms)}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function formatSkillTag(slug) {
  if (!slug) return '';
  return slug.replace(/^agent:/, '');
}

// ── TemplateCardPreview — renders a template via Handlebars into a srcdoc iframe ──

function TemplateCardPreview({ template, data, cardId, sessionId }) {
  const [html, setHtml] = useState(null);
  const [templateMissing, setTemplateMissing] = useState(false);
  const iframeRef = useRef(null);
  const dataJson = JSON.stringify(data);

  useEffect(() => {
    setHtml(null);
    setTemplateMissing(false);
    renderTemplate(template, data).then(result => {
      if (result) setHtml(result);
      else setTemplateMissing(true);
    });
  }, [template, dataJson]);

  // Bridge vi_action messages from this iframe → window CustomEvent so the
  // chat page can react. Used by ask-user-question Submit to "continue the
  // chat": the chosen option label / custom text becomes the next user turn.
  // sessionId travels with the event so the follow-up dispatch lands on the
  // card's OWN session — never stale React state.
  useEffect(() => {
    const onMsg = (e) => {
      if (e.source !== iframeRef.current?.contentWindow) return;
      const d = e.data;
      if (!d || d.type !== 'vi_action') return;
      if (d.action === 'user_reply') {
        let answers = null;
        try { answers = typeof d.reply === 'string' ? JSON.parse(d.reply) : d.reply; } catch { /* malformed */ }
        const detail = { template, cardId, sessionId, answers, raw: d };
        window.dispatchEvent(new CustomEvent('vi-card-reply', { detail }));
      } else {
        window.dispatchEvent(new CustomEvent('vi-card-action', { detail: { template, sessionId, ...d } }));
      }
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, [template, cardId, sessionId]);

  if (templateMissing) {
    // FU-2/FU-3 fallback: when the backend has no Handlebars template for this
    // card name (e.g. `html_block`, `user_questions_prediction` from the GET
    // snapshot — see findings.md), surface the raw card payload so the
    // developer-facing playground never silently hides content.
    return (
      <div>
        <div style={{
          padding: '6px 16px', fontSize: 11, color: '#fbbf24',
          background: '#2a1a0e', fontFamily: V.font,
        }}>
          [no template registered for "{template}" — showing raw payload]
        </div>
        <RawDataView card={{ template, data, cardId, sessionId }} />
      </div>
    );
  }
  if (!html) {
    return <div style={{ padding: 16, color: V.textDim, fontSize: 13 }}>Loading template...</div>;
  }

  return (
    <iframe
      key={template + dataJson}
      ref={iframeRef}
      srcDoc={html}
      // Issue #1411 follow-up: ``allow-same-origin`` so embedded
      // ``<img src="/api/media/...">`` fetches inherit the parent's
      // session cookie (null-origin iframes are cross-site and drop it).
      sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
      style={{ width: '100%', border: 'none', minHeight: 200 }}
      onLoad={() => {
        const iframe = iframeRef.current;
        if (iframe?.contentDocument?.body) {
          iframe.style.height = iframe.contentDocument.body.scrollHeight + 'px';
        }
      }}
    />
  );
}

// ── Shared: render a card via template renderer ──
//
// Template-name normalization (alias `html_block→freeform-html`, `text→agent-speech`)
// happens at the store ingest in `useSessionStore.processEvent`, so cards
// arriving here already have a canonical template name. See
// `services/templateRenderer.js#TEMPLATE_ALIASES` for the single source of truth.

export function CardRenderer({ card }) {
  // The session the card is rendered in. Prefer the sessionId stamped on the
  // card by the SSE store; fall back to the chat route's :sessionId. Some
  // card events (notably widget_log) can reach the store before the active
  // session is set, leaving card.sessionId null — the route param is the
  // reliable source for the host-action source_session_id below.
  const { sessionId: routeSessionId } = useParams();
  const navigate = useNavigate();
  const cardSessionId = card.sessionId || routeSessionId || null;
  const forwardHtmlAction = (actionData) => {
    window.dispatchEvent(new CustomEvent('vi-card-action', {
      detail: {
        template: card.template,
        cardId: card.cardId,
        sessionId: cardSessionId,
        ...actionData,
      },
    }));
  };

  // Thinking cards use ThinkingPlanCard (structured plan display with fold/unfold)
  if (card.template === 'thinking' || card.template === 'thinking-process') {
    return <ThinkingPlanCard data={card.data || {}} cardStatus={card.status} />;
  }

  if (isMailTemplate(card.template)) {
    return (
      <div style={{ padding: '12px 16px' }}>
        <MailCard
          card={card}
          onA2uiAction={(envelope) => window.dispatchEvent(new CustomEvent('vi-a2ui-action', {
            detail: { sessionId: cardSessionId, envelope },
          }))}
          onOpenTarget={(target) => {
            const href = mailLinkHref(target);
            if (href) navigate(href);
          }}
        />
      </div>
    );
  }

  // Widget log card — inline `Logged|Cancel` ⇄ `Log|Canceled` UX with
  // server-as-state-machine refresh (see frontend/src/components/session/LogCard.jsx).
  // The chat agent emits these via mcp_session_publish_card with
  // template="widget_log" + data={log_card_id, instance_id?, widget_id?,
  // widget_title, kind, payload}.
  if (card.template === 'widget_log' || card.template === 'widget-log') {
    const d = card.data || {};
    return (
      <LogCard
        logCardId={d.log_card_id}
        status={d.status || 'logged'}
        instanceId={d.instance_id || null}
        widgetId={d.widget_id || null}
        widgetRef={d.widget_ref || null}
        instantiateTitle={d.instantiate_title || null}
        widgetTitle={d.widget_title || 'Log'}
        kind={d.kind || 'item'}
        payload={d.payload || {}}
        sessionId={cardSessionId}
        widgetIcon={d.widget_icon || null}
        logOptions={d.log_options || null}
        display={Array.isArray(d.display) ? d.display : null}
        appendsTo={d.appends_to || null}
      />
    );
  }

  // Widget suggestion card — the dedicated adopt/create nudge (distinct from
  // widget_log). The chat agent emits it via mcp_session_suggest_widget when a
  // capture fits a not-yet-adopted widget/collection; "Create" instantiates +
  // logs the first item. See frontend/src/components/session/WidgetSuggestionCard.jsx.
  if (card.template === 'widget_suggestion' || card.template === 'widget-suggestion') {
    const d = card.data || {};
    return (
      <WidgetSuggestionCard
        cardId={card.cardId || null}
        logCardId={d.log_card_id || null}
        widgetId={d.widget_id || null}
        widgetKind={d.widget_kind || 'standard'}
        title={d.title || d.widget_title || ''}
        widgetTitle={d.widget_title || ''}
        reason={d.reason || ''}
        kind={d.kind || 'item'}
        payload={d.payload || {}}
        logOptions={d.log_options || null}
        sessionId={cardSessionId}
      />
    );
  }

  // Batch-log progress card (Use Case C) — emitted after mcp_batch_log_to_widget
  // logs N prior captures to a widget / fresh Collection instance.
  if (card.template === 'widget_batch_progress' || card.template === 'widget-batch-progress') {
    const d = card.data || {};
    return (
      <BatchProgressCard
        instanceId={d.instance_id || null}
        widgetTitle={d.widget_title || 'Batch log'}
        logged={d.logged || 0}
        skipped={d.skipped || 0}
        results={Array.isArray(d.results) ? d.results : []}
      />
    );
  }

  // Agent speech cards — markdown-rendered bubble
  if (card.template === 'agent-speech') {
    const msg = card.data?.message || '';
    return msg ? (
      <MarkdownRenderer style={{
        padding: '12px 16px', fontSize: 14, lineHeight: 1.6,
        color: V.text, fontFamily: V.fontUi,
      }}>{msg}</MarkdownRenderer>
    ) : null;
  }

  // Text cards (Postgres-snapshot name for plain text messages) — same data
  // shape as agent-speech (`data.message`) but kept as a distinct renderer
  // so the developer-facing card header reads "TEXT". See findings.md FU-2.
  if (card.template === 'text') {
    const msg = card.data?.message || '';
    if (!msg) return null;
    return (
      <div style={{
        padding: '12px 16px',
        borderLeft: `3px solid ${V.border}`,
        background: V.bgCard || '#fafafa',
      }}>
        <MarkdownRenderer style={{
          fontSize: 13, lineHeight: 1.55,
          color: V.text, fontFamily: V.fontUi,
        }}>{msg}</MarkdownRenderer>
      </div>
    );
  }

  // User-questions-prediction cards — minimal developer-facing renderer
  // exposing each tab's question + the predicted follow-up labels.
  // Two data shapes observed in the wild:
  //   New: { tabs: [{ id, question, options: [{id, label, hashtags?, style?}] }], ... }
  //   Flat: { question, predictions: [{id, label, hashtags?}] }
  // Both paths render as: question header + bordered rows of option labels.
  // Routing hashtags (`#exec_sdk` etc.) are intentionally hidden — they are
  // backend dispatch hints, not user-facing text. See findings.md FU-3.
  if (card.template === 'user_questions_prediction' || card.template === 'user-questions-prediction') {
    const data = card.data || {};
    const tabs = Array.isArray(data.tabs) && data.tabs.length > 0
      ? data.tabs
      : [{ id: 'tab-0', question: data.question, options: data.predictions || data.options }];
    const answered = (data.status || '').toLowerCase() === 'answered';

    const fireOption = (tab, opt) => {
      // Re-use the same `vi-card-reply` contract the iframe template
      // fires for tapped options — PlaygroundView's onCardReply handler
      // intercepts action-bearing options and re-dispatches plain prompts
      // to the agent. Inline-rendered cards skip the iframe + Handlebars
      // template, so this is the path that wires them up.
      const answersForEvt = {
        [tab?.id || 'next']: opt.action
          ? { kind: 'option', optionId: opt.id, label: opt.label, action: opt.action }
          : { kind: 'option', optionId: opt.id, label: opt.label, hashtags: opt.hashtags || [] },
      };
      try {
        window.dispatchEvent(new CustomEvent('vi-card-reply', {
          detail: {
            template: 'ask-user-question',
            cardId: card.cardId,
            sessionId: card.sessionId,
            answers: answersForEvt,
          },
        }));
      } catch (e) {
        console.warn('[user_questions_prediction] dispatch failed:', e);
      }
    };

    // user_questions_prediction is now next-step prompts ONLY — widget actions
    // moved to dedicated cards (widget_log / widget_suggestion / widget_batch_progress).
    // The legacy `#widget_<id>` / `#batch_<id>` typed-tag chip (green pill) has been
    // removed from the chat agent prompt, so there is nothing left to special-case
    // here: every option renders plainly and, when tapped, routes its label back to
    // the agent via the `vi-card-reply` contract below.
    const renderOption = (tab, opt, idx) => {
      const isAction = typeof opt?.action === 'string' && opt.action.length > 0;
      const dimmed = answered && !isAction;
      const borderColor = isAction ? V.accent : V.border;
      return (
        <button
          key={opt?.id || idx}
          type="button"
          disabled={dimmed}
          onClick={() => fireOption(tab, opt)}
          style={{
            padding: '10px 14px',
            border: `1px solid ${borderColor}`,
            borderRadius: 8,
            fontSize: 13,
            color: isAction ? V.accent : V.text,
            background: V.bg,
            cursor: dimmed ? 'default' : 'pointer',
            fontFamily: V.fontUi,
            textAlign: 'left',
            width: '100%',
            opacity: dimmed ? 0.55 : 1,
            display: 'flex', alignItems: 'center', gap: 8,
            transition: 'transform 80ms ease-out, background 120ms ease-out',
          }}
          onMouseEnter={(e) => { if (!dimmed) e.currentTarget.style.background = V.bgHover || '#1a1d24'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = V.bg; }}
        >
          {opt?.label || JSON.stringify(opt)}
        </button>
      );
    };
    // "Track with Widgets" — pull up the widget panel (pure client action; the
    // runner/server is unaware). The WidgetPanel in the session header listens
    // for this window event. A muted green so it stands out a little, not loud.
    const trackWithWidgets = () => {
      try {
        window.dispatchEvent(new CustomEvent('vi-open-widget-panel'));
      } catch (e) {
        console.warn('[user_questions_prediction] open widget panel failed:', e);
      }
    };
    return (
      <div style={{ padding: '12px 16px', fontFamily: V.fontUi, display: 'flex', flexDirection: 'column', gap: 14 }}>
        {tabs.map((tab, tIdx) => {
          const options = Array.isArray(tab?.options) ? tab.options : [];
          if (!tab?.question && options.length === 0) return null;
          return (
            <div key={tab?.id || tIdx} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {tab?.question && (
                <div style={{ fontSize: 13, fontWeight: 600, color: V.text }}>
                  {tab.question}
                </div>
              )}
              {options.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {options.map((opt, oIdx) => renderOption(tab, opt, oIdx))}
                </div>
              )}
            </div>
          );
        })}
        <button
          type="button"
          onClick={trackWithWidgets}
          style={{
            padding: '10px 14px',
            border: '1px solid rgba(46,155,87,0.35)',
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 700,
            color: '#2E9B57',
            background: 'rgba(46,155,87,0.10)',
            cursor: 'pointer',
            fontFamily: V.fontUi,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            width: '100%',
            transition: 'background 120ms ease-out',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(46,155,87,0.18)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(46,155,87,0.10)'; }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <rect x="3" y="3" width="7" height="7" rx="1.5" fill="#2E9B57" />
            <rect x="14" y="3" width="7" height="7" rx="1.5" fill="#2E9B57" opacity="0.55" />
            <rect x="3" y="14" width="7" height="7" rx="1.5" fill="#2E9B57" opacity="0.55" />
            <rect x="14" y="14" width="7" height="7" rx="1.5" fill="#2E9B57" />
          </svg>
          Track with Widgets
        </button>
      </div>
    );
  }

  // a2ui session surface — the turn's rich answer as an a2ui 1.0 envelope log,
  // rendered by the shared A2uiSurfaceCard (same component the app-facing
  // SessionView uses). A Button/chip tap posts the v1 `follow_up` action through
  // a dedicated event the composer picks up (dispatchExec with `a2ui_action`).
  if (card.template === 'a2ui_surface' || Array.isArray(card.data?.envelopes)) {
    return (
      <div style={{ padding: '12px 16px' }}>
        <A2uiSurfaceCard
          card={card}
          onCardAction={(a) => window.dispatchEvent(new CustomEvent('vi-card-action', {
            detail: { template: 'a2ui_surface', sessionId: cardSessionId, ...a },
          }))}
          onA2uiAction={(envelope) => window.dispatchEvent(new CustomEvent('vi-a2ui-action', {
            detail: { sessionId: cardSessionId, envelope },
          }))}
        />
      </div>
    );
  }

  // native-ir session block — one ViewIR node under data.node, rendered by the
  // shared SessionBlockCard (same component the app-facing SessionView uses).
  // Without this branch a session_block card falls through to the
  // "no template registered — raw payload" fallback below.
  if (card.template === 'session_block' || card.data?.node) {
    // The block itself is FRAMELESS and gutterless (it is one piece of one
    // answer, not an object) — so the SURFACE supplies the reading gutter, the
    // same `12px 16px` the markdown branch below uses. The app surface
    // (`SessionView`) supplies its own `px-5`; the playground's card chrome
    // supplies none, which would otherwise put the words on the frame's edge.
    return (
      <div style={{ padding: '12px 16px' }}>
        <SessionBlockCard
          card={card}
          onCardAction={(a) => window.dispatchEvent(new CustomEvent('vi-card-action', {
            detail: { template: 'session_block', sessionId: cardSessionId, ...a },
          }))}
          onFollowUp={(words) => window.dispatchEvent(new CustomEvent('vi-card-reply', {
            detail: { template: 'session_block', sessionId: cardSessionId, answers: words, raw: { action: 'user_reply', reply: words } },
          }))}
        />
      </div>
    );
  }

  // For freeform-html / debug-html (same renderer, different bucket) or any card with raw HTML
  // data, render via PersistentHtmlRenderer.
  const htmlContent = card.data?.html || card.data?.htmlContent;
  if (
    (card.template === 'freeform-html' ||
      card.template === 'freeform_html' ||
      card.template === 'debug-html') &&
    htmlContent
  ) {
    const trimmed = htmlContent.trim();
    const looksLikeMarkdown = !trimmed.startsWith('<') && (
      /^#{1,6}\s/m.test(trimmed) ||
      /^\*{1,2}[^*]+\*{1,2}/m.test(trimmed) ||
      /^[-*+]\s/m.test(trimmed) ||
      /^\d+\.\s/m.test(trimmed) ||
      /```[\s\S]*```/m.test(trimmed)
    );
    if (looksLikeMarkdown) {
      return (
        <div style={{ padding: '12px 16px' }}>
          <MarkdownRenderer content={trimmed} />
        </div>
      );
    }
    return (
      <PersistentHtmlRenderer
        streamingChunks={[htmlContent]}
        isStreaming={card.status === 'streaming'}
        onAction={forwardHtmlAction}
      />
    );
  }

  // During streaming, render md-* templates inline for instant progressive updates.
  // After finalize, fall through to Handlebars + iframe for full styling.
  const isDone = card.status === 'finalized' || card.status === 'done';
  if (!isDone && STREAMABLE_TEMPLATES.has(card.template)) {
    return <MdStreamingRenderer template={card.template} data={card.data || {}} darkMode={false} />;
  }

  // All other templates → render via Handlebars + iframe.
  // Pass sessionId so user_reply events (e.g. ask-user-question Submit)
  // dispatch the follow-up against the card's OWN session, not whatever
  // React state currently holds — prevents the 3-new-sessions bug where
  // a stale selectedSessionId caused fresh-session creation per click.
  return (
    <TemplateCardPreview
      template={card.template}
      data={card.data || {}}
      cardId={card.cardId}
      sessionId={card.sessionId}
    />
  );
}

// ── Raw data debug view — shows card JSON in a scrollable <pre> block ──

function RawDataView({ card }) {
  return (
    <pre style={{
      margin: 0, padding: '12px 16px', fontSize: 11, lineHeight: 1.5,
      fontFamily: V.font, background: '#1a1a2e', color: '#a8e6cf',
      overflow: 'auto', maxHeight: '60vh', whiteSpace: 'pre-wrap',
      wordBreak: 'break-all',
    }}>
      {JSON.stringify(card, null, 2)}
    </pre>
  );
}

// ── DebugPanel — collapsible bucket for `debug-html` cards (agent-speak tool I/O) ──
// Issue #1007. Uses native <details>/<summary> for zero-JS collapse. Same CardRenderer
// as main cards so rendering stays byte-for-byte identical to inline `freeform-html`.
function DebugPanel({ cards }) {
  if (!cards || cards.length === 0) return null;
  return (
    <details
      style={{
        marginTop: 16, marginBottom: 16, maxWidth: '80%', marginRight: 'auto',
        border: `1px solid ${V.border}`, borderRadius: 8, background: V.bgCard,
      }}
    >
      <summary
        style={{
          padding: '8px 12px', fontSize: 11, fontWeight: 600,
          textTransform: 'uppercase', letterSpacing: '0.04em',
          color: V.textDim, cursor: 'pointer', userSelect: 'none',
          listStyle: 'none',
        }}
      >
        🛠 Debug ({cards.length} {cards.length === 1 ? 'card' : 'cards'})
      </summary>
      <div style={{ borderTop: `1px solid ${V.border}`, padding: '8px 12px' }}>
        {cards.map(card => (
          <div
            key={card.cardId}
            style={{
              marginBottom: 8, background: '#fff', color: '#111',
              border: `1px solid ${V.border}`, borderRadius: 6, overflow: 'hidden',
            }}
          >
            <CardRenderer card={card} />
          </div>
        ))}
      </div>
    </details>
  );
}

function RawToggleButton({ active, onClick, style }) {
  return (
    <button
      onClick={onClick}
      title="Toggle raw data"
      style={{
        background: active ? 'rgba(88,86,214,0.15)' : 'transparent',
        color: active ? V.accent : V.textDim,
        border: `1px solid ${active ? V.accent : 'transparent'}`,
        borderRadius: 4, padding: '2px 6px', fontSize: 10, cursor: 'pointer',
        fontFamily: V.font, fontWeight: 600, lineHeight: 1,
        transition: 'all 0.15s',
        ...style,
      }}
    >
      &lt;/&gt;
    </button>
  );
}

// FetchModeSelector — developer-facing data-source picker.
// Auto:    active session → SSE; terminal session → GET snapshot (default behavior)
// GET:     force single-shot GET /sessions/:id, skip SSE — snapshot only
// SSE:     skip GET, open SSE stream and replay WAL from scratch
// GET+SSE: fetch snapshot AND open SSE simultaneously (good for diffing outputs)
const FETCH_MODES = [
  { value: 'auto',    label: 'Auto' },
  { value: 'get',     label: 'GET' },
  { value: 'sse',     label: 'SSE' },
  { value: 'get+sse', label: 'GET+SSE' },
];

function FetchModeSelector({ mode, onChange, style }) {
  return (
    <select
      value={mode}
      onChange={e => onChange(e.target.value)}
      onClick={e => e.stopPropagation()}
      title="Data source (developer debug): Auto / GET / SSE / GET+SSE"
      style={{
        background: mode === 'auto' ? 'transparent' : 'rgba(88,86,214,0.15)',
        color: mode === 'auto' ? V.textDim : V.accent,
        border: `1px solid ${mode === 'auto' ? V.border : V.accent}`,
        borderRadius: 4, padding: '2px 6px', fontSize: 10, cursor: 'pointer',
        fontFamily: V.font, fontWeight: 600, lineHeight: 1,
        ...style,
      }}
    >
      {FETCH_MODES.map(m => (
        <option key={m.value} value={m.value}>{m.label}</option>
      ))}
    </select>
  );
}

function shouldFetchGet(mode, status, TERMINAL_STATUSES) {
  if (mode === 'get' || mode === 'get+sse') return true;
  if (mode === 'sse') return false;
  // auto: only terminal sessions fetch GET snapshot
  return !!(status && TERMINAL_STATUSES.has(status));
}

function shouldConnectSSE(mode, status, TERMINAL_STATUSES) {
  if (mode === 'sse' || mode === 'get+sse') return true;
  if (mode === 'get') return false;
  // auto: only active (non-terminal) sessions connect SSE
  return !(status && TERMINAL_STATUSES.has(status));
}

// ══════════════════════════════════════════════════════════════════════
// NAV SIDEBAR — mirrors dashboard .sidebar
// ══════════════════════════════════════════════════════════════════════

// Environment badge colors
const ENV_COLORS = { dev: '#22c55e', staging: '#f59e0b', production: '#ef4444' };

function NavSidebar({ collapsed = false }) {
  const pages = [
    { id: 'chat', icon: '\u2709', label: 'Chat' },
    { id: 'widget/studio', icon: '\uD83D\uDEE0', label: 'Widget Studio' },
    { id: 'widget-native/studio', icon: '\u25E9', label: 'Native Widgets' },
    { id: 'intention', icon: '\uD83C\uDFAF', label: 'Intention' },
    { id: 'cards', icon: '\u25A6', label: 'Cards' },
    { id: 'tools', icon: '\uD83D\uDD27', label: 'Tools' },
    { id: 'memory', icon: '\uD83E\uDDE0', label: 'Memory' },
    { id: 'apis', icon: '\uD83D\uDD11', label: 'API Keys' },
    { id: 'usage', icon: '\uD83D\uDCCA', label: 'Usage' },
    { id: 'benchmark', icon: '\uD83E\uDDEA', label: 'Benchmark' },
    { id: 'batch', icon: '\u25B6', label: 'Batch Run' },
    { id: 'errors', icon: '\u26A0', label: 'Errors' },
    { id: 'connections', icon: '\uD83D\uDD17', label: 'Connections' },
    { id: 'routines', icon: '\u23F0', label: 'Routines' },
  ];

  const viUserId = api.getViUserId();
  const deviceId = api.getDeviceId();
  const baseUrl = api.getBaseUrl();
  // Fetch environment from backend
  const [envInfo, setEnvInfo] = useState(null);
  useEffect(() => {
    api.authFetch('/api/runner/dashboard/environment')
      .then(r => r.json())
      .then(d => setEnvInfo(d))
      .catch(() => {});
  }, []);
  // Fetch user balance
  const [balance, setBalance] = useState(null);
  useEffect(() => {
    if (!viUserId) return;
    api.request('/api/users/me/profile', {}, { retries: 1, timeout: 5000 })
      .then(d => { if (d?.balance != null) setBalance(d.balance); })
      .catch(() => {});
  }, [viUserId]);
  // Service versions — fixed order (frontend / api / agent) with a stable
  // placeholder so the sidebar layout doesn't jump as each fetch lands.
  // `agent` is the managed-agent-runner version (replaces the old `nano`
  // managed-agent-runner label after the managed-agent runtime switch).
  const VERSION_PLACEHOLDER = '----.--.--.-';
  const [versions, setVersions] = useState({
    frontend: import.meta.env.VITE_APP_VERSION || VERSION_PLACEHOLDER,
    api: VERSION_PLACEHOLDER,
    agent: VERSION_PLACEHOLDER,
    // Anthropic managed-agent version (independent of runner deploy — bumps on
    // agents.update / create_managed_defaults.py). Surfaced so a deploy can be
    // verified against the bound agent definition + skills attached.
    managedAgent: null,
  });
  useEffect(() => {
    if (!viUserId) return;
    api.request('/api/config', {}, { retries: 0, timeout: 3000 })
      .then(c => {
        if (c?.service_version) setVersions(v => ({ ...v, api: c.service_version }));
      })
      .catch(() => {});
    api.authFetch('/api/runner/dashboard/version')
      .then(r => r.json())
      .then(d => {
        setVersions(v => ({
          ...v,
          agent: d?.version ?? v.agent,
          managedAgent: d?.managed_agent?.version ?? null,
        }));
      })
      .catch(() => {});
  }, [viUserId]);

  if (collapsed) {
    // #1458 — collapsed rail: just a vertical "PLAYGROUND" label on a 24px strip.
    return (
      <aside className="pg-nav-sidebar pg-nav-sidebar-collapsed" style={{
        width: 24, background: V.bgCard,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0, overflow: 'hidden',
        transition: 'width 0.15s ease',
      }}>
        <div style={{
          writingMode: 'vertical-rl', transform: 'rotate(180deg)',
          fontSize: 10, fontWeight: 700, letterSpacing: '0.15em',
          textTransform: 'uppercase', color: V.textDim, userSelect: 'none',
        }}>Playground</div>
      </aside>
    );
  }
  return (
    <aside className="pg-nav-sidebar" style={{
      width: 200, background: V.bgCard,
      display: 'flex', flexDirection: 'column', flexShrink: 0,
      overflow: 'hidden',
      transition: 'width 0.15s ease',
    }}>
      <div style={{ padding: '16px 14px', borderBottom: `1px solid ${V.border}` }}>
        <div style={{ fontSize: 15, fontWeight: 600, letterSpacing: '-0.02em', color: V.text }}>
          Playground
        </div>
        {envInfo?.environment && (
          <div style={{
            marginTop: 8, padding: '6px 0', textAlign: 'center', borderRadius: 6,
            fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em',
            background: ENV_COLORS[envInfo.environment] || ENV_COLORS.dev,
            color: '#fff',
          }}>
            {envInfo.environment || 'unknown'}
          </div>
        )}
      </div>
      <nav style={{ padding: 6, flex: 1, overflowY: 'auto' }}>
        {pages.map(p => (
          <NavLink
            key={p.id}
            to={`/playground/${p.id}`}
            style={({ isActive }) => ({
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '8px 10px', borderRadius: 8, cursor: 'pointer',
              fontSize: 12, textDecoration: 'none',
              background: isActive ? V.accent : 'transparent',
              color: isActive ? '#fff' : V.textDim,
              transition: 'all 0.15s',
            })}
            onMouseEnter={e => {
              if (!e.currentTarget.classList.contains('active')) e.currentTarget.style.background = V.bgHover;
            }}
            onMouseLeave={e => {
              if (!e.currentTarget.classList.contains('active')) e.currentTarget.style.background = 'transparent';
            }}
          >
            <span style={{ fontSize: 14, width: 18, textAlign: 'center' }}>{p.icon}</span>
            {p.label}
          </NavLink>
        ))}
      </nav>
      {/* Info panel — click anywhere to copy all */}
      <div style={{
        padding: '10px 14px', borderTop: `1px solid ${V.border}`,
        fontSize: 10, color: V.textDim, lineHeight: 1.6, cursor: 'pointer',
      }}
        title="Click to copy all"
        onClick={() => {
          const lines = [];
          if (viUserId) lines.push(`vi-user: ${viUserId}`);
          if (deviceId) lines.push(`device: ${deviceId}`);
          if (baseUrl) lines.push(`api: ${baseUrl.replace(/^https?:\/\//, '')}`);
          navigator.clipboard.writeText(lines.join('\n'));
        }}
      >
        <InfoRow label="vi-user" value={viUserId} />
        <InfoRow label="device" value={deviceId} />
        {baseUrl && <InfoRow label="api" value={baseUrl.replace(/^https?:\/\//, '')} />}
        {viUserId && (
          <div style={{ marginTop: 4 }}>
            <button
              onClick={async (e) => {
                e.stopPropagation();
                try {
                  const { auth } = await import('../services/firebase.js');
                  const user = auth.currentUser;
                  if (!user) { alert('Not signed in'); return; }
                  const token = await user.getIdToken();
                  await navigator.clipboard.writeText(token);
                  e.target.textContent = 'Copied!';
                  setTimeout(() => { e.target.textContent = 'Copy Token'; }, 1500);
                } catch (err) { alert('Failed: ' + err.message); }
              }}
              style={{
                background: 'transparent', border: `1px solid ${V.border}`,
                borderRadius: 4, padding: '2px 8px', fontSize: 9, color: V.textDim,
                cursor: 'pointer', width: '100%',
              }}
            >Copy Token</button>
          </div>
        )}
        {/* Service versions — fixed order, stable placeholder until each fetch lands. */}
        <div style={{ borderTop: `1px solid ${V.border}`, margin: '6px 0' }} />
        <InfoRow label="frontend" value={versions.frontend} />
        <InfoRow label="api" value={versions.api} />
        <InfoRow label="agent runner" value={versions.agent} />
        {versions.managedAgent != null && (
          <InfoRow label="managed agent" value={`v${versions.managedAgent}`} />
        )}
      </div>
      <div style={{
        padding: '8px 14px', borderTop: `1px solid ${V.border}`,
        display: 'flex', gap: 6,
      }}>
        <button
          onClick={async () => {
            try {
              const { auth, signOut } = await import('../services/firebase.js');
              await signOut(auth);
              api.setViUserId(null);
              window.location.href = '/';
            } catch (e) {
              alert('退出失败: ' + (e.message || e));
            }
          }}
          style={{
            flex: 1, padding: '6px 0', border: `1px solid ${V.border}`, borderRadius: 6,
            background: 'transparent', color: V.text, fontSize: 11,
            cursor: 'pointer', textAlign: 'center',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = V.bgHover; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
        >
          退出登录
        </button>
        <button
          onClick={async () => {
            if (!window.confirm(
              '⚠️ 确认注销账号？\n\n注销后您的所有资源（包括账户余额）将被回收且不可恢复。此操作不可撤销。'
            )) return;
            if (!window.confirm('再次确认：您确定要永久注销此账号吗？')) return;
            try {
              await api.deleteAccount();
              const { auth, signOut } = await import('../services/firebase.js');
              await signOut(auth);
              window.location.href = '/';
            } catch (e) {
              alert('注销失败: ' + (e.message || e));
            }
          }}
          style={{
            flex: 1, padding: '6px 0', border: '1px solid rgba(255,77,79,0.4)', borderRadius: 6,
            background: 'transparent', color: '#ff4d4f', fontSize: 11,
            cursor: 'pointer', textAlign: 'center',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,77,79,0.1)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
        >
          注销账号
        </button>
      </div>
      <div style={{
        padding: '12px 14px', borderTop: `1px solid ${V.border}`,
        fontSize: 11, color: V.textDim,
      }}>
        <span style={{
          display: 'inline-block', width: 6, height: 6, borderRadius: '50%',
          background: V.green, marginRight: 6, verticalAlign: 'middle',
        }} />
        Connected
      </div>
    </aside>
  );
}

function InfoRow({ label, value }) {
  if (!value) return null;
  return (
    <div style={{ display: 'flex', gap: 4, alignItems: 'baseline' }}>
      <span style={{ color: V.textDim, flexShrink: 0 }}>{label}</span>
      <span style={{
        color: V.text, fontFamily: V.font, fontSize: 9,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        opacity: 0.8,
      }}
        title={value}
      >
        {value}
      </span>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// CHAT PAGE — mirrors dashboard chat-layout
// ══════════════════════════════════════════════════════════════════════

// ── InputConfigDropdowns — the Model / Render / Reasoning / Worker selects laid out
// INLINE (a horizontal row), placed in the "Use cached GCS URL" row above the composer
// (not in the text-input row). Each select commits ON CHANGE (writing the same
// localStorage keys the old pill selects used) on a NEW session; on a started session
// (`on*Change` absent, `locked*` set) each renders as a 🔒 read-only badge showing the
// bound value. Reasoning is offered only for catalog reasoning models (disabled + 'none' otherwise).
function InputConfigDropdowns({
  models, selectedModel, onModelChange, lockedModel,
  selectedScheme, onSchemeChange, lockedScheme,
  selectedEffort, onEffortChange, lockedEffort,
  selectedWorker, onWorkerChange, lockedWorker,
}) {
  // Editable = a NEW session (at least one on*Change handler present); otherwise the
  // session is started and every control renders as a locked badge.
  const editable = !!(onModelChange || onSchemeChange || onEffortChange || onWorkerChange);

  // Compact inline-select CSS (the old pill-dropdown look, sized for a single row).
  const sel = {
    background: 'rgba(0,0,0,0.04)', border: 'none', borderRadius: 14,
    color: 'rgba(0,0,0,0.55)', fontSize: 12, padding: '4px 10px', cursor: 'pointer',
    outline: 'none', fontFamily: V.fontUi, height: 28,
    WebkitAppearance: 'none', appearance: 'none',
    backgroundImage: `url("data:image/svg+xml,%3Csvg width='10' height='6' viewBox='0 0 10 6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%23999' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat', backgroundPosition: 'right 6px center',
    paddingRight: 22,
  };
  const disabledSel = { ...sel, cursor: 'not-allowed', opacity: 0.6 };
  const lockSpan = { fontSize: 11, color: V.textDim, opacity: 0.7, whiteSpace: 'nowrap', userSelect: 'none' };
  const set = (key, cb) => (e) => { cb(e.target.value); try { localStorage.setItem(key, e.target.value); } catch (_) { /* storage unavailable */ } };

  // Reasoning gating follows the catalog capability: only the efforts the effective model accepts
  // are offered, and the value shown is the resolved effort it will run at (so the pill never lies).
  const effModel = effectiveModelId(selectedModel, selectedScheme);
  const canReason = reasoningSupported(effModel);
  const effortValue = canReason ? resolveAutoEffort(effModel, selectedEffort) : 'none';

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
      {/* Model */}
      {editable ? (
        <select value={selectedModel} onChange={set('playground-model', onModelChange)} style={sel} title="Model">
          {models.filter(m => !HIDDEN_SELECTOR_MODELS.has(m.id)).map(m => (
            <option key={m.id} value={m.id}>{m.id === 'auto' ? autoModelLabel(lockedScheme || selectedScheme) : m.label}</option>
          ))}
        </select>
      ) : lockedModel != null ? (
        <span style={lockSpan}>🔒 {catalogModelLabel(lockedModel)}</span>
      ) : null}
      {/* Render */}
      {editable ? (
        <select value={selectedScheme} onChange={set('playground-render-scheme', onSchemeChange)} style={sel} title="Render scheme — HTML (WebView) vs NativeIR (typed blocks)">
          {RENDER_SCHEMES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
        </select>
      ) : schemeBadgeLabel(lockedScheme) ? (
        <span style={lockSpan}>🔒 {schemeBadgeLabel(lockedScheme)}</span>
      ) : null}
      {/* Reasoning — catalog reasoning models only, disabled + forced 'none' otherwise. */}
      {editable ? (
        <select
          value={effortValue}
          disabled={!canReason}
          onChange={set('playground-reasoning-effort', onEffortChange)}
          title={canReason ? 'Reasoning effort — locked once the session starts' : `Reasoning effort is unavailable${effModel ? ` — ${effModel} does not reason` : ''}`}
          style={canReason ? sel : disabledSel}
        >
          {REASONING_EFFORTS.filter(r => (canReason ? modelAcceptedEfforts(effModel).includes(r.id) : r.id === 'none')).map(r => (
            <option key={r.id} value={r.id}>{r.label}</option>
          ))}
        </select>
      ) : reasoningBadgeLabel(lockedEffort) ? (
        <span style={lockSpan}>🔒 {reasoningBadgeLabel(lockedEffort)}</span>
      ) : null}
      {/* Worker */}
      {editable ? (
        <select value={selectedWorker} onChange={set('playground-worker-backend', onWorkerChange)} style={sel} title="Worker exec backend (managed vs HR)">
          {WORKER_BACKENDS.map(w => <option key={w.id} value={w.id}>{w.label}</option>)}
        </select>
      ) : workerBadgeLabel(lockedWorker) ? (
        <span style={lockSpan}>🔒 Worker: {workerBadgeLabel(lockedWorker)}</span>
      ) : null}
    </div>
  );
}

// ── ChatInputBar — iOS-style floating pill (matching LiveSessionView) ──

export function ChatInputBar({ onSend, disabled, isExecuting, onStop, selectedModel, onModelChange, lockedModel, selectedScheme, onSchemeChange, lockedScheme, selectedEffort, onEffortChange, lockedEffort, selectedWorker, onWorkerChange, lockedWorker, prefill }) {
  const [text, setText] = useState('');
  const [images, setImages] = useState([]);
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  // Live model list from the gateway catalog (falls back to DEFAULT_MODELS).
  // The composer always renders, so mounting it is what warms the shared catalog
  // the other label lookups read.
  const models = useModelCatalog();
  // Issue #1411 follow-up: the content-hash upload cache in api.js stores
  // ``{hash → public_url}``. A URL shape regression (e.g. pre-#1411 proxy
  // URL cached while the site now expects a signed URL) will silently serve
  // the bad URL forever unless we bypass the cache. Exposing the toggle in
  // the composer lets the user flip cache on/off without opening devtools.
  // Storage key is shared with the intention page's toggle so the setting
  // carries across both surfaces.
  const [useUploadCache, setUseUploadCache] = useState(() => {
    try { return localStorage.getItem('playground-intention-use-upload-cache') !== '0'; } catch { return true; }
  });
  const toggleUseUploadCache = useCallback((next) => {
    setUseUploadCache(next);
    try { localStorage.setItem('playground-intention-use-upload-cache', next ? '1' : '0'); } catch { /* storage disabled */ }
  }, []);
  const fileRef = useRef(null);
  const inputRef = useRef(null);
  const errorTimerRef = useRef(null);

  const showUploadError = useCallback((msg) => {
    setUploadError(msg);
    if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
    errorTimerRef.current = setTimeout(() => setUploadError(null), 6000);
  }, []);

  // Rewind prefill seam (#session-rewind). `text`/`images` are LOCAL state, so a
  // rewind hands the composer a fresh `{ text, mediaUrls }` object and this
  // effect adopts it. Keyed on object identity — the parent mints a new object
  // per rewind tap, so rewinding twice to the same turn re-fills. Media come
  // back as already-hosted URLs: no re-upload, and `handleSend` already ships
  // `images.map(i => i.url)` as `media_urls` (server re-canonicalizes to media://).
  useEffect(() => {
    if (!prefill) return;
    setText(prefill.text || '');
    setImages((prefill.mediaUrls || []).map(url => ({
      url, name: '', isVideo: _isVideoUrl(url),
    })));
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
      inputRef.current.focus();
    }
  }, [prefill]);

  const handleSend = useCallback(() => {
    if (!text.trim() && images.length === 0) return;
    onSend(text.trim(), images.map(i => i.url));
    setText('');
    setImages([]);
    if (inputRef.current) inputRef.current.style.height = 'auto';
  }, [text, images, onSend]);

  const handleFileSelect = useCallback(async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    setShowAddMenu(false);
    const skipCache = !useUploadCache;
    for (const file of files) {
      try {
        const isVideo = file.type.startsWith('video/');
        let url;
        if (isVideo) {
          const ext = file.name.match(/\.(mp4|mov|webm)$/i)?.[1]?.toLowerCase() || 'mp4';
          url = await api.uploadBlob(file, ext, { skipCache });
        } else {
          const dataUrl = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (ev) => resolve(ev.target.result);
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });
          url = await api.uploadDataUrl(dataUrl, { skipCache });
        }
        // Issue #1411 verification: confirm uploads return the expected URL
        // shape. With cookie-auth #1396 blocked, the frontend asks presign
        // for ``?format=signed`` so the web ``<img src>`` can fetch without
        // credentials — expect a ``storage.googleapis.com`` URL here.
        console.log('[#1411 upload][chat]', { name: file.name, isVideo, skipCache, url });
        if (url) setImages(prev => [...prev, { url, name: file.name, isVideo }]);
      } catch (err) {
        console.error('Upload failed:', err);
        const kind = file.type.startsWith('video/') ? '视频' : '图片';
        showUploadError(`${kind} "${file.name}" 上传失败：${err.message || err}`);
      }
    }
  }, [showUploadError, useUploadCache]);

  const hasContent = text.trim() || images.length > 0;

  return (
    <div style={{ padding: '8px 16px 12px', background: V.bgCard }}>
      {/* Upload error banner — auto-dismisses after 6s */}
      {uploadError && (
        <div style={{
          margin: '0 4px 8px',
          padding: '10px 12px',
          borderRadius: 14,
          background: 'rgba(255, 59, 48, 0.08)',
          border: '1px solid rgba(255, 59, 48, 0.25)',
          color: V.red,
          fontSize: 12,
          lineHeight: 1.45,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 10,
          fontFamily: V.fontUi,
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
        }}>
          <span style={{ fontSize: 14, lineHeight: 1, marginTop: 1 }}>⚠</span>
          <span style={{ flex: 1, wordBreak: 'break-word' }}>{uploadError}</span>
          <button
            onClick={() => {
              setUploadError(null);
              if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
            }}
            style={{
              background: 'transparent', border: 'none', color: V.red,
              cursor: 'pointer', fontSize: 14, padding: 0,
              lineHeight: 1, opacity: 0.6, marginTop: 1,
            }}
            aria-label="dismiss"
          >×</button>
        </div>
      )}
      {/* Issue #1411 follow-up: upload cache toggle — shared storage key with
          the intention page's toggle so the setting persists across both
          surfaces. Disable to bypass the content-hash cache when a URL-shape
          change invalidates stored URLs (e.g. proxy ↔ signed flip). */}
      {/* GCS-cache toggle + the Model/Render/Reasoning/Worker config dropdowns, laid out
          inline in this row (the config lives here, NOT in the text-input row below). */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
        padding: '2px 8px', marginBottom: 6,
      }}>
        <label style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          fontSize: 11, color: V.textDim, cursor: 'pointer', userSelect: 'none',
        }}>
          <input
            type="checkbox"
            checked={useUploadCache}
            onChange={e => toggleUseUploadCache(e.target.checked)}
            style={{ cursor: 'pointer', margin: 0 }}
          />
          <span>Use cached GCS URL</span>
        </label>
        <InputConfigDropdowns
          models={models}
          selectedModel={selectedModel} onModelChange={onModelChange} lockedModel={lockedModel}
          selectedScheme={selectedScheme} onSchemeChange={onSchemeChange} lockedScheme={lockedScheme}
          selectedEffort={selectedEffort} onEffortChange={onEffortChange} lockedEffort={lockedEffort}
          selectedWorker={selectedWorker} onWorkerChange={onWorkerChange} lockedWorker={lockedWorker}
        />
      </div>
      {/* Image preview strip */}
      {images.length > 0 && (
        <div style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap', padding: '0 4px' }}>
          {images.map((img, i) => (
            <div key={i} style={{ position: 'relative' }}>
              {img.isVideo || /\.(mp4|mov|webm)$/i.test(img.url) ? (
                <div style={{ width: 56, height: 56, borderRadius: 12, overflow: 'hidden', position: 'relative', background: '#000' }}>
                  <video src={img.url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} preload="metadata" muted />
                  <div style={{
                    position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: 'rgba(0,0,0,0.2)', color: '#fff', fontSize: 16,
                  }}>&#9654;</div>
                </div>
              ) : (
                <img src={img.url} alt="" style={{ width: 56, height: 56, borderRadius: 12, objectFit: 'cover' }} />
              )}
              <button
                onClick={() => setImages(prev => prev.filter((_, j) => j !== i))}
                style={{
                  position: 'absolute', top: -6, right: -6, width: 20, height: 20,
                  borderRadius: '50%', background: 'rgba(0,0,0,0.6)', color: '#fff', border: 'none',
                  fontSize: 11, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  backdropFilter: 'blur(4px)',
                }}
              >×</button>
            </div>
          ))}
        </div>
      )}
      <div style={{
        display: 'flex', alignItems: 'flex-end', gap: 6,
        padding: '6px 8px', borderRadius: 28,
        background: 'rgba(255,255,255,0.85)',
        backdropFilter: 'blur(40px)',
        WebkitBackdropFilter: 'blur(40px)',
        boxShadow: '0 0 0 1px rgba(0,0,0,0.06), 0 4px 24px rgba(0,0,0,0.06)',
      }}>
        {/* + button */}
        <button
          onClick={() => { setShowAddMenu(prev => !prev); fileRef.current?.click(); }}
          style={{
            width: 36, height: 36, borderRadius: '50%', border: 'none',
            background: showAddMenu ? 'rgba(0,0,0,0.08)' : 'rgba(0,0,0,0.04)',
            color: showAddMenu ? 'rgba(0,0,0,0.5)' : 'rgba(0,0,0,0.25)',
            cursor: 'pointer', fontSize: 18, display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'all 0.3s',
            transform: showAddMenu ? 'rotate(45deg)' : 'none',
          }}
        >+</button>
        {/* Text input */}
        <textarea
          ref={inputRef}
          value={text}
          onChange={e => { setText(e.target.value); e.target.style.height = 'auto'; e.target.style.height = Math.min(e.target.scrollHeight, 96) + 'px'; }}
          onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); handleSend(); } }}
          placeholder="Ask anything..."
          disabled={disabled}
          rows={1}
          style={{
            flex: 1, background: 'transparent', border: 'none', outline: 'none',
            fontSize: 15, fontWeight: 400, color: '#000', letterSpacing: '-0.01em', minWidth: 0,
            resize: 'none', lineHeight: '1.4', maxHeight: 96, overflow: 'auto',
          }}
        />
        {/* Stop button (visible during execution) */}
        {isExecuting && onStop && (
          <button
            onClick={onStop}
            style={{
              width: 36, height: 36, borderRadius: '50%', border: 'none',
              background: '#ef4444', color: '#fff', cursor: 'pointer',
              fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'all 0.2s',
              boxShadow: '0 2px 8px rgba(239,68,68,0.3)',
            }}
            title="Stop"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor">
              <rect x="1" y="1" width="12" height="12" rx="2" />
            </svg>
          </button>
        )}
        {/* Send button */}
        <button
          onClick={handleSend}
          disabled={!hasContent || disabled}
          style={{
            width: 36, height: 36, borderRadius: '50%', border: 'none',
            background: hasContent ? '#000' : 'rgba(0,0,0,0.08)',
            color: hasContent ? '#fff' : 'rgba(0,0,0,0.3)',
            cursor: hasContent ? 'pointer' : 'default',
            fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'all 0.2s',
            boxShadow: hasContent ? '0 2px 8px rgba(0,0,0,0.2)' : 'none',
          }}
        >↑</button>
      </div>
      <input ref={fileRef} type="file" accept="image/*,video/mp4,video/webm,video/quicktime" multiple style={{ display: 'none' }} onChange={handleFileSelect} />
    </div>
  );
}

// ── LiveCardBlock — renders a streaming card from sessionStore ──

function LiveCardBlock({ card }) {
  // Always wrap in LiveCardBlockInner so every card carries the ProducerBadge
  // (executor identity) and the </> raw-data toggle. Thinking cards used to
  // render naked via CardRenderer — that hid the executor info.
  return <LiveCardBlockInner card={card} />;
}

function LiveCardBlockInner({ card }) {
  const [showRaw, setShowRaw] = useState(false);
  return (
    <div style={{
      background: V.bg, border: `1px solid ${V.border}`,
      borderRadius: 8, overflow: 'hidden', marginTop: 6,
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '8px 12px', fontSize: 11, fontWeight: 600,
        textTransform: 'uppercase', letterSpacing: '0.04em',
        color: V.textDim, borderBottom: `1px solid ${V.border}`, background: V.bg,
      }}>
        {(() => {
          // A native-IR card's header names its REAL ViewIR component (stat_grid,
          // hero_media, …) — not the generic "session_block" envelope — and links
          // into the Component Bootstrap, which scrolls to that component's card.
          const nodeType = card.data?.node?.type;
          if (card.template === 'session_block' && nodeType) {
            return (
              <Link
                to={bootstrapHref(nodeType)}
                title={`Open ${nodeType} in the Component Bootstrap`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'inherit', textDecoration: 'underline', textUnderlineOffset: 2 }}
              >
                {nodeType}
              </Link>
            );
          }
          return card.template || 'card';
        })()}
        <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
          <ProducerBadge producerModel={card.producerModel} producer={card.producer} />
          {card.template !== 'ask-user-question' && (
            <span style={{
              background: card.status === 'finalized' ? 'rgba(52,199,89,0.12)' : 'rgba(255,149,0,0.12)',
              color: card.status === 'finalized' ? V.green : V.yellow,
              padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 500,
            }}>
              {card.status === 'finalized' ? 'done' : 'streaming'}
            </span>
          )}
          <RawToggleButton active={showRaw} onClick={() => setShowRaw((s) => !s)} />
        </span>
      </div>
      <div style={{ background: '#fff', color: '#111' }}>
        {showRaw ? <RawDataView card={card} /> : <CardRenderer card={card} />}
      </div>
    </div>
  );
}

// ── ProcessingPill — live indicator while a task is queued / running ──
//
// Shows: spinner · executor (Fast/SDK · model) · active card type (text /
// tool / think / data) · elapsed time. Mirrors what SDK turns expose so a
// developer can see at a glance which lane is doing what.
function ProcessingPill({ session }) {
  const liveTasks = (session?.tasks || []).filter(t => t.status === 'running' || t.status === 'queued');
  const [now, setNow] = useState(0);
  useEffect(() => { setNow(Date.now()); }, []);
  useEffect(() => {
    if (liveTasks.length === 0) return;
    const id = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(id);
  }, [liveTasks.length]);
  if (liveTasks.length === 0) return null;
  const t = liveTasks[0];

  // Source of truth: the RUNNING task's `executor` (set from the live
  // `exec_start.executor` event). Fall back to session-sticky only when the
  // task hasn't emitted exec_start yet. Using session.context.executor as
  // the primary source made the pill lie on turns where a hard bypass
  // (`#exec_agent`) or backstop-submit flipped the server-side executor but
  // nobody broadcast an SSE update — see Issue #1391.
  const taskExecutor = t.executor || null;
  const sessionExecutor = taskExecutor || session.context?.executor;
  const sessionModel = session.context?.model;
  const modelLabel = sessionModel ? catalogModelLabel(sessionModel) : null;
  const execLabel = sessionExecutor === 'fast'
    ? '⚡ Fast · Gemini 3 Flash'
    : sessionExecutor === 'sdk'
      ? `🔷 SDK · ${modelLabel || 'model'}`
      : (modelLabel ? `🔷 ${modelLabel}` : 'processing');

  // Find the most-recent live card for this task to surface what kind of
  // work is happening right now.
  const cards = Object.values(t.cards || {});
  const liveCards = cards.filter(c => c.status !== 'finalized' && c.status !== 'done');
  const latest = (liveCards.length > 0 ? liveCards : cards).sort((a, b) => (b.ts || 0) - (a.ts || 0))[0];
  const tpl = latest?.template || '';
  const cardKind = (() => {
    if (!tpl) return null;
    if (tpl === 'thinking-process' || tpl === 'thinking') return { label: 'think', emoji: '🧠' };
    if (tpl === 'debug-html') return { label: 'tool', emoji: '🔧' };
    if (tpl === 'agent-speech' || tpl === 'md-text' || tpl === 'freeform-html') return { label: 'text', emoji: '💬' };
    if (tpl === 'ask-user-question') return { label: 'ask', emoji: '❓' };
    if (tpl === 'todo-list') return { label: 'plan', emoji: '✅' };
    if (tpl.startsWith('md-')) return { label: 'data', emoji: '📊' };
    return { label: tpl, emoji: '🃏' };
  })();

  // Elapsed time: prefer task start over session.dispatched_at.
  const startedTs = t.dispatched_at ? new Date(t.dispatched_at).getTime() : (t.ts || session.latestTs || now);
  const elapsedSec = Math.max(0, Math.floor((now - startedTs) / 1000));
  const elapsedLabel = elapsedSec < 60 ? `${elapsedSec}s` : `${Math.floor(elapsedSec / 60)}m${elapsedSec % 60}s`;

  if (t.status === 'queued') {
    return (
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 6,
        fontSize: 10, fontWeight: 500, whiteSpace: 'nowrap',
        background: 'rgba(255,149,0,0.12)', color: V.yellow,
      }} title={`Task ${t.taskId || ''} queued`}>
        <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: 3, background: V.yellow, animation: 'spin 1.2s linear infinite' }} />
        Queued · {elapsedLabel}
      </span>
    );
  }

  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6, padding: '2px 8px', borderRadius: 6,
      fontSize: 10, fontWeight: 500, whiteSpace: 'nowrap',
      background: 'rgba(255,149,0,0.12)', color: V.yellow,
    }} title={`Task ${t.taskId || ''} running — executor=${sessionExecutor || 'unset'}, model=${sessionModel || 'unset'}`}>
      <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: 3, background: V.yellow, animation: 'spin 1.2s linear infinite' }} />
      Processing
      <span style={{ opacity: 0.85 }}>· {execLabel}</span>
      {cardKind && (
        <span style={{
          padding: '0 5px', borderRadius: 3, background: 'rgba(255,149,0,0.18)',
          color: V.yellow, fontSize: 9, fontWeight: 600, letterSpacing: '0.04em',
        }}>{cardKind.emoji} {cardKind.label}</span>
      )}
      <span style={{ opacity: 0.7 }}>· {elapsedLabel}</span>
    </span>
  );
}

const CHARLOTTE_REPORT_FAILURE_STATUSES = new Set(['failed', 'error', 'cancelled', 'canceled']);
const CHARLOTTE_REPORT_READY_STATUSES = new Set(['ready', 'completed', 'complete', 'done', 'succeeded', 'success']);
const CHARLOTTE_REPORT_PENDING_STATUSES = new Set(['queued', 'pending', 'running', 'generating', 'in_progress']);
const CHARLOTTE_REPORT_MAX_POLL_MS = 120000;
const CHARLOTTE_AUTO_MEDIA_PREVIEW_LIMIT = 0;

function getCharlotteReportUrl(report) {
  if (!report) return null;
  if (report.report_url) return report.report_url;
  const artifacts = Array.isArray(report.artifacts) ? report.artifacts : [];
  for (const artifact of artifacts) {
    if (typeof artifact === 'string') return artifact;
    const url = artifact?.report_url || artifact?.url || artifact?.public_url || artifact?.href || artifact?.download_url;
    if (url) return url;
  }
  return null;
}

function isCharlotteReportReady(report) {
  const status = String(report?.status || '').toLowerCase();
  return Boolean(getCharlotteReportUrl(report)) || CHARLOTTE_REPORT_READY_STATUSES.has(status);
}

function deriveCharlotteReportState(report, fallback = 'idle') {
  if (!report) return { status: fallback, report: null, error: null };
  const status = String(report.status || '').toLowerCase();
  if (CHARLOTTE_REPORT_FAILURE_STATUSES.has(status)) {
    return {
      status: 'error',
      report,
      error: report.error || report.detail || 'Report generation failed.',
    };
  }
  if (CHARLOTTE_REPORT_PENDING_STATUSES.has(status)) {
    return { status: 'generating', report, error: null };
  }
  if (isCharlotteReportReady(report)) {
    return { status: 'ready', report, error: null };
  }
  return { status: 'generating', report, error: null };
}

function getCharlotteReportId(report) {
  return report?.report_id || report?.id || null;
}

function summarizeCharlotteReportForPanel(report) {
  if (!report || typeof report !== 'object') return report;
  const {
    payload: _payload,
    ...summary
  } = report;
  return {
    ...summary,
    payload: {},
  };
}

function getCharlotteEvidenceHref(sessionId, report) {
  const reportId = getCharlotteReportId(report);
  if (!sessionId || !reportId) return null;
  return `/playground/sessions/${encodeURIComponent(sessionId)}/evidence?reportId=${encodeURIComponent(reportId)}`;
}

function normalizeCharlotteMediaUrl(rawUrl) {
  if (!rawUrl || typeof rawUrl !== 'string') return '';
  if (/^data:/i.test(rawUrl.trim())) return '';
  if (/^blob:/i.test(rawUrl) || rawUrl.startsWith('/')) return rawUrl;
  if (/^https?:/i.test(rawUrl)) {
    try {
      const parsed = new URL(rawUrl);
      if (parsed.pathname.startsWith('/api/media/')) {
        return `${parsed.pathname}${parsed.search || ''}`;
      }
    } catch {
      return rawUrl;
    }
    return rawUrl;
  }
  if (/^(photos|videos|audio)\//.test(rawUrl)) {
    return `/api/media/${encodeURIComponent(rawUrl)}`;
  }
  return rawUrl;
}

function collectCharlotteMedia(mediaRefs) {
  if (!mediaRefs) return [];
  const rawItems = Array.isArray(mediaRefs)
    ? mediaRefs
    : typeof mediaRefs === 'object'
      ? Object.entries(mediaRefs).map(([key, value]) => ({ key, value }))
      : [{ key: 'media', value: mediaRefs }];
  return rawItems.flatMap((item, index) => {
    const value = item?.value ?? item;
    if (typeof value === 'string') return [{ id: item?.key || `media-${index}`, url: normalizeCharlotteMediaUrl(value) }];
    if (!value || typeof value !== 'object') return [];
    const url = value.preview_url || value.url || value.signed_url || value.signedUrl
      || value.media_url || value.mediaUrl || value.public_url || value.href
      || value.source_artifact_url;
    return [{
      id: item?.key || value.id || value.media_id || value.name || `media-${index}`,
      url: url ? normalizeCharlotteMediaUrl(url) : '',
      mimeType: value.mime_type || value.mimeType || value.type || '',
      kind: value.preview_kind || value.kind || '',
    }];
  });
}

function normalizeCharlotteTimeline(payload) {
  if (!payload || typeof payload !== 'object') return [];
  if (Array.isArray(payload.timeline)) return payload.timeline;
  if (Array.isArray(payload.timeline?.events)) return payload.timeline.events;
  if (Array.isArray(payload.session_timeline)) {
    return payload.session_timeline.map((item, index) => ({
      timestamp: item.ts || item.request_ts || null,
      stage: item.summary || item.type || `Session timeline ${index + 1}`,
      evidence: item.prompt || item.summary || '',
      component: 'playground-session',
      source: 'session_timeline',
      relative_ms: item.relative_ms,
      duration_ms: item.duration_ms,
      status: item.status,
      width: item.width,
    }));
  }
  return [];
}

function isCharlottePrivateArtifactUrl(url) {
  if (typeof url !== 'string') return false;
  let pathname = url;
  if (/^https?:\/\//i.test(url)) {
    try {
      const parsed = new URL(url);
      const allowedOrigins = new Set([window.location.origin]);
      const apiBaseUrl = api.getBaseUrl?.();
      if (apiBaseUrl) {
        try {
          allowedOrigins.add(new URL(apiBaseUrl, window.location.origin).origin);
        } catch {
          /* Ignore invalid runtime API URLs; relative artifacts still work. */
        }
      }
      if (!allowedOrigins.has(parsed.origin)) return false;
      pathname = parsed.pathname;
    } catch {
      return false;
    }
  }
  return pathname.startsWith('/api/users/')
    && pathname.includes('/charlotte-reports/')
    && pathname.includes('/artifacts/');
}

function CharlotteMediaPreview({ item, index, autoLoad = true }) {
  const url = item?.url || '';
  const [objectUrl, setObjectUrl] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [requested, setRequested] = useState(autoLoad);
  const isPrivateArtifact = isCharlottePrivateArtifactUrl(url);
  const renderUrl = objectUrl || (isPrivateArtifact ? '' : url);
  const isVideo = String(item?.mimeType || renderUrl || url).toLowerCase().includes('video');
  const shouldFetchPrivateArtifact = isPrivateArtifact && requested;

  useEffect(() => {
    setRequested(autoLoad);
  }, [autoLoad, url]);

  useEffect(() => {
    let cancelled = false;
    let revokeUrl = null;
    setObjectUrl(null);
    setLoadError(null);
    if (!shouldFetchPrivateArtifact) return undefined;
    api.fetchCharlotteArtifactObjectUrl(url, { accept: item?.mimeType || '*/*' })
      .then((result) => {
        if (cancelled) {
          if (result?.url && typeof URL.revokeObjectURL === 'function') URL.revokeObjectURL(result.url);
          return;
        }
        revokeUrl = result.url;
        setObjectUrl(result.url);
      })
      .catch((err) => {
        if (!cancelled) setLoadError(err?.message || 'Could not load private media artifact.');
      });
    return () => {
      cancelled = true;
      if (revokeUrl && typeof URL.revokeObjectURL === 'function') URL.revokeObjectURL(revokeUrl);
    };
  }, [item?.mimeType, shouldFetchPrivateArtifact, url]);

  return (
    <div style={{ border: `1px solid ${V.border}`, borderRadius: 8, padding: 8, background: V.bg }}>
      {renderUrl ? (
        isVideo ? (
          <video src={renderUrl} controls style={{ maxWidth: 260, maxHeight: 200, display: 'block', borderRadius: 6 }} />
        ) : (
          <img src={renderUrl} alt={`Input media ${index + 1}`} style={{ maxWidth: 260, maxHeight: 200, objectFit: 'contain', display: 'block', borderRadius: 6 }} />
        )
      ) : (
        <div style={{
          width: 240,
          minHeight: 140,
          display: 'grid',
          placeItems: 'center',
          border: `1px dashed ${V.border}`,
          borderRadius: 6,
          color: V.textDim,
          fontSize: 12,
          textAlign: 'center',
          padding: 10,
        }}>
          {loadError || (isPrivateArtifact && !requested ? (
            <button
              type="button"
              onClick={() => setRequested(true)}
              style={{ ...CHARLOTTE_BUTTON_STYLE, fontSize: 11 }}
            >
              Load private media preview
            </button>
          ) : (isPrivateArtifact ? 'Loading private media...' : 'Media unavailable'))}
        </div>
      )}
      <div style={{ marginTop: 6, fontSize: 11, color: V.textDim, wordBreak: 'break-all' }}>
        {item.id}{item.kind ? ` · ${item.kind}` : ''}
      </div>
    </div>
  );
}

function CharlotteEvidenceLink({ link }) {
  const [state, setState] = useState({ status: 'idle', error: null });
  const isPrivateArtifact = isCharlottePrivateArtifactUrl(link.url);

  const openPrivateArtifact = useCallback(async () => {
    if (!isPrivateArtifact || state.status === 'loading') return;
    setState({ status: 'loading', error: null });
    const targetWindow = window.open('', '_blank');
    if (!targetWindow) {
      setState({
        status: 'error',
        error: 'Popup blocked. Allow popups or open the report from the evidence page.',
      });
      return;
    }
    try {
      targetWindow.opener = null;
      targetWindow.document.title = 'Opening private artifact...';
      targetWindow.document.body.textContent = 'Opening private artifact...';
    } catch (_) {
      /* Cross-browser best effort only. */
    }
    let artifact = null;
    try {
      artifact = await api.fetchCharlotteArtifactObjectUrl(link.url, { accept: '*/*' });
      targetWindow.location.href = artifact.url;
      if (typeof URL.revokeObjectURL === 'function') {
        setTimeout(() => URL.revokeObjectURL(artifact.url), 60_000);
      }
      setState({ status: 'idle', error: null });
    } catch (err) {
      try { targetWindow?.close?.(); } catch (_) { /* noop */ }
      if (artifact?.url && typeof URL.revokeObjectURL === 'function') {
        URL.revokeObjectURL(artifact.url);
      }
      setState({ status: 'error', error: err?.message || 'Could not open private artifact.' });
    }
  }, [isPrivateArtifact, link.url, state.status]);

  const commonStyle = {
    display: 'block',
    width: '100%',
    textAlign: 'left',
    border: `1px solid ${V.border}`,
    borderRadius: 8,
    padding: 10,
    color: V.accent,
    textDecoration: 'none',
    background: V.bg,
    cursor: 'pointer',
  };
  const body = (
    <>
      <div style={{ fontWeight: 700, fontSize: 13 }}>{link.label}</div>
      <div style={{ color: V.textDim, fontSize: 12, marginTop: 3 }}>{link.why}</div>
      {isPrivateArtifact && (
        <div style={{ color: V.textDim, fontSize: 11, marginTop: 4 }}>
          Opens through authenticated fetch.
        </div>
      )}
      {state.status === 'error' && (
        <div style={{ color: V.red, fontSize: 11, marginTop: 4 }}>{state.error}</div>
      )}
    </>
  );

  if (isPrivateArtifact) {
    return (
      <button type="button" onClick={openPrivateArtifact} disabled={state.status === 'loading'} style={commonStyle}>
        {state.status === 'loading' ? 'Opening private artifact...' : body}
      </button>
    );
  }

  return (
    <a href={link.url} target="_blank" rel="noopener noreferrer" style={commonStyle}>
      {body}
    </a>
  );
}

function CharlotteValue({ value }) {
  if (value == null || value === '') return <span style={{ color: V.textDim }}>unavailable</span>;
  if (typeof value === 'object') {
    return (
      <pre style={{
        margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
        fontSize: 11, lineHeight: 1.5, color: V.text, background: V.bg,
        border: `1px solid ${V.border}`, borderRadius: 6, padding: 8,
      }}>
        {JSON.stringify(value, null, 2)}
      </pre>
    );
  }
  return <span>{String(value)}</span>;
}

function CharlotteSection({ title, children }) {
  return (
    <section style={{
      background: V.bgCard, border: `1px solid ${V.border}`, borderRadius: 8,
      padding: 16, display: 'grid', gap: 12,
    }}>
      <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: V.text }}>{title}</h2>
      {children}
    </section>
  );
}

const CHARLOTTE_BUTTON_STYLE = {
  background: V.bgHover,
  color: V.text,
  border: `1px solid ${V.border}`,
  borderRadius: 6,
  padding: '6px 10px',
  cursor: 'pointer',
  fontSize: 12,
};

const CHARLOTTE_BADGE_STYLE = {
  display: 'inline-flex',
  alignItems: 'center',
  minHeight: 24,
  border: `1px solid ${V.border}`,
  borderRadius: 6,
  padding: '2px 8px',
  fontSize: 11,
  fontWeight: 700,
};

function isSafeCharlotteEvidenceUrl(url) {
  if (typeof url !== 'string' || !url.trim()) return false;
  const value = url.trim();
  if (value.startsWith('/')) return !value.startsWith('//');
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'https:' || parsed.protocol === 'http:';
  } catch {
    return false;
  }
}

function normalizeCharlotteEvidenceLinks(primaryLinks, fallbackLinks) {
  const rawLinks = Array.isArray(primaryLinks)
    ? primaryLinks
    : (Array.isArray(fallbackLinks) ? fallbackLinks : []);
  return rawLinks
    .filter((link) => link && typeof link === 'object')
    .map((link) => ({
      label: typeof link.label === 'string' && link.label.trim() ? link.label.trim() : 'Evidence link',
      url: typeof link.url === 'string' ? link.url.trim() : '',
      why: typeof link.why === 'string' ? link.why : '',
    }))
    .filter((link) => isSafeCharlotteEvidenceUrl(link.url));
}

function CharlotteEvidenceTable({ rows }) {
  return (
    <div style={{ display: 'grid', border: `1px solid ${V.border}`, borderRadius: 8, overflow: 'hidden' }}>
      {rows.map(([key, value], index) => (
        <div
          key={`${key}-${index}`}
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(120px, 190px) minmax(0, 1fr)',
            borderBottom: index === rows.length - 1 ? 'none' : `1px solid ${V.border}`,
          }}
        >
          <div style={{ padding: '8px 10px', background: V.bg, color: V.textDim, fontSize: 12, fontWeight: 600 }}>{key}</div>
          <div style={{ padding: '8px 10px', color: V.text, fontSize: 12, wordBreak: 'break-word' }}>
            <CharlotteValue value={value} />
          </div>
        </div>
      ))}
    </div>
  );
}

function charlotteEvidenceResultItems(externalEvidence, key) {
  const resultMap = externalEvidence?.[key]?.results;
  if (!resultMap || typeof resultMap !== 'object') return [];
  return Object.entries(resultMap)
    .filter(([, value]) => value && typeof value === 'object')
    .map(([name, value]) => ({ name, ...value }));
}

function formatCharlotteSampleValue(sample) {
  const raw = Array.isArray(sample?.value) ? sample.value[1] : sample?.value;
  if (raw == null || raw === '') return 'no value';
  const numeric = Number(raw);
  if (Number.isFinite(numeric)) return Math.abs(numeric) >= 1 ? numeric.toFixed(3) : numeric.toPrecision(3);
  return String(raw);
}

function CharlotteEvidenceMiniCard({ title, subtitle, children }) {
  return (
    <div style={{ border: `1px solid ${V.border}`, borderRadius: 8, padding: 10, background: V.bg }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 13, fontWeight: 800 }}>{title}</div>
        {subtitle && <div style={{ fontSize: 11, color: V.textDim }}>{subtitle}</div>}
      </div>
      <div style={{ marginTop: 8 }}>{children}</div>
    </div>
  );
}

function CharlotteExternalEvidenceSummary({ externalEvidence, analysis, resolvedIds = {} }) {
  const metrics = charlotteEvidenceResultItems(externalEvidence, 'metrics');
  const logs = charlotteEvidenceResultItems(externalEvidence, 'logs');
  const traceSummary = externalEvidence?.trace?.summary || {};
  const managedMetadata = externalEvidence?.managed_agent?.metadata || {};
  const managedEvents = Array.isArray(managedMetadata.events) ? managedMetadata.events : [];
  const traceSpans = Array.isArray(traceSummary.spans) ? traceSummary.spans : [];

  return (
    <div style={{ display: 'grid', gap: 10 }}>
      <CharlotteEvidenceMiniCard
        title="Prometheus metrics"
        subtitle={externalEvidence?.metrics?.status || 'not collected'}
      >
        {metrics.length ? (
          <div style={{ display: 'grid', gap: 8 }}>
            {metrics.slice(0, 8).map((metric) => {
              const samples = metric.result?.samples || [];
              return (
                <div key={metric.name} style={{ display: 'grid', gap: 4 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                    <b style={{ fontSize: 12 }}>{metric.name}</b>
                    <span style={{ color: V.textDim, fontSize: 11 }}>
                      {metric.status || 'unknown'} · series {metric.result?.series_count ?? 0}
                    </span>
                  </div>
                  {samples.length ? (
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {samples.slice(0, 4).map((sample, index) => (
                        <span key={index} style={CHARLOTTE_BADGE_STYLE}>
                          {sample.metric?.route || sample.metric?.outcome || sample.metric?.status || sample.metric?.pod || `sample ${index + 1}`}: {formatCharlotteSampleValue(sample)}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <div style={{ color: V.textDim, fontSize: 12 }}>No sample values returned for this query.</div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <CharlotteValue value={externalEvidence?.metrics || { status: 'unavailable' }} />
        )}
      </CharlotteEvidenceMiniCard>

      <CharlotteEvidenceMiniCard
        title="Loki logs"
        subtitle={externalEvidence?.logs?.status || 'not collected'}
      >
        {logs.length ? (
          <div style={{ display: 'grid', gap: 6 }}>
            {logs.slice(0, 8).map((item) => (
              <div key={item.name} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', fontSize: 12 }}>
                <b>{item.name}</b>
                <span style={{ color: V.textDim }}>
                  {item.status || 'unknown'} · streams {item.result?.stream_count ?? 0} · entries {item.result?.entry_count ?? 0}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <CharlotteValue value={externalEvidence?.logs || { status: 'unavailable' }} />
        )}
      </CharlotteEvidenceMiniCard>

      <CharlotteEvidenceMiniCard
        title="Jaeger trace"
        subtitle={externalEvidence?.trace?.status || 'not collected'}
      >
        <div style={{ display: 'grid', gap: 6 }}>
          <div style={{ color: V.textDim, fontSize: 12 }}>
            traces {traceSummary.trace_count ?? externalEvidence?.trace?.trace_count ?? 0} · spans {traceSummary.span_count ?? externalEvidence?.trace?.span_count ?? 0}
          </div>
          {traceSpans.length ? traceSpans.slice(0, 6).map((span) => (
            <div key={span.span_id || span.operation_name} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', fontSize: 12 }}>
              <b>{span.service_name || 'service'} · {span.operation_name || 'operation'}</b>
              <span style={{ color: V.textDim }}>{formatDuration(span.duration != null ? span.duration / 1000 : null)}</span>
            </div>
          )) : (
            <div style={{ color: V.textDim, fontSize: 12 }}>No span summary persisted.</div>
          )}
        </div>
      </CharlotteEvidenceMiniCard>

      <CharlotteEvidenceMiniCard
        title="Managed Agent debug"
        subtitle={externalEvidence?.managed_agent?.status || 'link only'}
      >
        <div style={{ display: 'grid', gap: 6 }}>
          <CharlotteEvidenceTable rows={[
            ['managed_session_id', externalEvidence?.managed_agent?.managed_session_id || managedMetadata.session?.id || resolvedIds.managed_session_id],
            ['managed_event_id', externalEvidence?.managed_agent?.managed_event_id || resolvedIds.managed_event_id],
            ['subagent_thread_id', externalEvidence?.managed_agent?.subagent_thread_id || resolvedIds.subagent_thread_id],
            ['session_status', managedMetadata.session?.status],
            ['event_count', managedMetadata.event_count ?? managedEvents.length],
          ]} />
          {managedEvents.length > 0 && (
            <div style={{ display: 'grid', gap: 5 }}>
              {managedEvents.slice(0, 8).map((event) => (
                <div key={event.id || `${event.type}-${event.created_at}`} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', fontSize: 12 }}>
                  <b>{event.type || 'event'}</b>
                  <span style={{ color: V.textDim }}>{event.status || 'status unknown'} · {event.created_at || 'time unknown'}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </CharlotteEvidenceMiniCard>

      <CharlotteEvidenceMiniCard title="Analysis snapshot" subtitle={analysis?.verdict || 'evidence'}>
        <CharlotteEvidenceTable rows={[
          ['critical_path', analysis?.critical_path],
          ['primary_bottleneck', analysis?.primary_bottleneck],
          ['root_cause_hypothesis', analysis?.root_cause_hypothesis],
        ]} />
      </CharlotteEvidenceMiniCard>
    </div>
  );
}

export function CharlotteSessionEvidencePage() {
  const { sessionId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const reportId = new URLSearchParams(location.search).get('reportId');
  const [state, setState] = useState({ status: 'loading', report: null, error: null });
  const [reloadSeq, setReloadSeq] = useState(0);
  const [actionState, setActionState] = useState({ busy: false, error: null });

  useEffect(() => {
    let cancelled = false;
    let timer = null;
    const startedAt = Date.now();
    async function load() {
      if (!sessionId) {
        setState({ status: 'error', report: null, error: 'Missing session id.' });
        return;
      }
      if (!reportId) {
        setState({
          status: 'error',
          report: null,
          error: 'Missing report id. Generate evidence from the Playground session panel first.',
        });
        return;
      }
      setActionState((current) => ({ ...current, error: null }));
      setState({ status: 'loading', report: null, error: null });
      try {
        const report = await api.getCharlotteReport(sessionId, reportId);
        if (cancelled) return;
        const derived = deriveCharlotteReportState(report);
        if (derived.status === 'ready') {
          setState({ status: 'ready', report, error: null });
        } else if (derived.status === 'error') {
          setState({ status: 'error', report, error: derived.error || 'Charlotte evidence generation failed.' });
        } else {
          if (Date.now() - startedAt > CHARLOTTE_REPORT_MAX_POLL_MS) {
            setState({ status: 'error', report, error: 'Charlotte evidence generation timed out. Refresh or regenerate the report from the session panel.' });
            return;
          }
          setState({ status: 'generating', report, error: null });
          timer = setTimeout(load, 2500);
        }
      } catch (err) {
        if (!cancelled) {
          setState({ status: 'error', report: null, error: err?.message || 'Could not load session evidence.' });
        }
      }
    }
    load();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [reloadSeq, reportId, sessionId]);

  const handleRefreshReport = useCallback(() => {
    if (!sessionId || !reportId || actionState.busy) return;
    setReloadSeq((value) => value + 1);
  }, [actionState.busy, reportId, sessionId]);

  const handleRegenerateReport = useCallback(async ({ includePrivateEvidence = false } = {}) => {
    if (!sessionId || actionState.busy) return;
    setActionState({ busy: true, error: null });
    setState((current) => ({ ...current, status: 'generating', error: null }));
    try {
      const currentReportEnv = state.report?.environment || state.report?.payload?.environment;
      const regenerateOptions = {
        include_private_evidence: includePrivateEvidence,
        regenerate: true,
      };
      if (currentReportEnv) {
        regenerateOptions.env = currentReportEnv;
      }
      const report = await api.createCharlotteReport(sessionId, regenerateOptions);
      const nextReportId = getCharlotteReportId(report);
      if (!nextReportId) {
        setState((current) => ({ ...current, status: 'error', report, error: 'Report request did not return a report id.' }));
        return;
      }
      if (nextReportId === reportId) {
        const derived = deriveCharlotteReportState(report);
        setState({ status: derived.status, report, error: derived.error });
        if (derived.status === 'generating') {
          setReloadSeq((value) => value + 1);
        }
        return;
      }
      navigate(`/playground/sessions/${encodeURIComponent(sessionId)}/evidence?reportId=${encodeURIComponent(nextReportId)}`);
    } catch (err) {
      setState((current) => ({
        ...current,
        status: 'error',
        error: err?.message || 'Could not regenerate Charlotte evidence.',
      }));
      setActionState({ busy: false, error: err?.message || 'Could not regenerate Charlotte evidence.' });
      return;
    } finally {
      setActionState((current) => ({ ...current, busy: false }));
    }
  }, [actionState.busy, navigate, reportId, sessionId, state.report]);

  const report = state.report || {};
  const payload = report.payload || {};
  const session = payload.session || {};
  const input = payload.input || {};
  const output = payload.output || {};
  const analysis = payload.analysis || {};
  const externalEvidence = payload.external_evidence || {};
  const resolvedIds = payload.resolved_ids || report.resolved_ids || {};
  const timeline = normalizeCharlotteTimeline(payload);
  const logs = Array.isArray(payload.execution_logs) ? payload.execution_logs : [];
  const taskArtifacts = Array.isArray(payload.task_artifacts) ? payload.task_artifacts : [];
  const localLogEvidence = payload.local_log_evidence || {};
  const localLogSamples = Array.isArray(payload.local_log_event_samples) ? payload.local_log_event_samples : [];
  const criticalSegments = Array.isArray(analysis.critical_path_segments) ? analysis.critical_path_segments : [];
  const metrics = Array.isArray(payload.metrics_catalog) ? payload.metrics_catalog : [];
  const links = normalizeCharlotteEvidenceLinks(payload.evidence_links, report.evidence_links);
  const media = collectCharlotteMedia(input.media_refs || input.media);
  const reportArtifacts = Array.isArray(payload.artifact_index)
    ? payload.artifact_index
    : (Array.isArray(report.artifacts) ? report.artifacts : []);
  const primaryBottleneck = analysis.primary_bottleneck || criticalSegments[0] || null;
  const actionDisabled = !sessionId || !reportId || actionState.busy || state.status === 'loading' || state.status === 'generating';

  return (
    <div style={{
      height: '100%', minHeight: 0, boxSizing: 'border-box',
      background: V.bg, color: V.text, overflowY: 'auto',
    }}>
      <div style={{ maxWidth: 1180, margin: '0 auto', padding: '24px 24px 56px', display: 'grid', gap: 14 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
          <div>
            <button
              type="button"
              onClick={() => navigate(`/playground/chat/${encodeURIComponent(sessionId || '')}`)}
              style={{ ...CHARLOTTE_BUTTON_STYLE, marginBottom: 10 }}
            >
              ← Back to session
            </button>
            <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800 }}>Session Evidence</h1>
            <div style={{ color: V.textDim, fontSize: 12, marginTop: 4 }}>
              Charlotte triage replay for one Playground session.
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <button
              type="button"
              disabled={actionDisabled}
              onClick={handleRefreshReport}
              style={{ ...CHARLOTTE_BUTTON_STYLE, opacity: actionDisabled ? 0.55 : 1 }}
            >
              Refresh report
            </button>
            <button
              type="button"
              disabled={actionDisabled}
              onClick={() => handleRegenerateReport({ includePrivateEvidence: false })}
              style={{ ...CHARLOTTE_BUTTON_STYLE, opacity: actionDisabled ? 0.55 : 1 }}
            >
              Regenerate report
            </button>
            <button
              type="button"
              disabled={actionDisabled}
              onClick={() => handleRegenerateReport({ includePrivateEvidence: true })}
              style={{ ...CHARLOTTE_BUTTON_STYLE, opacity: actionDisabled ? 0.55 : 1 }}
              title="Persist private prompt/result snapshots and copied input-media previews for admin-only deep replay."
            >
              Generate private replay
            </button>
            <span style={{ ...CHARLOTTE_BADGE_STYLE, background: V.accentDim, color: V.accent }}>{report.environment || payload.environment || 'env unknown'}</span>
            <span style={{ ...CHARLOTTE_BADGE_STYLE, background: V.bgCard, color: V.textDim }}>{report.namespace || payload.namespace || 'namespace unknown'}</span>
            <span style={{ ...CHARLOTTE_BADGE_STYLE, background: V.bgCard, color: V.textDim }}>{report.status || state.status}</span>
          </div>
        </div>

        {actionState.error && (
          <CharlotteSection title="Action error">
            <div style={{ color: V.red, fontSize: 13 }}>{actionState.error}</div>
          </CharlotteSection>
        )}

        {state.status === 'loading' && (
          <CharlotteSection title="Loading">
            <div style={{ color: V.textDim, fontSize: 13 }}>Loading Charlotte evidence...</div>
          </CharlotteSection>
        )}

        {state.status === 'generating' && (
          <CharlotteSection title="Generating">
            <div style={{ color: V.textDim, fontSize: 13 }}>
              Charlotte evidence is {report.status || 'running'}. This page will refresh automatically.
            </div>
          </CharlotteSection>
        )}

        {state.status === 'error' && (
          <CharlotteSection title="Error">
            <div style={{ color: V.red, fontSize: 13 }}>{state.error}</div>
          </CharlotteSection>
        )}

        {state.status === 'ready' && (
          <>
            <CharlotteSection title="1. Session Overview">
              <CharlotteEvidenceTable rows={[
                ['session_id', session.session_id || sessionId],
                ['report_id', report.report_id],
                ['status', session.status || report.status],
                ['session_type', session.session_type],
                ['task_id', resolvedIds.task_id],
                ['trace_id', resolvedIds.trace_id],
                ['managed_session_id', resolvedIds.managed_session_id],
                ['managed_event_id', resolvedIds.managed_event_id],
                ['subagent_thread_id', resolvedIds.subagent_thread_id],
              ]} />
            </CharlotteSection>

            <CharlotteSection title="2. Input Replay">
              <CharlotteEvidenceTable rows={[
                ['prompt', input.prompt],
                ['prompt_chars', input.prompt_chars],
                ['context', input.context],
                ['media_count', media.length],
              ]} />
              {media.length > 0 && (
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  {media.map((item, index) => (
                    <CharlotteMediaPreview
                      key={item.id || item.url}
                      item={item}
                      index={index}
                      autoLoad={index < CHARLOTTE_AUTO_MEDIA_PREVIEW_LIMIT}
                    />
                  ))}
                </div>
              )}
            </CharlotteSection>

            <CharlotteSection title="3. Output Replay">
              <CharlotteEvidenceTable rows={[
                ['result_summary', output.result_summary],
                ['result', output.result],
              ]} />
            </CharlotteSection>

            <CharlotteSection title="4. Lifecycle Timeline">
              <CharlotteEvidenceTable rows={[
                ['timeline_events', timeline.length],
                ['critical_segments', criticalSegments.length],
                ['primary_bottleneck', primaryBottleneck],
                ['critical_path', analysis.critical_path],
              ]} />
              {criticalSegments.length > 0 && (
                <div style={{ display: 'grid', gap: 8, marginBottom: 14 }}>
                  <div style={{ fontSize: 13, fontWeight: 700 }}>Critical path segments</div>
                  {criticalSegments.slice(0, 8).map((segment, index) => (
                    <div key={`${segment.label || 'segment'}-${index}`} style={{ border: `1px solid ${V.border}`, borderRadius: 8, padding: 10, background: V.bgCard }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                        <div style={{ fontSize: 13, fontWeight: 700 }}>{segment.label || `Segment ${index + 1}`}</div>
                        <div style={{ fontSize: 13, fontWeight: 800, color: V.accent }}>{formatDuration(segment.duration_ms)}</div>
                      </div>
                      <div style={{ color: V.textDim, fontSize: 12, marginTop: 4 }}>
                        {segment.component || 'component unknown'} · {formatDuration(segment.start_ms)} → {formatDuration(segment.end_ms)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div style={{ display: 'grid', gap: 10 }}>
                {timeline.length ? timeline.map((item, index) => (
                  <div key={`${item.stage || 'stage'}-${index}`} style={{ display: 'grid', gridTemplateColumns: 'minmax(130px, 230px) minmax(0, 1fr)', gap: 10, alignItems: 'start' }}>
                    <div style={{ color: V.textDim, fontSize: 11, wordBreak: 'break-word' }}>{item.timestamp || 'time unavailable'}</div>
                    <div style={{ border: `1px solid ${V.border}`, borderRadius: 8, padding: 10, background: V.bg }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                        <div style={{ fontSize: 13, fontWeight: 700 }}>{item.stage || `Stage ${index + 1}`}</div>
                        <div style={{ fontSize: 11, color: V.textDim }}>{item.component || item.source || ''}</div>
                      </div>
                      <div style={{ color: V.textDim, fontSize: 12, marginTop: 3 }}>{item.evidence || ''}</div>
                      <div style={{ color: V.textDim, fontSize: 11, marginTop: 3 }}>
                        relative {formatDuration(item.relative_ms)}{item.duration_ms != null ? ` · duration ${formatDuration(item.duration_ms)}` : ''}{item.status ? ` · ${item.status}` : ''}
                      </div>
                      <div style={{ height: 8, background: '#e5e7eb', borderRadius: 99, marginTop: 8, overflow: 'hidden' }}>
                        <span style={{ display: 'block', width: `${Math.max(3, Math.min(100, Number(item.width) || 10))}%`, height: '100%', background: V.accent, borderRadius: 99 }} />
                      </div>
                    </div>
                  </div>
                )) : (
                  <div style={{ color: V.textDim, fontSize: 13 }}>No timeline events were captured.</div>
                )}
              </div>
            </CharlotteSection>

            <CharlotteSection title="5. Task Execution Logs">
              <div style={{ color: V.textDim, fontSize: 12, marginBottom: 8 }}>
                Local log snapshot: {localLogEvidence.status || 'unknown'} · events {localLogEvidence.event_count ?? 0}
              </div>
              {localLogSamples.length > 0 && (
                <div style={{ display: 'grid', gap: 8, marginBottom: 12 }}>
                  <div style={{ fontSize: 13, fontWeight: 700 }}>Local log samples</div>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                      <thead>
                        <tr>
                          {['time', 'service', 'event', 'status', 'duration', 'source'].map((head) => (
                            <th key={head} style={{ textAlign: 'left', padding: 8, borderBottom: `1px solid ${V.border}`, color: V.textDim }}>{head}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {localLogSamples.slice(0, 12).map((item, index) => (
                          <tr key={`${item.event || 'event'}-${item.ts_ms || index}-${index}`}>
                            <td style={{ padding: 8, borderBottom: `1px solid ${V.border}`, wordBreak: 'break-word' }}>
                              <CharlotteValue value={item.timestamp || item.ts_ms} />
                            </td>
                            <td style={{ padding: 8, borderBottom: `1px solid ${V.border}` }}>
                              <CharlotteValue value={item.service || item.component || item.source} />
                            </td>
                            <td style={{ padding: 8, borderBottom: `1px solid ${V.border}` }}>
                              <CharlotteValue value={item.event || item.step_name || item.marker} />
                            </td>
                            <td style={{ padding: 8, borderBottom: `1px solid ${V.border}` }}>
                              <CharlotteValue value={item.status || item.outcome} />
                            </td>
                            <td style={{ padding: 8, borderBottom: `1px solid ${V.border}` }}>
                              <CharlotteValue value={item.duration_ms != null ? formatDuration(item.duration_ms) : null} />
                            </td>
                            <td style={{ padding: 8, borderBottom: `1px solid ${V.border}`, wordBreak: 'break-word' }}>
                              <CharlotteValue value={item.file ? `${item.file}:${item.line || '?'}` : item.source} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr>
                      {['executed_at', 'task_id', 'trace_id', 'status', 'duration_ms', 'media_count'].map((head) => (
                        <th key={head} style={{ textAlign: 'left', padding: 8, borderBottom: `1px solid ${V.border}`, color: V.textDim }}>{head}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {logs.length ? logs.map((log, index) => (
                      <tr key={log.task_id || index}>
                        {[log.executed_at, log.task_id, log.trace_id, log.status, log.duration_ms, log.media_count].map((value, cellIndex) => (
                          <td key={cellIndex} style={{ padding: 8, borderBottom: `1px solid ${V.border}`, wordBreak: 'break-word' }}>
                            <CharlotteValue value={value} />
                          </td>
                        ))}
                      </tr>
                    )) : (
                      <tr><td colSpan={6} style={{ padding: 8, color: V.textDim }}>No TaskExecutionLog rows were found.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              {taskArtifacts.length > 0 && (
                <div style={{ display: 'grid', gap: 8, marginTop: 12 }}>
                  <div style={{ fontSize: 13, fontWeight: 700 }}>Local task artifacts</div>
                  {taskArtifacts.slice(0, 8).map((artifact, index) => (
                    <div key={artifact.task_id || index} style={{ border: `1px solid ${V.border}`, borderRadius: 8, padding: 10, background: V.bg }}>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                        <b>{artifact.task_id || `task ${index + 1}`}</b>
                        <span style={CHARLOTTE_BADGE_STYLE}>{artifact.status || 'unknown'}</span>
                        <span style={{ color: V.textDim, fontSize: 12 }}>{formatDuration(artifact.duration_ms)}</span>
                        <span style={{ color: V.textDim, fontSize: 12 }}>{artifact.card_count || 0} cards</span>
                      </div>
                      <div style={{ color: V.textDim, fontSize: 12, marginTop: 6 }}>
                        <CharlotteValue value={artifact.result_summary || artifact.managed_agent || 'No artifact summary'} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CharlotteSection>

            <CharlotteSection title="6. Metrics">
              <div style={{ display: 'grid', gap: 8 }}>
                {metrics.length ? metrics.map((metric) => (
                  <div key={metric.name} style={{ border: `1px solid ${V.border}`, borderRadius: 8, padding: 10, background: V.bg }}>
                    <div style={{ fontSize: 13, fontWeight: 700 }}>{metric.name}</div>
                    <div style={{ fontSize: 12, color: V.textDim, marginTop: 3 }}>{metric.query}</div>
                    <div style={{ fontSize: 12, marginTop: 5 }}>{metric.why}</div>
                  </div>
                )) : (
                  <div style={{ color: V.textDim, fontSize: 13 }}>No metrics catalog entries were captured.</div>
                )}
              </div>
            </CharlotteSection>

            <CharlotteSection title="7. Evidence Links">
              <div style={{ display: 'grid', gap: 8 }}>
                {links.length ? links.map((link) => (
                  <CharlotteEvidenceLink
                    key={`${link.label}-${link.url}`}
                    link={link}
                  />
                )) : (
                  <div style={{ color: V.textDim, fontSize: 13 }}>No evidence links were captured.</div>
                )}
              </div>
            </CharlotteSection>

            <CharlotteSection title="8. External Evidence Status">
              <CharlotteExternalEvidenceSummary externalEvidence={externalEvidence} analysis={analysis} resolvedIds={resolvedIds} />
            </CharlotteSection>

            <CharlotteSection title="9. Raw Evidence Artifacts">
              <div style={{ color: V.textDim, fontSize: 12 }}>
                Private report artifacts persisted for replay, audit, and AI-assisted triage.
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr>
                      {['kind', 'mime_type', 'artifact', 'link'].map((head) => (
                        <th key={head} style={{ textAlign: 'left', padding: 8, borderBottom: `1px solid ${V.border}`, color: V.textDim }}>{head}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {reportArtifacts.length ? reportArtifacts.map((artifact, index) => (
                      <tr key={artifact.id || artifact.url || index}>
                        <td style={{ padding: 8, borderBottom: `1px solid ${V.border}` }}>
                          <CharlotteValue value={artifact.kind} />
                        </td>
                        <td style={{ padding: 8, borderBottom: `1px solid ${V.border}` }}>
                          <CharlotteValue value={artifact.mime_type} />
                        </td>
                        <td style={{ padding: 8, borderBottom: `1px solid ${V.border}`, wordBreak: 'break-word' }}>
                          <CharlotteValue value={artifact.id || artifact.name || `artifact ${index + 1}`} />
                        </td>
                        <td style={{ padding: 8, borderBottom: `1px solid ${V.border}`, wordBreak: 'break-word' }}>
                          {artifact.url ? (
                            <CharlotteEvidenceLink link={{ label: 'Open artifact', url: artifact.url, why: artifact.url }} />
                          ) : (
                            <span style={{ color: V.textDim }}>unavailable</span>
                          )}
                        </td>
                      </tr>
                    )) : (
                      <tr><td colSpan={4} style={{ padding: 8, color: V.textDim }}>No artifact index was captured.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CharlotteSection>
          </>
        )}
      </div>
    </div>
  );
}

export function CharlotteReportAction({ sessionId, pollIntervalMs = 2000, maxPollMs = CHARLOTTE_REPORT_MAX_POLL_MS }) {
  const [reportState, setReportState] = useState({
    status: 'idle',
    report: null,
    reports: [],
    historyOpen: false,
    error: null,
  });
  const timerRef = useRef(null);
  const requestSeqRef = useRef(0);

  const clearPollTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const finishFromReport = useCallback((report) => {
    const reportSummary = summarizeCharlotteReportForPanel(report);
    if (isCharlotteReportReady(report)) {
      setReportState((current) => ({
        ...current,
        status: 'ready',
        report: reportSummary,
        reports: [reportSummary, ...current.reports.filter((item) => item.report_id !== report.report_id)],
        error: null,
      }));
      return true;
    }
    const status = String(report?.status || '').toLowerCase();
    if (CHARLOTTE_REPORT_FAILURE_STATUSES.has(status)) {
      setReportState({
        reports: [],
        historyOpen: false,
        status: 'error',
        report: reportSummary,
        error: report?.error || report?.detail || 'Report generation failed.',
      });
      return true;
    }
    return false;
  }, []);

  const schedulePoll = useCallback((sid, reportId, requestSeq, startedAt = Date.now()) => {
    clearPollTimer();
    timerRef.current = setTimeout(async () => {
      if (requestSeqRef.current !== requestSeq) return;
      if (Date.now() - startedAt > maxPollMs) {
        setReportState({
          status: 'error',
          report: null,
          reports: [],
          historyOpen: false,
          error: 'Charlotte evidence generation timed out. Refresh or regenerate the report.',
        });
        return;
      }
      try {
        const report = await api.getCharlotteReport(sid, reportId);
        if (requestSeqRef.current !== requestSeq) return;
        if (!finishFromReport(report)) {
        setReportState((current) => ({ ...current, status: 'generating', report: summarizeCharlotteReportForPanel(report), error: null }));
          schedulePoll(sid, reportId, requestSeq, startedAt);
        }
      } catch (err) {
        if (requestSeqRef.current !== requestSeq) return;
        setReportState({
          status: 'error',
          report: null,
          reports: [],
          historyOpen: false,
          error: err?.message || 'Could not generate report.',
        });
      }
    }, pollIntervalMs);
  }, [clearPollTimer, finishFromReport, maxPollMs, pollIntervalMs]);

  useEffect(() => {
    clearPollTimer();
    setReportState({ status: 'idle', report: null, reports: [], historyOpen: false, error: null });
    requestSeqRef.current += 1;
    return clearPollTimer;
  }, [clearPollTimer, sessionId]);

  const handleGenerateReport = useCallback(async ({ regenerate = false, includePrivateEvidence = false } = {}) => {
    if (!sessionId || reportState.status === 'generating') return;
    clearPollTimer();
    const requestSeq = requestSeqRef.current + 1;
    requestSeqRef.current = requestSeq;
    setReportState((current) => ({ ...current, status: 'generating', report: null, error: null }));
    try {
      const report = await api.createCharlotteReport(sessionId, {
        include_private_evidence: includePrivateEvidence,
        regenerate,
      });
      if (requestSeqRef.current !== requestSeq) return;
      if (finishFromReport(report)) return;
      const reportId = report?.report_id || report?.id;
      if (!reportId) {
        setReportState((current) => ({
          ...current,
          status: 'error',
          report: summarizeCharlotteReportForPanel(report),
          error: 'Report request did not return a report id.',
        }));
        return;
      }
      setReportState((current) => ({ ...current, status: 'generating', report: summarizeCharlotteReportForPanel(report), error: null }));
      schedulePoll(sessionId, reportId, requestSeq);
    } catch (err) {
      if (requestSeqRef.current !== requestSeq) return;
      setReportState((current) => ({
        ...current,
        status: 'error',
        report: null,
        error: err?.message || 'Could not generate report.',
      }));
    }
  }, [clearPollTimer, finishFromReport, reportState.status, schedulePoll, sessionId]);

  const handleRefreshReports = useCallback(async () => {
    if (!sessionId || reportState.status === 'generating') return;
    clearPollTimer();
    const requestSeq = requestSeqRef.current + 1;
    requestSeqRef.current = requestSeq;
    setReportState((current) => ({ ...current, status: 'loading', error: null }));
    try {
      const reports = await api.listCharlotteReports(sessionId, {
        limit: reportState.historyOpen ? 20 : 1,
      });
      if (requestSeqRef.current !== requestSeq) return;
      const list = Array.isArray(reports) ? reports.map(summarizeCharlotteReportForPanel) : [];
      const derived = deriveCharlotteReportState(list[0]);
      setReportState((current) => ({
        ...current,
        status: derived.status,
        report: derived.report,
        reports: list,
        error: derived.error,
      }));
      if (derived.status === 'generating') {
        const reportId = list[0]?.report_id || list[0]?.id;
        if (reportId) schedulePoll(sessionId, reportId, requestSeq);
      }
    } catch (err) {
      if (requestSeqRef.current !== requestSeq) return;
      setReportState((current) => ({
        ...current,
        status: 'error',
        error: err?.message || 'Could not refresh reports.',
      }));
    }
  }, [clearPollTimer, reportState.historyOpen, reportState.status, schedulePoll, sessionId]);

  const handleToggleHistory = useCallback(async () => {
    if (!sessionId) return;
    const nextOpen = !reportState.historyOpen;
    setReportState((current) => ({ ...current, historyOpen: nextOpen }));
    if (!nextOpen || reportState.reports.length > 1) return;
    const requestSeq = requestSeqRef.current + 1;
    requestSeqRef.current = requestSeq;
    try {
      const reports = await api.listCharlotteReports(sessionId, { limit: 20 });
      if (requestSeqRef.current !== requestSeq) return;
      const list = Array.isArray(reports) ? reports.map(summarizeCharlotteReportForPanel) : [];
      const derived = deriveCharlotteReportState(list[0], reportState.status);
      setReportState((current) => ({
        ...current,
        status: derived.report ? derived.status : current.status,
        report: derived.report || current.report,
        reports: list.length ? list : current.reports,
        error: derived.error,
      }));
      const reportId = list[0]?.report_id || list[0]?.id;
      if (derived.status === 'generating' && reportId) {
        schedulePoll(sessionId, reportId, requestSeq);
      }
    } catch (err) {
      if (requestSeqRef.current !== requestSeq) return;
      setReportState((current) => ({
        ...current,
        status: 'error',
        error: err?.message || 'Could not load report history.',
      }));
    }
  }, [reportState.historyOpen, reportState.reports.length, reportState.status, schedulePoll, sessionId]);

  const disabled = !sessionId || reportState.status === 'generating';
  const evidenceHref = getCharlotteEvidenceHref(sessionId, reportState.report);

  return (
    <div style={{ marginTop: 10, border: `1px solid ${V.border}`, borderRadius: 8, padding: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <div style={{ fontSize: 11, fontWeight: 650, color: V.text }}>Session evidence</div>
        <button
          type="button"
          disabled={!sessionId || reportState.status === 'generating' || reportState.status === 'loading'}
          onClick={handleRefreshReports}
          style={{
            border: 'none', background: 'transparent', color: V.textDim,
            fontSize: 10, cursor: !sessionId ? 'not-allowed' : 'pointer',
          }}
        >
          Refresh
        </button>
      </div>
      <button
        type="button"
        disabled={disabled}
        onClick={() => handleGenerateReport({ regenerate: false })}
        style={{
          width: '100%', padding: '6px 0', border: `1px solid ${V.border}`,
          borderRadius: 6, background: reportState.status === 'generating' ? V.bgHover : 'transparent',
          color: !sessionId ? V.textDim : V.text, fontSize: 11, fontWeight: 500,
          cursor: disabled ? 'not-allowed' : 'pointer', transition: 'all 0.15s',
          opacity: !sessionId ? 0.55 : 1,
        }}
        onMouseEnter={e => {
          if (disabled) return;
          e.currentTarget.style.borderColor = V.accent;
          e.currentTarget.style.color = V.accent;
        }}
        onMouseLeave={e => {
          e.currentTarget.style.borderColor = V.border;
          e.currentTarget.style.color = !sessionId ? V.textDim : V.text;
        }}
      >
        {reportState.status === 'generating' ? 'Generating evidence report...' : 'Generate safe evidence report'}
      </button>
      <div style={{ marginTop: 6, color: V.textDim, fontSize: 10, lineHeight: 1.4 }}>
        Collects trace IDs, timing, metrics/log/trace snapshots, Managed Agent debug links, and safe summaries without persisting raw prompt/result bodies by default.
      </div>
      {reportState.status === 'loading' && (
        <div style={{ marginTop: 6, color: V.textDim, fontSize: 11 }}>Loading latest report...</div>
      )}
      {reportState.status === 'error' && (
        <div style={{ marginTop: 6, color: V.red, fontSize: 11 }}>
          {reportState.error}
        </div>
      )}
      {reportState.status === 'ready' && evidenceHref && (
        <Link
          to={evidenceHref}
          style={{
            display: 'block', marginTop: 6, color: V.accent,
            fontSize: 11, fontWeight: 600, textDecoration: 'none',
          }}
        >
          Open session evidence
        </Link>
      )}
      {reportState.status === 'ready' && reportState.report && (
        <button
          type="button"
          disabled={disabled}
          onClick={() => handleGenerateReport({ regenerate: true })}
          style={{
            marginTop: 6, padding: 0, border: 'none', background: 'transparent',
            color: V.accent, fontSize: 11, fontWeight: 600,
            cursor: disabled ? 'not-allowed' : 'pointer',
          }}
        >
          Regenerate report
        </button>
      )}
      <button
        type="button"
        disabled={disabled}
        onClick={() => handleGenerateReport({ regenerate: true, includePrivateEvidence: true })}
        style={{
          marginTop: 6, marginLeft: reportState.status === 'ready' ? 10 : 0,
          padding: 0, border: 'none', background: 'transparent',
          color: V.textDim, fontSize: 11, fontWeight: 600,
          cursor: disabled ? 'not-allowed' : 'pointer',
        }}
        title="Persist private prompt/result snapshots and copied input-media previews for admin-only deep replay."
      >
        Generate private replay
      </button>
      {reportState.report && (
        <div style={{ marginTop: 6, color: V.textDim, fontSize: 10 }}>
          v{reportState.report.version || 1} · {reportState.report.environment || 'env unknown'} · {reportState.report.status}
        </div>
      )}
      {sessionId && (
        <button
          type="button"
          onClick={handleToggleHistory}
          style={{
            marginTop: 6, padding: 0, border: 'none', background: 'transparent',
            color: V.accent, fontSize: 11, fontWeight: 600, cursor: 'pointer',
          }}
        >
          {reportState.historyOpen ? 'Hide history' : 'Show history'}
        </button>
      )}
      {reportState.historyOpen && reportState.reports.length > 0 && (
        <div style={{ marginTop: 6, display: 'grid', gap: 4 }}>
          {reportState.reports.map((report) => {
            const url = getCharlotteReportUrl(report);
            return (
              <button
                type="button"
                key={report.report_id || report.id}
                onClick={() => {
                  const derived = deriveCharlotteReportState(report);
                  const reportId = report.report_id || report.id;
                  const requestSeq = requestSeqRef.current + 1;
                  requestSeqRef.current = requestSeq;
                  clearPollTimer();
                  setReportState((current) => ({
                    ...current,
                    report: derived.report,
                    status: derived.status,
                    error: derived.error,
                  }));
                  if (derived.status === 'generating' && reportId) {
                    schedulePoll(sessionId, reportId, requestSeq);
                  }
                }}
                style={{
                  textAlign: 'left', padding: '5px 6px', border: `1px solid ${V.border}`,
                  borderRadius: 6, background: report.report_id === reportState.report?.report_id ? V.bgHover : 'transparent',
                  color: url ? V.text : V.textDim, fontSize: 10, cursor: 'pointer',
                }}
              >
                v{report.version || 1} · {report.status} · {report.updated_at || report.created_at || 'time unknown'}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Evidence panel — an "Evidence" button on the session info bar that opens a
// popover (mirrors WidgetPanel) holding the full evidence-report tool. This is
// the SINGLE home for session evidence; the session-list column no longer
// duplicates it. CharlotteReportAction's own "Open session evidence" link is the
// in-panel "jump to the full report" button (the old #session-evidence jump).
function EvidencePanel({ sessionId }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);
  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
        title="Session evidence report"
        style={{
          border: `1px solid ${V.border}`, borderRadius: 4, padding: '3px 10px',
          color: open ? V.accent : V.text, background: open ? V.bgHover : V.bg,
          fontSize: 10, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
        }}
      >
        Evidence
      </button>
      {open && (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'absolute', top: 28, right: 0, zIndex: 30, width: 320,
            maxHeight: 460, overflowY: 'auto', background: V.bgCard,
            border: `1px solid ${V.border}`, borderRadius: 12, padding: '4px 10px 10px',
            boxShadow: '0 8px 28px rgba(0,0,0,0.18)',
          }}
        >
          <CharlotteReportAction sessionId={sessionId} />
        </div>
      )}
    </div>
  );
}

// ── Session Info Header — collapsible metadata bar above chat messages ──

function SessionInfoHeader({ session, fetchMode, onChangeFetchMode, onDelete, widgetPanel }) {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);

  const [shareUrl, setShareUrl] = useState(null);

  // Pre-fetch share URL so the click handler can write to the clipboard
  // synchronously. iOS Safari rejects navigator.clipboard.writeText when
  // it isn't in the same task as the user gesture — any `await` between
  // the click and the write breaks the chain and the write silently fails.
  useEffect(() => {
    const sid = session?.sessionId;
    if (!sid) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await api.request(`/api/users/sessions/${sid}/share`, { method: 'POST' });
        if (!cancelled) setShareUrl(`${window.location.origin}/playground/share/${data.token}`);
      } catch {
        const viUserId = api.getViUserId();
        if (viUserId && !cancelled) {
          setShareUrl(`${window.location.origin}/playground/share/${viUserId}/${sid}`);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [session?.sessionId]);

  const flashCopied = () => {
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShare = () => {
    if (copied) return;
    // Fast path — prefetched URL: stay synchronous so iOS Safari accepts
    // the clipboard write inside the user-gesture task.
    if (shareUrl) {
      if (copyWithFallback(shareUrl)) flashCopied();
      return;
    }
    // Lazy fallback — only reached if prefetch hasn't completed yet. The
    // async gap may cause iOS Safari to silently drop the clipboard write;
    // that's acceptable as a secondary path since the prefetch almost
    // always wins by the time the user taps Share.
    (async () => {
      try {
        const data = await api.request(`/api/users/sessions/${session.sessionId}/share`, { method: 'POST' });
        const url = `${window.location.origin}/playground/share/${data.token}`;
        setShareUrl(url);
        if (copyWithFallback(url)) flashCopied();
      } catch {
        const viUserId = api.getViUserId();
        if (viUserId) {
          const url = `${window.location.origin}/playground/share/${viUserId}/${session.sessionId}`;
          setShareUrl(url);
          if (copyWithFallback(url)) flashCopied();
        }
      }
    })();
  };

  const firstPrompt = session.tasks?.[0]?.prompt || '';
  const skillSlug = session.tasks?.[0]?.skillSlug;
  const status = session.status || 'unknown';
  const startedAt = session.latestTs ? new Date(session.latestTs).toLocaleString() : null;
  const taskCount = session.tasks?.length || 0;
  const hasTimeline = session.timeline?.length > 0;
  // Issue #1391: the session-level executor badge must reflect the LATEST
  // task's executor when a task is live or was the most recent one. The
  // `session.context.executor` returned by api-server is sticky-read from
  // Redis at session-FETCH time and lags real turns (e.g. a `#exec_agent`
  // hard bypass that flipped server-side executor mid-session). Derive
  // from live task state first; fall back to sticky when no task exists.
  const latestTask = (() => {
    const ts = session.tasks || [];
    if (ts.length === 0) return null;
    const running = ts.find(t => t.status === 'running' || t.status === 'queued' || t.status === 'loading');
    if (running) return running;
    return ts.reduce((a, b) => ((b._ts || 0) > (a._ts || 0) ? b : a));
  })();
  const effectiveExecutor = (latestTask?.executor) || session.context?.executor;
  const cardTemplates = new Set();
  if (session.result?.cards) {
    for (const card of Object.values(session.result.cards)) {
      if (card.template) cardTemplates.add(card.template);
    }
  }

  const statusColor = {
    completed: V.green, active: V.blue, failed: V.red,
    queued: V.yellow, created: V.textDim,
    stopped: '#ff9500', timeout: V.red,
  }[status] || V.textDim;

  const pill = (text, bg, color) => (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 6,
      fontSize: 10, fontWeight: 500, background: bg, color,
      whiteSpace: 'nowrap',
    }}>{text}</span>
  );

  return (
    <div style={{
      borderBottom: `1px solid ${V.border}`, background: V.bgCard,
      fontSize: 12, flexShrink: 0,
    }}>
      {/* Info row — line 1 (click to expand) */}
      <div className="pg-session-header-row" style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '6px 16px 2px', cursor: 'pointer',
      }} onClick={() => setExpanded(!expanded)}>
        <span style={{ fontSize: 10, color: V.textDim }}>{expanded ? '▼' : '▶'}</span>
        {pill(status, `${statusColor}20`, statusColor)}
        {/* LINE 1 — the session title (swapped up from the action row). */}
        <span
          title={session.title || firstPrompt || session.sessionId}
          style={{
            flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis',
            whiteSpace: 'nowrap', color: V.text, fontWeight: 600, fontSize: 12,
          }}
        >
          {session.title || firstPrompt || session.sessionId}
        </span>
        <span style={{ fontSize: 10, color: V.textDim }}>{taskCount} task{taskCount !== 1 ? 's' : ''}</span>
        {hasTimeline && <span style={{ fontSize: 10, color: V.textDim }}>{session.timeline.length} rounds</span>}
        {/* Model badge — always shown (even when the session ran on the default
            `auto`, which persists no context.model). On `auto` it names the model
            that scheme's binding resolves to (`gpt-5.4-mini · auto`) and falls
            back to the bare "Auto" when the binding isn't known. */}
        <span style={{
          display: 'inline-block', padding: '2px 8px', borderRadius: 6,
          fontSize: 10, fontWeight: 500, background: 'rgba(175,82,222,0.08)',
          color: V.purple, whiteSpace: 'nowrap',
        }}>
          {session.context?.model
            ? catalogModelLabel(session.context.model)
            : autoModelBadge(session.context?.render_scheme)}
        </span>
        {/* Render-scheme badge — sits next to the model badge. render_scheme is
            persisted on every session context, so this always renders. */}
        {schemeBadgeLabel(session.context?.render_scheme) && (
          <span style={{
            display: 'inline-block', padding: '2px 8px', borderRadius: 6,
            fontSize: 10, fontWeight: 500,
            background: session.context?.render_scheme === 'native-ir' ? 'rgba(52,199,89,0.12)' : 'rgba(0,122,255,0.10)',
            color: session.context?.render_scheme === 'native-ir' ? '#2f855a' : '#0a66c2',
            whiteSpace: 'nowrap',
          }} title={`Render scheme: ${session.context?.render_scheme}`}>
            {schemeBadgeLabel(session.context?.render_scheme)}
          </span>
        )}
        {/* Reasoning-effort badge — only when the session bound a real effort (a reasoning model). A
            non-reasoning session (none/absent) shows nothing. */}
        {reasoningBadgeLabel(session.context?.reasoning_effort) && (
          <span style={{
            display: 'inline-block', padding: '2px 8px', borderRadius: 6,
            fontSize: 10, fontWeight: 500, background: 'rgba(255,149,0,0.12)',
            color: '#b25000', whiteSpace: 'nowrap',
          }} title={`Reasoning effort: ${session.context?.reasoning_effort}`}>
            {reasoningBadgeLabel(session.context?.reasoning_effort)}
          </span>
        )}
        {/* Worker-backend badge — only when the session bound a non-default worker
            (managed / harnessrouter). A default-worker (auto/absent) session shows nothing. */}
        {workerBadgeLabel(session.context?.worker_backend) && (
          <span style={{
            display: 'inline-block', padding: '2px 8px', borderRadius: 6,
            fontSize: 10, fontWeight: 500, background: 'rgba(20,184,166,0.14)',
            color: '#0f766e', whiteSpace: 'nowrap',
          }} title={`Worker backend: ${session.context?.worker_backend}`}>
            {workerBadgeLabel(session.context?.worker_backend)}
          </span>
        )}
        {effectiveExecutor && (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 3, padding: '2px 8px', borderRadius: 6,
            fontSize: 10, fontWeight: 500, whiteSpace: 'nowrap',
            background: effectiveExecutor === 'fast' ? 'rgba(52,199,89,0.12)' : 'rgba(88,86,214,0.12)',
            color: effectiveExecutor === 'fast' ? '#2f855a' : '#5856d6',
          }} title={`Session executor: ${effectiveExecutor}${latestTask?.executor && latestTask.executor !== session.context?.executor ? ' (from live task)' : ''}`}>
            {effectiveExecutor === 'fast' ? '⚡ Fast' : '🔷 SDK'}
          </span>
        )}
        <ProcessingPill session={session} />
      </div>
      {/* Action row — second line: the session title (left) + the action
          buttons (right): widgets · evidence · share · delete · auto-dropdown. */}
      <div className="pg-session-header-actions" style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '0 16px 6px', flexWrap: 'wrap',
      }} onClick={() => setExpanded(!expanded)}>
        {/* LEFT — the widget-instance skill tag (swapped down from the info row).
            Truncates with an ellipsis so it shares ONE row with the buttons
            (the pill can shrink below its content width via minWidth:0); hover
            shows the full slug, and a click selects it whole for copying
            (stopPropagation so it doesn't toggle the header expand). A flex
            spacer when there's no skill tag keeps the buttons right-aligned. */}
        {skillSlug ? (
          <span
            title={formatSkillTag(skillSlug)}
            onClick={(e) => e.stopPropagation()}
            style={{
              flex: '0 1 auto', minWidth: 0, overflow: 'hidden',
              textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 500,
              background: 'rgba(175,82,222,0.12)', color: V.purple,
              cursor: 'text', userSelect: 'all',
            }}
          >
            {formatSkillTag(skillSlug)}
          </span>
        ) : (
          <span style={{ flex: 1 }} />
        )}

        {/* RIGHT — action buttons, pushed to the right edge */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto' }} onClick={e => e.stopPropagation()}>
          {widgetPanel && (
            <span onClick={e => e.stopPropagation()} style={{ display: 'inline-flex' }}>
              <WidgetPanel
                suggest={widgetPanel.suggest}
                attention={widgetPanel.attention}
                loading={widgetPanel.loading}
                onSeen={widgetPanel.onSeen}
                onOpen={widgetPanel.onOpen}
                onAction={widgetPanel.onAction}
                onJumpToCard={widgetPanel.onJumpToCard}
              />
            </span>
          )}
          {session.sessionId && (
            <span onClick={e => e.stopPropagation()} style={{ display: 'inline-flex' }}>
              <EvidencePanel sessionId={session.sessionId} />
            </span>
          )}
          {session.sessionId && (
            <button
              onClick={e => { e.stopPropagation(); handleShare(); }}
              style={{
                background: copied ? V.green : V.accent, color: '#fff', border: 'none',
                borderRadius: 4, padding: '3px 10px', fontSize: 10, cursor: 'pointer',
                fontWeight: 600, transition: 'background 0.2s',
              }}
            >
              {copied ? 'Copied!' : 'Share'}
            </button>
          )}
          {session.sessionId && onDelete && (
            <button
              onClick={e => { e.stopPropagation(); onDelete(session.sessionId); }}
              title="Delete this session"
              style={{
                background: 'transparent', color: '#d11', border: '1px solid rgba(221,17,17,0.4)',
                borderRadius: 4, padding: '3px 10px', fontSize: 10, cursor: 'pointer', fontWeight: 600,
              }}
            >
              Delete
            </button>
          )}
          {onChangeFetchMode && (
            <FetchModeSelector
              mode={fetchMode || 'auto'}
              onChange={onChangeFetchMode}
            />
          )}
        </div>
      </div>
      {/* Expanded details */}
      {expanded && (
        <div style={{
          padding: '0 16px 10px', display: 'grid',
          gridTemplateColumns: '1fr 1fr', gap: '4px 24px',
          fontSize: 11, color: V.textDim, lineHeight: 1.8,
        }}>
          <div><b style={{ color: V.text }}>Session ID</b></div>
          <div style={{ fontFamily: V.font, fontSize: 10, wordBreak: 'break-all' }}>{session.sessionId}</div>
          {api.getViUserId() && <>
            <div><b style={{ color: V.text }}>VI User ID</b></div>
            <div style={{ fontFamily: V.font, fontSize: 10 }}>{api.getViUserId()}</div>
          </>}
          {startedAt && <>
            <div><b style={{ color: V.text }}>Started</b></div>
            <div>{startedAt}</div>
          </>}
          {skillSlug && <>
            <div><b style={{ color: V.text }}>Executor</b></div>
            <div>{skillSlug}</div>
          </>}
          {cardTemplates.size > 0 && <>
            <div><b style={{ color: V.text }}>Card Templates</b></div>
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {[...cardTemplates].map(t => (
                <span key={t} style={{
                  background: V.bgHover, padding: '1px 6px', borderRadius: 4,
                  fontSize: 10, fontFamily: V.font,
                }}>{t}</span>
              ))}
            </div>
          </>}
          {session.artifacts?.length > 0 && <>
            <div><b style={{ color: V.text }}>Artifacts</b></div>
            <div>{session.artifacts.length} file{session.artifacts.length !== 1 ? 's' : ''}</div>
          </>}
          {shareUrl && <>
            <div><b style={{ color: V.text }}>Share URL</b></div>
            <div style={{
              fontFamily: V.font, fontSize: 10, wordBreak: 'break-all',
              cursor: 'pointer', color: V.accent,
            }} onClick={handleShare} title="Click to copy">{shareUrl}</div>
          </>}
        </div>
      )}
    </div>
  );
}

// ── Unified Timeline: merges historical tasks + live blocks into one sorted list ──

// Per-user-turn inline action (Copy / Rewind) — mirrors the muted, borderless
// look of the other small text buttons in the timeline.
const REWIND_ACTION_BTN = {
  background: 'transparent', border: 'none', padding: 0,
  color: V.textDim, fontSize: 11, cursor: 'pointer',
  fontFamily: V.fontUi, opacity: 0.75, lineHeight: 1.2,
};

export function UnifiedTimeline({
  sortedTasks, allCards, cardTaskMap, liveBlocks, sending,
  // Rewind (#session-rewind): `rewindCut` = `{ key, rid, tappedAt }` — a LOCAL,
  // not-yet-durable cut. `onRewind`/`onCopy` are the per-user-turn actions.
  rewindCut, onRewind, onCopy,
}) {
  const [perCardRaw, setPerCardRaw] = useState({});
  const [copiedKey, setCopiedKey] = useState(null);
  const copiedTimerRef = useRef(null);
  useEffect(() => () => { if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current); }, []);
  const toggleCardRaw = useCallback((cardId) => {
    setPerCardRaw(prev => ({ ...prev, [cardId]: !prev[cardId] }));
  }, []);
  const handleCopy = useCallback((msg) => {
    // Synchronous — the clipboard write must stay inside the click's task.
    if (onCopy?.(msg) === false) return;
    setCopiedKey(msg.key);
    if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
    copiedTimerRef.current = setTimeout(() => setCopiedKey(null), 1500);
  }, [onCopy]);
  // Flat list, sorted by ts. Dedup cards by cardId, prompts by text.
  const messages = useMemo(() => {
    const items = [];
    const seenCardIds = new Set();

    // Collect live prompt texts for dedup against historical
    const livePromptTexts = new Set();
    for (const b of liveBlocks) {
      if (b.type === 'user' && b.text) livePromptTexts.add(b.text);
    }

    // Historical: user prompts + cards + text results
    for (const task of sortedTasks) {
      // Skip historical prompt if live already has it (prevents duplicate during active chat)
      if (task.prompt && !livePromptTexts.has(task.prompt)) {
        items.push({
          key: `prompt-${task.taskId}`,
          type: 'user',
          text: task.prompt,
          mediaUrls: task.mediaUrls || [],
          // Server round id — the value `rewind_to_rid` carries on re-send.
          // null for live/legacy turns (rewind then stays client-local).
          rid: task.rid || null,
          ts: task.ts || 0,
        });
      }
      for (const [id, card] of Object.entries(task.cards || {})) {
        if (cardTaskMap[id] !== task) continue;
        seenCardIds.add(id);
        // debug-html cards are partitioned out into the DebugPanel below — skip here.
        if (card.template === 'debug-html') continue;
        items.push({
          key: `card-${id}`,
          type: 'card',
          card: { cardId: id, ...card },
          ts: card.ts || task.ts || 0,
        });
      }
      if (task.result && !hasContentCards(task.cards)) {
        items.push({
          key: `result-${task.taskId}`,
          type: 'text-result',
          text: task.result,
          ts: (task.ts || 0) + 1,
        });
      }
    }

    // Live: user inputs + cards + errors — skip cards already in historical
    for (let i = 0; i < liveBlocks.length; i++) {
      const b = liveBlocks[i];
      if (b.type === 'card' && b.card?.cardId && seenCardIds.has(b.card.cardId)) continue;
      // Live debug-html cards also partitioned out into DebugPanel.
      if (b.type === 'card' && b.card?.template === 'debug-html') continue;
      items.push({
        key: `live-${b.type}-${b.type === 'card' ? b.card?.cardId : i}`,
        ...b,
        ts: b.ts || 0,
      });
    }

    // Sort by ts ascending; ties (cards born in the same stream delta share a
    // ts) break on the server-persisted creation `seq` — JSONB key order is
    // noise. Non-card blocks have no seq and keep their relative order.
    items.sort((a, b) =>
      ((a.ts || 0) - (b.ts || 0))
      || ((a.card?.seq ?? a.seq ?? 1e9) - (b.card?.seq ?? b.seq ?? 1e9)));

    // Rewind cut — applied HERE, inside the memo, so it survives the wholesale
    // `sessionTasks` re-derivation (the historical half of this list is rebuilt
    // from scratch every time the session detail refreshes; a cut applied to
    // `sessionTasks` itself would be undone). Keyed on the turn's STABLE key
    // (`prompt-<sessionId>-round-N`), never on `ts` — the prompt ts is
    // synthesized (`request_ts || earliestCardTs - 1`) and can tie across rounds.
    // Items born AFTER the rewind tap survive the cut: the re-sent turn's own
    // live bubble and its cards must render below the truncation point.
    if (rewindCut?.key) {
      const at = items.findIndex(m => m.key === rewindCut.key);
      if (at >= 0) {
        const kept = items.slice(0, at);
        for (let i = at; i < items.length; i++) {
          if ((items[i].ts || 0) > (rewindCut.tappedAt || 0)) kept.push(items[i]);
        }
        return kept;
      }
    }
    return items;
  }, [sortedTasks, allCards, cardTaskMap, liveBlocks, rewindCut]);

  // Collect debug-html cards (historical + live) for the bottom DebugPanel.
  // Sorted by ts so the panel reads chronologically.
  const debugCards = useMemo(() => {
    const out = [];
    const seen = new Set();
    for (const task of sortedTasks) {
      for (const [id, card] of Object.entries(task.cards || {})) {
        if (cardTaskMap[id] !== task) continue;
        if (card.template !== 'debug-html') continue;
        seen.add(id);
        out.push({ cardId: id, ...card, ts: card.ts || task.ts || 0 });
      }
    }
    for (const b of liveBlocks) {
      if (b.type !== 'card' || !b.card) continue;
      if (b.card.template !== 'debug-html') continue;
      if (b.card.cardId && seen.has(b.card.cardId)) continue;
      out.push({ ...b.card, ts: b.ts || 0 });
    }
    out.sort((a, b) => (a.ts || 0) - (b.ts || 0));
    return out;
  }, [sortedTasks, cardTaskMap, liveBlocks]);

  return (
    <>
      {messages.map(msg => {
        if (msg.type === 'user') {
          // The playground is DEVELOPER-FACING — show the prompt VERBATIM, including any
          // hidden <vi_prefix>/<vi_suffix> instruction blocks (the app hides them from the
          // end user; the playground must not, or a fully-wrapped prompt looks like an image
          // with no prompt). Only a truly empty message with no media renders no bubble.
          const visibleText = msg.text || '';
          if (!visibleText && !(msg.mediaUrls && msg.mediaUrls.length)) return null;
          return (
          <div key={msg.key} style={{ marginBottom: 16, maxWidth: '80%', marginLeft: 'auto' }}>
            <div style={{
              padding: '12px 16px', borderRadius: 12, fontSize: 14,
              lineHeight: 1.5, wordBreak: 'break-word',
              background: V.accent, color: '#fff',
              borderBottomRightRadius: 4,
            }}>
              {msg.mediaUrls?.length > 0 && (
                <div style={{ marginBottom: 8 }}>
                  {msg.mediaUrls.map((url, j) => {
                    const safeUrl = normalizeMediaUrl(url);
                    return _isVideoUrl(safeUrl) ? (
                      <video key={j} src={safeUrl} controls playsInline preload="metadata" style={{
                        maxWidth: 200, maxHeight: 150, borderRadius: 8, margin: 2,
                      }} />
                    ) : (
                      <img key={j} src={safeUrl} alt="" style={{
                        maxWidth: 200, maxHeight: 150, borderRadius: 8, margin: 2,
                      }} onError={e => { e.target.style.display = 'none'; }} />
                    );
                  })}
                </div>
              )}
              {visibleText}
            </div>
            <div style={{ fontSize: 11, color: V.textDim, marginTop: 4, padding: '0 4px', textAlign: 'right' }}>
              {msg.skillSlug && (
                <span style={{
                  background: 'rgba(175,82,222,0.12)', color: V.purple,
                  padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 500,
                  marginRight: 6,
                }}>
                  {formatSkillTag(msg.skillSlug)}
                </span>
              )}
              {formatDateTime(msg.ts)}
            </div>
            {/* Per-turn actions (#session-rewind). Subtle inline text buttons,
                not CTAs — they sit under the bubble like the timestamp. */}
            {(onCopy || onRewind) && (
              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 3, padding: '0 4px' }}>
                {onCopy && (
                  <button
                    onClick={() => handleCopy(msg)}
                    title="Copy this message"
                    style={REWIND_ACTION_BTN}
                  >{copiedKey === msg.key ? 'Copied' : 'Copy message'}</button>
                )}
                {onRewind && (
                  <button
                    onClick={() => onRewind(msg)}
                    title="Clear this turn and everything below it, and put it back in the composer to edit and re-send"
                    style={REWIND_ACTION_BTN}
                  >Rewind to here</button>
                )}
              </div>
            )}
          </div>
        );
        }

        if (msg.type === 'card') {
          const card = msg.card;
          const isThinkingCard = card.template === 'thinking' || card.template === 'thinking-process';
          const isLive = msg.key.startsWith('live-');
          const cardTime = (msg.ts || card.ts || card.createdAt) ? formatDateTime(msg.ts || card.ts || card.createdAt) : '';

          // Thinking cards + live cards — render with LiveCardBlock so every
          // card carries the executor ProducerBadge + </> toggle (developer
          // facing). Thinking cards previously rendered as bare CardRenderer
          // without a title bar — that's why they appeared without a badge.
          if (isThinkingCard || isLive) {
            return (
              <div key={msg.key} style={{ marginBottom: 16, maxWidth: '80%', marginRight: 'auto' }}>
                <LiveCardBlock card={card} />
                {cardTime && <div style={{ fontSize: 11, color: V.textDim, marginTop: 4, padding: '0 4px' }}>{cardTime}</div>}
              </div>
            );
          }

          // Historical content cards — full header with status badge
          const statusBadge = card.status === 'finalized'
            ? { bg: 'rgba(52,199,89,0.12)', color: V.green, text: 'done' }
            : { bg: 'rgba(255,149,0,0.12)', color: V.yellow, text: 'streaming' };
          const showRaw = !!perCardRaw[card.cardId];

          return (
            <div key={msg.key} style={{ marginBottom: 16, maxWidth: '80%', marginRight: 'auto' }}>
              <div style={{
                background: V.bg, border: `1px solid ${V.border}`,
                borderRadius: 8, overflow: 'hidden', marginTop: 6,
              }}>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '8px 12px', fontSize: 11, fontWeight: 600,
                  textTransform: 'uppercase', letterSpacing: '0.04em',
                  color: V.textDim, borderBottom: `1px solid ${V.border}`,
                  background: V.bg,
                }}>
                  {(() => {
                    // Native-IR card → its REAL ViewIR component name (stat_grid,
                    // hero_media, …), linking into the Component Bootstrap.
                    const nodeType = card.data?.node?.type;
                    if (card.template === 'session_block' && nodeType) {
                      return (
                        <Link
                          to={bootstrapHref(nodeType)}
                          title={`Open ${nodeType} in the Component Bootstrap`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: 'inherit', textDecoration: 'underline', textUnderlineOffset: 2 }}
                        >
                          {nodeType}
                        </Link>
                      );
                    }
                    return card.template || 'card';
                  })()}
                  <span style={{ fontWeight: 400, fontSize: 10, marginLeft: 'auto' }}>
                    {cardTime}
                  </span>
                  <ProducerBadge producerModel={card.producerModel} producer={card.producer} />
                  {card.template !== 'ask-user-question' && (
                    <span style={{
                      background: statusBadge.bg, color: statusBadge.color,
                      padding: '2px 8px', borderRadius: 6, fontSize: 10,
                      fontWeight: 500,
                    }}>
                      {statusBadge.text}
                    </span>
                  )}
                  <RawToggleButton
                    active={showRaw}
                    onClick={() => toggleCardRaw(card.cardId)}
                  />
                </div>
                <div style={cardBodyFrameStyle(card, showRaw)}>
                  {showRaw ? <RawDataView card={card} /> : <CardRenderer card={card} />}
                </div>
              </div>
            </div>
          );
        }

        if (msg.type === 'text-result') return (
          <div key={msg.key} style={{ marginBottom: 16, maxWidth: '80%', marginRight: 'auto' }}>
            <div style={{
              padding: '12px 16px', borderRadius: 12, fontSize: 14,
              lineHeight: 1.5, wordBreak: 'break-word',
              background: V.bgCard, border: `1px solid ${V.border}`,
              borderBottomLeftRadius: 4, color: V.text,
              whiteSpace: 'pre-wrap',
            }}>
              {msg.text}
            </div>
            <div style={{ fontSize: 11, color: V.textDim, marginTop: 4, padding: '0 4px' }}>
              {formatDuration(msg.durationMs)}
            </div>
          </div>
        );

        if (msg.type === 'error') return (
          <div key={msg.key} style={{ marginBottom: 16, maxWidth: '80%', marginRight: 'auto' }}>
            <div style={{ padding: '8px 12px', borderRadius: 8, background: 'rgba(255,59,48,0.1)', color: V.red, fontSize: 13 }}>
              Error: {msg.text}
            </div>
          </div>
        );

        return null;
      })}

      {/* Thinking indicator */}
      {sending && liveBlocks.filter(b => b.type === 'card').length === 0 && (
        <div style={{ marginBottom: 16, maxWidth: '80%', marginRight: 'auto' }}>
          <div style={{ padding: '10px 16px', borderRadius: 12, background: V.bgCard, border: `1px solid ${V.border}`, borderBottomLeftRadius: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 16, animation: 'pulse 1.5s infinite' }}>🧠</span>
            <span style={{ fontSize: 13, color: V.textDim }}>Thinking...</span>
          </div>
        </div>
      )}

      {/* Debug panel — agent-speak tool I/O cards, collapsed by default (Issue #1007) */}
      <DebugPanel cards={debugCards} />
    </>
  );
}

// ── PromptInspector — expandable debug view of the assembled Gemini prompt ──

function PromptInspector() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchPrompt = useCallback(async () => {
    setLoading(true);
    try {
      const result = await api.getBriefPrompt();
      setData(result);
    } catch (err) {
      setData({ error: err.message });
    } finally {
      setLoading(false);
    }
  }, []);

  const handleToggle = useCallback(async () => {
    const next = !open;
    setOpen(next);
    if (next) {
      // Always re-fetch on expand
      await fetchPrompt();
    }
  }, [open, fetchPrompt]);

  return (
    <div style={{ marginTop: 8 }}>
      <button
        onClick={handleToggle}
        style={{
          background: 'none', border: 'none', padding: 0,
          fontSize: 12, color: V.textDim, cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 4,
        }}
      >
        <span style={{ fontSize: 10, transition: 'transform 0.15s', transform: open ? 'rotate(90deg)' : 'none' }}>
          {'\u25B6'}
        </span>
        Prompt
        {data?.package_count != null && (
          <span style={{ color: V.accent, fontFamily: V.font }}>({data.package_count} packages)</span>
        )}
      </button>
      {open && (
        <div style={{
          marginTop: 8, padding: '12px 14px', borderRadius: 10,
          background: V.bgCard, border: `1px solid ${V.border}`,
          fontSize: 11, fontFamily: V.font, lineHeight: 1.6,
          maxHeight: 400, overflowY: 'auto',
        }}>
          {loading && <div style={{ color: V.textDim }}>Loading...</div>}
          {data?.error && <div style={{ color: V.red }}>{data.error}</div>}
          {data && !data.error && (
            <>
              <div style={{ marginBottom: 10 }}>
                <span style={{ fontWeight: 700, color: V.accent }}>Model: </span>
                <span style={{ color: V.text }}>{data.model}</span>
              </div>
              <div style={{ marginBottom: 10 }}>
                <span style={{ fontWeight: 700, color: V.accent }}>System Prompt:</span>
                <pre style={{
                  margin: '4px 0 0', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                  color: V.text, fontSize: 10, padding: 8, borderRadius: 6,
                  background: V.bg,
                }}>
                  {data.system_prompt}
                </pre>
              </div>
              <div style={{ marginBottom: 10 }}>
                <span style={{ fontWeight: 700, color: V.accent }}>Context Text:</span>
                <pre style={{
                  margin: '4px 0 0', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                  color: V.text, fontSize: 10, padding: 8, borderRadius: 6,
                  background: V.bg,
                }}>
                  {data.context_text}
                </pre>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// INTENTION PAGE — image upload → streaming brief → intention cards → start session
// ══════════════════════════════════════════════════════════════════════

// mapBookToOptionCards moved to ./playgroundBookCards (react-refresh: this file
// must export only its component). Web has no ML Kit, so it shows EVERY option.

function IntentionPage() {
  const navigate = useNavigate();
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [intentions, setIntentions] = useState(null);
  const [mediaContext, setMediaContext] = useState(null); // Issue #1002: per-image descriptions from brief
  const [error, setError] = useState(null);
  const [dispatching, setDispatching] = useState(null);
  // Issue #1015: model selector — shared between intention-button dispatch
  // and the free-text ChatInputBar dispatch below the intention cards.
  // The model is persisted onto the new session's `context.model` via /exec
  // so the chat-page top bar can display it.
  // Issue #1078: persist last-selected model in localStorage
  const [selectedModel, setSelectedModel] = useState(() => {
    try { return localStorage.getItem('playground-model') || DEFAULT_MODEL; } catch { return DEFAULT_MODEL; }
  });
  // Render-scheme picker — mirrors selectedModel; persisted so it carries across sessions.
  const [selectedScheme, setSelectedScheme] = useState(() => {
    try { return localStorage.getItem('playground-render-scheme') || DEFAULT_RENDER_SCHEME; } catch { return DEFAULT_RENDER_SCHEME; }
  });
  // Reasoning-effort picker — PER-TURN (re-sent every message), persisted like the two above.
  const [selectedEffort, setSelectedEffort] = useState(() => {
    try { return localStorage.getItem('playground-reasoning-effort') || DEFAULT_REASONING_EFFORT; } catch { return DEFAULT_REASONING_EFFORT; }
  });
  // Worker-backend picker — SESSION-BOUND (mirrors model/scheme), persisted like the above.
  const [selectedWorker, setSelectedWorker] = useState(() => {
    try { return localStorage.getItem('playground-worker-backend') || DEFAULT_WORKER_BACKEND; } catch { return DEFAULT_WORKER_BACKEND; }
  });
  // Issue #1339: opt-out of the content-hash upload cache (issue #999) so
  // re-uploading the same file actually exercises the presign → GCS PUT
  // path instead of returning the stored URL. Default ON because repeat
  // uploads of the same image are the common case; unchecking is a dev
  // affordance for testing the upload flow. Persisted so the user doesn't
  // re-check it every session.
  const [useUploadCache, setUseUploadCache] = useState(() => {
    try { return localStorage.getItem('playground-intention-use-upload-cache') !== '0'; } catch { return true; }
  });
  const toggleUseUploadCache = useCallback((next) => {
    setUseUploadCache(next);
    try { localStorage.setItem('playground-intention-use-upload-cache', next ? '1' : '0'); } catch { /* storage disabled */ }
  }, []);
  const fileRef = useRef(null);
  const abortRef = useRef(null);
  const dropRef = useRef(null);
  const clientFlowRef = useRef(null);
  const clientFlowMetaRef = useRef({});

  // Timing state
  const [timings, setTimings] = useState(null); // { start, upload, firstToken, summaryDone, intentions, complete }
  const [elapsed, setElapsed] = useState(0);
  // Issue #1015: correlation id for /profile <session_id> — shown in the
  // timing bar AND reused as the actual DB session_id when the user clicks
  // an intention. Same id end-to-end (Loki, Langfuse, Redis quick_response,
  // /api/users/exec session_id).
  const [briefSessionId, setBriefSessionId] = useState(null);
  const [briefSessionIdCopied, setBriefSessionIdCopied] = useState(false);

  // Robust copy with execCommand fallback (some browser contexts block
  // navigator.clipboard silently). Sets short "Copied!" feedback.
  const copyBriefSessionId = useCallback(() => {
    if (!briefSessionId) return;
    let ok = false;
    try {
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(briefSessionId);
        ok = true;
      }
    } catch (_) { /* fall through */ }
    if (!ok) {
      try {
        const ta = document.createElement('textarea');
        ta.value = briefSessionId;
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        ok = true;
      } catch (_) { /* give up */ }
    }
    if (ok) {
      setBriefSessionIdCopied(true);
      setTimeout(() => setBriefSessionIdCopied(false), 1200);
    }
  }, [briefSessionId]);
  const timerRef = useRef(null);

  // Live elapsed timer — ticks every 100ms while streaming
  useEffect(() => {
    if (streaming && timings?.start) {
      timerRef.current = setInterval(() => setElapsed(Date.now() - timings.start), 100);
    } else {
      clearInterval(timerRef.current);
      if (timings?.complete) setElapsed(timings.complete);
    }
    return () => clearInterval(timerRef.current);
  }, [streaming, timings?.start, timings?.complete]);

  // Cleanup abort on unmount
  useEffect(() => () => { abortRef.current?.(); clearInterval(timerRef.current); }, []);

  const handleFiles = useCallback(async (files) => {
    setUploading(true);
    setError(null);
    const uploadStart = Date.now();
    const clientFlowId = clientFlowRef.current || newClientFlowId();
    clientFlowRef.current = clientFlowId;
    const clientUploadId = newClientFlowId('up');
    clientFlowMetaRef.current = {
      ...(clientFlowMetaRef.current || {}),
      client_flow_id: clientFlowId,
      client_upload_id: clientUploadId,
      upload_start_ms: uploadStart,
      media_type: files.some(file => file.type.startsWith('video/')) ? 'video' : 'image',
    };
    api.sendClientE2EBeacon({
      event: 'client_upload_started',
      source: 'web',
      surface: 'web',
      route: 'upload',
      media_type: files.some(file => file.type.startsWith('video/')) ? 'video' : 'image',
      client_flow_id: clientFlowId,
      client_upload_id: clientUploadId,
    });
    try {
      // Parallel upload — all files concurrently. Track per-file content-hash
      // cache hits so the timing badge can say "(cached)" when nothing
      // actually hit the network.
      let cacheHits = 0;
      const skipCache = !useUploadCache;
      const uploadPromises = files.map(async (file) => {
        const isVideo = file.type.startsWith('video/');
        const onCached = () => { cacheHits += 1; };
        let gcsUrl;
        if (isVideo) {
          const ext = file.name.match(/\.(mp4|mov|webm)$/i)?.[1]?.toLowerCase() || 'mp4';
          gcsUrl = await api.uploadBlob(file, ext, { onCached, skipCache });
        } else {
          const dataUrl = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (ev) => resolve(ev.target.result);
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });
          gcsUrl = await api.uploadDataUrl(dataUrl, { onCached, skipCache });
        }
        // Issue #1411 verification: confirm intention-flow uploads return
        // proxy URLs (expect ``/api/media/...``, not ``storage.googleapis.com``).
        console.log('[#1411 upload][intention]', { name: file.name, isVideo, url: gcsUrl });
        return { url: gcsUrl, name: file.name, isVideo };
      });
      const results = await Promise.all(uploadPromises);
      setImages(prev => [...prev, ...results.filter(Boolean)]);
      setTimings(prev => ({
        ...prev,
        upload: Date.now() - uploadStart,
        uploadCacheHits: cacheHits,
        uploadCount: files.length,
      }));
      clientFlowMetaRef.current = {
        ...(clientFlowMetaRef.current || {}),
        upload_complete_ms: Date.now(),
        upload_duration_ms: Date.now() - uploadStart,
        media_count: results.length,
      };
      api.sendClientE2EBeacon({
        event: 'client_upload_complete',
        outcome: 'success',
        stage: 'upload',
        source: 'web',
        surface: 'web',
        route: 'upload',
        media_type: results.some(item => item?.isVideo) ? 'video' : 'image',
        duration_ms: Date.now() - uploadStart,
        upload_duration_ms: Date.now() - uploadStart,
        client_flow_id: clientFlowId,
        client_upload_id: clientUploadId,
        status: cacheHits === files.length ? 'cached' : 'uploaded',
      });
    } catch (err) {
      api.sendClientE2EBeacon({
        event: 'client_upload_complete',
        outcome: 'error',
        stage: 'upload',
        source: 'web',
        surface: 'web',
        route: 'upload',
        media_type: files.some(file => file.type.startsWith('video/')) ? 'video' : 'image',
        duration_ms: Date.now() - uploadStart,
        upload_duration_ms: Date.now() - uploadStart,
        client_flow_id: clientFlowId,
        client_upload_id: clientUploadId,
        status: err?.message || 'upload_failed',
      });
      setError(`Upload failed: ${err.message}`);
    } finally {
      setUploading(false);
    }
  }, [useUploadCache]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    const files = Array.from(e.dataTransfer?.files || []).filter(f => f.type.startsWith('image/') || f.type.startsWith('video/'));
    if (files.length) handleFiles(files);
  }, [handleFiles]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleSend = useCallback(() => {
    if (images.length === 0) return;
    const t0 = Date.now();
    const clientFlowId = clientFlowRef.current || newClientFlowId();
    clientFlowRef.current = clientFlowId;
    clientFlowMetaRef.current = {
      ...(clientFlowMetaRef.current || {}),
      client_flow_id: clientFlowId,
      brief_send_ms: t0,
    };
    setStreaming(true);
    setTitle('');
    setSummary('');
    setIntentions(null);
    setMediaContext(null);
    setError(null);
    setBriefSessionId(null);
    setTimings(prev => ({ upload: prev?.upload, start: t0 }));
    setElapsed(0);

    let gotFirstToken = false;
    let streamSessionId = null;
    const imageUrls = images.map(i => i.url);
    api.sendClientE2EBeacon({
      event: 'client_send_pressed',
      stage: 'sse_first_byte',
      source: 'web',
      surface: 'web',
      route: 'session_stream',
      media_type: imageUrls.length ? 'image' : 'unknown',
      client_flow_id: clientFlowId,
    });

    // ── INTENT_MLKIT_LABEL_RULES (mirror the app's production capture flow) ──
    // The web has no ML Kit, so instead of the cloud VLM's 3 generated
    // intentions we fetch the admin option book (single source of truth) and
    // show ALL options. In parallel we stream title+summary from
    // /brief/summary/stream — the SAME endpoint the app uses, which ALSO
    // pre-binds the Managed Agent session server-side. So while the user reads
    // the options, sessions.create runs off the hot path and the option-tap
    // /exec reuses the prebound session. This makes the playground a faithful
    // turnover-profiling proxy of the shipping flow.
    if (IS_LABEL_RULES) {
      api.getIntentionRules('INTENT_MLKIT_LABEL_RULES')
        .then((book) => {
          setTimings(prev => ({ ...prev, intentions: Date.now() - t0 }));
          setIntentions(mapBookToOptionCards(book));
        })
        .catch((err) => setError(`Failed to load options: ${err.message}`));
      const abortSummary = api.streamSessionBrief(imageUrls, null, {
        onSessionId: (id) => {
          streamSessionId = id;
          setBriefSessionId(id);
          writePlaygroundClientFlow(id, {
            ...(clientFlowMetaRef.current || {}),
            client_flow_id: clientFlowId,
            brief_session_id: id,
            sessionId: id,
          });
          api.sendClientE2EBeacon({
            event: 'client_sse_connected',
            stage: 'sse_first_byte', source: 'web', surface: 'web',
            route: 'session_stream',
            media_type: imageUrls.length ? 'image' : 'unknown',
            sessionId: id, client_flow_id: clientFlowId,
          });
        },
        onTitleToken: (token) => {
          if (!gotFirstToken) {
            gotFirstToken = true;
            setTimings(prev => ({ ...prev, firstToken: Date.now() - t0 }));
            api.sendClientE2EBeacon({
              event: 'client_first_token_received',
              stage: 'sse_first_byte', source: 'web', surface: 'web',
              route: 'session_stream',
              media_type: imageUrls.length ? 'image' : 'unknown',
              duration_ms: Date.now() - t0, sse_first_byte_ms: Date.now() - t0,
              sessionId: streamSessionId || undefined, client_flow_id: clientFlowId,
              eventType: 'brief_title_token',
            });
          }
          setTitle(prev => prev + token);
        },
        onSummaryToken: (token) => {
          if (!gotFirstToken) {
            gotFirstToken = true;
            setTimings(prev => ({ ...prev, firstToken: Date.now() - t0 }));
          }
          setSummary(prev => prev + token);
        },
        onComplete: () => {
          setTimings(prev => ({ ...prev, complete: Date.now() - t0 }));
          api.sendClientE2EBeacon({
            event: 'completed', stage: 'total', source: 'web', surface: 'web',
            route: 'session_stream',
            media_type: imageUrls.length ? 'image' : 'unknown',
            duration_ms: Date.now() - t0, sessionId: streamSessionId || undefined,
            client_flow_id: clientFlowId,
          });
          setStreaming(false);
        },
        // The summary brief (title/summary) is DECORATIVE under LABEL_RULES: the
        // actionable options come from the rules book (loaded independently) and
        // the prebind already fired server-side, so a summary-stream hiccup must
        // NOT block the flow or alarm the user — log it and move on. (This also
        // swallows a dev-only vConsole network-hook artifact that rejects the
        // SSE reader when the parallel rules fetch overlaps the stream.)
        onError: (err) => {
          console.warn('[intention] summary brief stream error (non-fatal):', err);
          setTimings(prev => ({ ...prev, complete: Date.now() - t0 }));
          setStreaming(false);
        },
      }, { path: '/api/v1/session/brief/summary/stream' });
      abortRef.current = abortSummary;
      return;
    }

    const abort = api.streamSessionBrief(imageUrls, null, {
      onSessionId: (id) => {
        streamSessionId = id;
        setBriefSessionId(id);
        writePlaygroundClientFlow(id, {
          ...(clientFlowMetaRef.current || {}),
          client_flow_id: clientFlowId,
          brief_session_id: id,
          sessionId: id,
        });
        api.sendClientE2EBeacon({
          event: 'client_sse_connected',
          stage: 'sse_first_byte',
          source: 'web',
          surface: 'web',
          route: 'session_stream',
          media_type: imageUrls.length ? 'image' : 'unknown',
          sessionId: id,
          client_flow_id: clientFlowId,
        });
      },
      onTitleToken: (token) => {
        if (!gotFirstToken) {
          gotFirstToken = true;
          const firstTokenMs = Date.now();
          clientFlowMetaRef.current = {
            ...(clientFlowMetaRef.current || {}),
            brief_first_token_ms: firstTokenMs,
          };
          if (streamSessionId) {
            writePlaygroundClientFlow(streamSessionId, {
              ...(clientFlowMetaRef.current || {}),
              client_flow_id: clientFlowId,
              brief_session_id: streamSessionId,
              sessionId: streamSessionId,
            });
          }
          setTimings(prev => ({ ...prev, firstToken: firstTokenMs - t0 }));
          api.sendClientE2EBeacon({
            event: 'client_first_token_received',
            stage: 'sse_first_byte',
            source: 'web',
            surface: 'web',
            route: 'session_stream',
            media_type: imageUrls.length ? 'image' : 'unknown',
            duration_ms: Date.now() - t0,
            sse_first_byte_ms: Date.now() - t0,
            sessionId: streamSessionId || undefined,
            client_flow_id: clientFlowId,
            eventType: 'brief_title_token',
          });
        }
        setTitle(prev => prev + token);
      },
      onSummaryToken: (token) => {
        if (!gotFirstToken) {
          gotFirstToken = true;
          const firstTokenMs = Date.now();
          clientFlowMetaRef.current = {
            ...(clientFlowMetaRef.current || {}),
            brief_first_token_ms: firstTokenMs,
          };
          if (streamSessionId) {
            writePlaygroundClientFlow(streamSessionId, {
              ...(clientFlowMetaRef.current || {}),
              client_flow_id: clientFlowId,
              brief_session_id: streamSessionId,
              sessionId: streamSessionId,
            });
          }
          setTimings(prev => ({ ...prev, firstToken: firstTokenMs - t0 }));
          api.sendClientE2EBeacon({
            event: 'client_first_token_received',
            stage: 'sse_first_byte',
            source: 'web',
            surface: 'web',
            route: 'session_stream',
            media_type: imageUrls.length ? 'image' : 'unknown',
            duration_ms: Date.now() - t0,
            sse_first_byte_ms: Date.now() - t0,
            sessionId: streamSessionId || undefined,
            client_flow_id: clientFlowId,
            eventType: 'brief_summary_token',
          });
        }
        setSummary(prev => prev + token);
      },
      onIntentions: (items) => {
        setTimings(prev => ({ ...prev, intentions: Date.now() - t0 }));
        setIntentions(items);
      },
      onMedia: (media) => {
        // Issue #1002: per-image descriptions + cache_hit flag.
        setTimings(prev => ({ ...prev, media: Date.now() - t0 }));
        setMediaContext(media);
      },
      onComplete: () => {
        clientFlowMetaRef.current = {
          ...(clientFlowMetaRef.current || {}),
          brief_complete_ms: Date.now(),
        };
        if (streamSessionId) {
          writePlaygroundClientFlow(streamSessionId, {
            ...(clientFlowMetaRef.current || {}),
            client_flow_id: clientFlowId,
            brief_session_id: streamSessionId,
            sessionId: streamSessionId,
          });
        }
        setTimings(prev => ({ ...prev, complete: Date.now() - t0 }));
        api.sendClientE2EBeacon({
          event: 'completed',
          stage: 'total',
          source: 'web',
          surface: 'web',
          route: 'session_stream',
          media_type: imageUrls.length ? 'image' : 'unknown',
          duration_ms: Date.now() - t0,
          sessionId: streamSessionId || undefined,
          client_flow_id: clientFlowId,
        });
        setStreaming(false);
      },
      onError: (err) => {
        setTimings(prev => ({ ...prev, complete: Date.now() - t0 }));
        api.sendClientE2EBeacon({
          event: 'failed',
          outcome: 'error',
          stage: 'total',
          source: 'web',
          surface: 'web',
          route: 'session_stream',
          media_type: imageUrls.length ? 'image' : 'unknown',
          duration_ms: Date.now() - t0,
          sessionId: streamSessionId || undefined,
          client_flow_id: clientFlowId,
          status: err?.message || 'brief_failed',
        });
        setError(err.message || 'Brief failed');
        setStreaming(false);
      },
    });
    abortRef.current = abort;
  }, [images]);

  const handleStartSession = useCallback(async (intention) => {
    setDispatching(intention.title);
    setError(null);
    const dispatchStart = Date.now();
    const clientFlowId = clientFlowRef.current || newClientFlowId();
    clientFlowRef.current = clientFlowId;
    clientFlowMetaRef.current = {
      ...(clientFlowMetaRef.current || {}),
      client_flow_id: clientFlowId,
      dispatch_start_ms: dispatchStart,
    };
    try {
      const hashtag = intention.hashtag;
      const skillSlug = hashtag ? hashtag.replace(/^#/, '') : undefined;
      // LABEL_RULES option chips carry the EXACT sent text in ``prompt`` (the
      // book's contract — it both triggers the owning skill and instructs the
      // photo task), so send it VERBATIM. The cloud-VLM path has no ``prompt``,
      // so reconstruct ``title — description #skill`` as before. Including the
      // #skill-slug in the reconstructed prompt lets exec routing pick it up
      // even if the skillSlug param is stripped; routing (SDK vs FAST) is made
      // server-side by the runner's route-classifier.
      const prompt = intention.prompt
        ? intention.prompt
        : intention.title
          + (intention.description ? ` — ${intention.description}` : '')
          + (skillSlug ? ` #${skillSlug}` : '');
      const result = await api.dispatchExec({
        prompt,
        skillSlug,
        mediaUrls: images.map(i => i.url),
        // Issue #1015: reuse the brief/stream session_id so /exec hits the
        // pre-cached `vi:session:{uid}:{sid}:quick_response` HASH and can
        // replay the agent-speech card before the agent boots.
        sessionId: briefSessionId || undefined,
        // Issue #1015: model selected on the intention page — propagates to
        // session.context.model so the chat top bar can display it.
        model: resolveAutoModel(selectedModel),
        renderScheme: resolveAutoScheme(selectedScheme),
        // Worker backend — SESSION-BOUND; `auto` → undefined (server resolves as today).
        workerBackend: resolveAutoWorker(selectedWorker),
        // Per-turn reasoning effort. Unconditional (not gated on new-session like the
        // scheme): the runner ignores it for every model without catalog reasoning efforts.
        reasoningEffort: resolveAutoEffort(
          effectiveModelId(selectedModel, selectedScheme), selectedEffort,
        ),
      });
      writePlaygroundClientFlow(result?.sessionId || briefSessionId, {
        ...(clientFlowMetaRef.current || {}),
        client_flow_id: clientFlowId,
        brief_session_id: briefSessionId || undefined,
        taskId: result?.taskId || undefined,
        dispatch_ack_ms: Date.now(),
        send_to_ack_ms: Date.now() - dispatchStart,
      });
      api.sendClientE2EBeacon({
        event: 'client_send_ack',
        outcome: 'success',
        stage: 'send_to_ack',
        source: 'web',
        surface: 'web',
        route: 'session_exec',
        media_type: images.length ? 'image' : 'unknown',
        duration_ms: Date.now() - dispatchStart,
        send_to_ack_ms: Date.now() - dispatchStart,
        sessionId: result?.sessionId || briefSessionId || undefined,
        taskId: result?.taskId || undefined,
        client_flow_id: clientFlowId,
      });
      if (result?.sessionId) {
        navigate(`/playground/chat/${result.sessionId}`);
      }
    } catch (err) {
      api.sendClientE2EBeacon({
        event: 'client_send_ack',
        outcome: 'error',
        stage: 'send_to_ack',
        source: 'web',
        surface: 'web',
        route: 'session_exec',
        media_type: images.length ? 'image' : 'unknown',
        duration_ms: Date.now() - dispatchStart,
        send_to_ack_ms: Date.now() - dispatchStart,
        sessionId: briefSessionId || undefined,
        client_flow_id: clientFlowId,
        status: err?.message || 'dispatch_failed',
      });
      setError(`Failed to start session: ${err.message}`);
    } finally {
      setDispatching(null);
    }
  }, [images, navigate, briefSessionId, selectedModel, selectedScheme, selectedEffort]);

  // Issue #1015: free-text dispatch from the ChatInputBar below the
  // intention cards. Same dispatch shape as handleStartSession but without
  // the skill_slug — the user typed a custom prompt, so let runner's
  // routing pick the executor. We DO pass the brief session_id so the
  // (still-cached) media descriptions are reused, and pass the uploaded
  // images so the agent can see them just like an intention click would.
  const handleFreeTextDispatch = useCallback(async (text, extraImageUrls) => {
    if (!text || !text.trim()) return;
    setDispatching('__free_text__');
    setError(null);
    const dispatchStart = Date.now();
    const clientFlowId = clientFlowRef.current || newClientFlowId();
    clientFlowRef.current = clientFlowId;
    clientFlowMetaRef.current = {
      ...(clientFlowMetaRef.current || {}),
      client_flow_id: clientFlowId,
      dispatch_start_ms: dispatchStart,
    };
    try {
      // Combine images already uploaded for the brief with any extra images
      // that ChatInputBar uploaded inline.
      const briefImageUrls = images.map(i => i.url);
      const mediaUrls = [...briefImageUrls, ...(extraImageUrls || [])];
      const result = await api.dispatchExec({
        prompt: text.trim(),
        mediaUrls,
        sessionId: briefSessionId || undefined,
        model: resolveAutoModel(selectedModel),
        renderScheme: resolveAutoScheme(selectedScheme),
        // Worker backend — SESSION-BOUND; `auto` → undefined (server resolves as today).
        workerBackend: resolveAutoWorker(selectedWorker),
        // Per-turn reasoning effort. Unconditional (not gated on new-session like the
        // scheme): the runner ignores it for every model without catalog reasoning efforts.
        reasoningEffort: resolveAutoEffort(
          effectiveModelId(selectedModel, selectedScheme), selectedEffort,
        ),
      });
      writePlaygroundClientFlow(result?.sessionId || briefSessionId, {
        ...(clientFlowMetaRef.current || {}),
        client_flow_id: clientFlowId,
        brief_session_id: briefSessionId || undefined,
        taskId: result?.taskId || undefined,
        dispatch_ack_ms: Date.now(),
        send_to_ack_ms: Date.now() - dispatchStart,
      });
      api.sendClientE2EBeacon({
        event: 'client_send_ack',
        outcome: 'success',
        stage: 'send_to_ack',
        source: 'web',
        surface: 'web',
        route: 'session_exec',
        media_type: mediaUrls.length ? 'image' : 'unknown',
        duration_ms: Date.now() - dispatchStart,
        send_to_ack_ms: Date.now() - dispatchStart,
        sessionId: result?.sessionId || briefSessionId || undefined,
        taskId: result?.taskId || undefined,
        client_flow_id: clientFlowId,
      });
      if (result?.sessionId) {
        navigate(`/playground/chat/${result.sessionId}`);
      }
    } catch (err) {
      api.sendClientE2EBeacon({
        event: 'client_send_ack',
        outcome: 'error',
        stage: 'send_to_ack',
        source: 'web',
        surface: 'web',
        route: 'session_exec',
        media_type: images.length ? 'image' : 'unknown',
        duration_ms: Date.now() - dispatchStart,
        send_to_ack_ms: Date.now() - dispatchStart,
        sessionId: briefSessionId || undefined,
        client_flow_id: clientFlowId,
        status: err?.message || 'dispatch_failed',
      });
      setError(`Failed to start session: ${err.message}`);
    } finally {
      setDispatching(null);
    }
  }, [images, briefSessionId, selectedModel, selectedScheme, selectedEffort, navigate]);

  const removeImage = useCallback((idx) => {
    setImages(prev => prev.filter((_, i) => i !== idx));
    setTitle('');
    setSummary('');
    setIntentions(null);
    setMediaContext(null);
    setTimings(null);
    setElapsed(0);
    setBriefSessionId(null);
    clientFlowRef.current = null;
    clientFlowMetaRef.current = {};
  }, []);

  const hasResults = title || summary || intentions || mediaContext;
  const fmtMs = (ms) => ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`;

  return (
    // The outer wrapper fills the parent `<main>` (which has `flex: 1`
    // inside a flex row in PlaygroundLayout). `height: 100%` is required
    // because `<main>` is a block element — `flex: 1` on children would
    // otherwise be ignored and the scroll container would size to its
    // content instead of the viewport.
    <div style={{
      height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden',
      background: V.bg,
    }}>
      {/* Inner scroll container. minHeight: 0 lets it shrink under the
          max-content of its flex children so overflowY actually kicks in. */}
      <div style={{
        flex: 1, minHeight: 0, overflowY: 'auto',
        padding: '24px 20px 40px',
      }}>
        <div style={{
          display: 'flex', flexDirection: 'column', gap: 20,
          maxWidth: 640, width: '100%', margin: '0 auto',
        }}>
        {/* Header */}
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 600, color: V.text, margin: 0, letterSpacing: '-0.02em' }}>
            Intention
          </h2>
          <p style={{ fontSize: 13, color: V.textDim, margin: '4px 0 0' }}>
            Upload photos and get AI-generated intention suggestions to start a session.
          </p>
          <PromptInspector />
        </div>


        {/* Image Upload Zone */}
        <div
          ref={dropRef}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onClick={() => { if (!uploading) fileRef.current?.click(); }}
          style={{
            border: `2px dashed ${V.border}`,
            borderRadius: 16,
            padding: images.length > 0 ? 12 : '40px 20px',
            textAlign: 'center',
            cursor: uploading ? 'wait' : 'pointer',
            background: V.bgCard,
            transition: 'all 0.2s',
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = V.accent; e.currentTarget.style.background = V.accentDim; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = V.border; e.currentTarget.style.background = V.bgCard; }}
        >
          {images.length === 0 ? (
            <div>
              <div style={{ fontSize: 32, marginBottom: 8 }}>{uploading ? '...' : '\uD83D\uDCF7'}</div>
              <div style={{ fontSize: 14, color: V.text, fontWeight: 500 }}>
                {uploading ? 'Uploading...' : 'Drop images here or click to upload'}
              </div>
              <div style={{ fontSize: 12, color: V.textDim, marginTop: 4 }}>
                Supports JPG, PNG, WebP
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center' }}>
              {images.map((img, i) => (
                <div key={i} style={{ position: 'relative' }}>
                  {img.isVideo || _isVideoUrl(img.url) ? (
                    <div style={{ width: 80, height: 80, borderRadius: 12, overflow: 'hidden', position: 'relative', background: '#000' }}>
                      <video src={img.url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} preload="metadata" muted />
                      <div style={{
                        position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: 'rgba(0,0,0,0.2)', color: '#fff', fontSize: 20,
                      }}>&#9654;</div>
                    </div>
                  ) : (
                    <img
                      src={img.url}
                      alt={img.name}
                      style={{ width: 80, height: 80, borderRadius: 12, objectFit: 'cover' }}
                    />
                  )}
                  <button
                    onClick={(e) => { e.stopPropagation(); removeImage(i); }}
                    style={{
                      position: 'absolute', top: -6, right: -6, width: 22, height: 22,
                      borderRadius: '50%', background: 'rgba(0,0,0,0.6)', color: '#fff', border: 'none',
                      fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >{'\u00D7'}</button>
                </div>
              ))}
              {uploading && (
                <div style={{
                  width: 80, height: 80, borderRadius: 12, background: V.bgHover,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 12, color: V.textDim,
                }}>
                  Uploading...
                </div>
              )}
            </div>
          )}
        </div>
        <input
          ref={fileRef}
          type="file"
          accept="image/*,video/mp4,video/webm,video/quicktime"
          multiple
          style={{ display: 'none' }}
          onChange={(e) => {
            const files = Array.from(e.target.files || []);
            e.target.value = '';
            if (files.length) handleFiles(files);
          }}
        />

        {/* Upload cache toggle (Issue #1339 test affordance). When checked,
            the content-hash cache (issue #999) short-circuits repeat uploads.
            Uncheck to force the full presign → GCS PUT path. */}
        <label
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            fontSize: 11, color: V.textDim, userSelect: 'none', cursor: 'pointer',
            marginTop: -8,
          }}
          title="When checked, re-uploading the same file returns the cached GCS URL without hitting the network. Uncheck to test the upload flow end-to-end."
        >
          <input
            type="checkbox"
            checked={useUploadCache}
            onChange={(e) => toggleUseUploadCache(e.target.checked)}
            style={{ margin: 0, cursor: 'pointer' }}
          />
          Use cached GCS URL
        </label>

        {/* Upload timing badge */}
        {timings?.upload && !hasResults && (
          <div style={{ fontSize: 11, color: V.textDim, textAlign: 'center', marginTop: -12 }}>
            Uploaded in {fmtMs(timings.upload)}
            {timings.uploadCount > 0 && timings.uploadCacheHits === timings.uploadCount && (
              <span style={{ color: V.green, marginLeft: 4 }}>(cached)</span>
            )}
            {timings.uploadCount > 0 && timings.uploadCacheHits > 0 && timings.uploadCacheHits < timings.uploadCount && (
              <span style={{ color: V.green, marginLeft: 4 }}>
                ({timings.uploadCacheHits}/{timings.uploadCount} cached)
              </span>
            )}
          </div>
        )}

        {/* Send Button */}
        {images.length > 0 && !hasResults && (
          <button
            onClick={handleSend}
            disabled={streaming || uploading}
            style={{
              padding: '12px 24px',
              borderRadius: 12,
              border: 'none',
              background: streaming ? V.textDim : V.accent,
              color: '#fff',
              fontSize: 15,
              fontWeight: 600,
              cursor: streaming ? 'wait' : 'pointer',
              transition: 'all 0.2s',
              boxShadow: `0 2px 12px ${V.accent}40`,
            }}
          >
            {streaming ? 'Analyzing...' : 'Analyze Images'}
          </button>
        )}

        {/* Error */}
        {error && (
          <div style={{
            padding: '10px 14px', borderRadius: 10,
            background: 'rgba(255,59,48,0.08)', color: V.red,
            fontSize: 13, fontWeight: 500,
          }}>
            {error}
          </div>
        )}

        {/* Timing Bar — live elapsed + phase deltas */}
        {timings?.start && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
            padding: '8px 12px', borderRadius: 10,
            background: V.bgCard, border: `1px solid ${V.border}`,
            fontSize: 11, fontFamily: V.font, color: V.textDim,
          }}>
            <span style={{
              fontWeight: 700, fontSize: 13, color: streaming ? V.accent : V.green,
              fontVariantNumeric: 'tabular-nums',
            }}>
              {fmtMs(elapsed)}
            </span>
            {briefSessionId && (
              <span
                title={`Click to copy full session_id. Use with /profile <session_id> or as Langfuse trace id.\n\n${briefSessionId}`}
                onClick={copyBriefSessionId}
                style={{
                  padding: '1px 6px', borderRadius: 4,
                  background: briefSessionIdCopied ? 'rgba(52,199,89,0.18)' : 'rgba(255,255,255,0.06)',
                  color: briefSessionIdCopied ? V.green : V.textDim,
                  cursor: 'pointer',
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                  userSelect: 'all',
                  transition: 'background 0.15s, color 0.15s',
                }}
              >
                {briefSessionIdCopied ? '✓ copied' : `sid ${briefSessionId}`}
              </span>
            )}
            {timings.upload && (
              <span style={{ padding: '1px 6px', borderRadius: 4, background: 'rgba(0,122,255,0.08)', color: V.blue }}>
                upload {fmtMs(timings.upload)}
                {timings.uploadCount > 0 && timings.uploadCacheHits === timings.uploadCount && ' · cached'}
              </span>
            )}
            {timings.firstToken && (
              <span style={{ padding: '1px 6px', borderRadius: 4, background: 'rgba(52,199,89,0.08)', color: V.green }}>
                TTFT {fmtMs(timings.firstToken)}
              </span>
            )}
            {timings.intentions && (
              <span style={{ padding: '1px 6px', borderRadius: 4, background: 'rgba(88,86,214,0.08)', color: V.accent }}>
                intentions {fmtMs(timings.intentions)}
              </span>
            )}
            {timings.media && (
              <span style={{ padding: '1px 6px', borderRadius: 4, background: 'rgba(255,149,0,0.08)', color: V.yellow }}>
                media {fmtMs(timings.media)}
              </span>
            )}
            {timings.complete && (
              <span style={{ padding: '1px 6px', borderRadius: 4, background: 'rgba(52,199,89,0.08)', color: V.green }}>
                done {fmtMs(timings.complete)}
              </span>
            )}
            {streaming && <span style={{ animation: 'pulse 1s infinite' }}>...</span>}
          </div>
        )}

        {/* Brief title + conversational opening — Issue #1292: title streams
            first as a bold headline, then summary follows as VI's first line. */}
        {(title || summary) && (
          <div style={{
            display: 'flex', gap: 12, alignItems: 'flex-start',
            padding: '14px 16px 16px',
            borderRadius: 16,
            background: `linear-gradient(135deg, ${V.accent}14, ${V.accent}05)`,
            border: `1px solid ${V.accent}26`,
          }}>
            <div style={{
              flexShrink: 0,
              width: 32, height: 32, borderRadius: '50%',
              background: V.accent, color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 16, fontWeight: 700,
              boxShadow: `0 2px 8px ${V.accent}40`,
            }}>
              VI
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              {title && (
                <div style={{
                  fontSize: 17, fontWeight: 700, lineHeight: 1.3, color: V.text,
                  marginBottom: summary ? 6 : 0,
                }}>
                  {title}
                  {streaming && !summary && (
                    <span style={{ animation: 'pulse 1s infinite', opacity: 0.5, marginLeft: 2 }}>|</span>
                  )}
                </div>
              )}
              {summary && (
                <div style={{
                  fontSize: 15, lineHeight: 1.55, color: V.text,
                  fontWeight: 500,
                }}>
                  {summary}
                  {streaming && (
                    <span style={{ animation: 'pulse 1s infinite', opacity: 0.5, marginLeft: 2 }}>|</span>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Per-image media context — Issue #1002: brief/stream now writes
            vlm descriptions to the shared Redis registry. This block surfaces
            them so the user can verify what the agent will see at /exec time,
            including whether the description was served from cache. */}
        {mediaContext && mediaContext.length > 0 && (
          <div style={{
            padding: '16px 18px', borderRadius: 14,
            background: V.bgCard, border: `1px solid ${V.border}`,
          }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10,
            }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: V.textDim, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Image Context
              </div>
              <span style={{ fontSize: 11, color: V.textDim }}>
                {mediaContext.length} image{mediaContext.length > 1 ? 's' : ''}
                {' · '}
                {mediaContext.filter(m => m.cache_hit).length}/{mediaContext.length} cache hit
              </span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {mediaContext.map((m, i) => {
                const img = images[i];
                return (
                  <div key={m.ref || i} style={{
                    display: 'flex', gap: 12, alignItems: 'flex-start',
                    padding: 10, borderRadius: 10,
                    background: V.bg,
                  }}>
                    {img && !img.isVideo && !_isVideoUrl(img.url) && (
                      <img
                        src={img.url}
                        alt=""
                        style={{ width: 56, height: 56, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }}
                      />
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: 6,
                        marginBottom: 4,
                      }}>
                        <code style={{
                          fontSize: 11, fontFamily: V.font, color: V.accent,
                          background: 'rgba(88,86,214,0.08)',
                          padding: '2px 6px', borderRadius: 4,
                          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                          maxWidth: 220,
                        }}>
                          media://{m.media_id || '—'}
                        </code>
                        <span style={{
                          fontSize: 10, fontWeight: 600,
                          padding: '2px 6px', borderRadius: 4,
                          background: m.cache_hit ? 'rgba(52,199,89,0.12)' : 'rgba(0,122,255,0.10)',
                          color: m.cache_hit ? V.green : V.blue,
                          textTransform: 'uppercase', letterSpacing: '0.03em',
                        }}>
                          {m.cache_hit ? 'cached' : 'fresh'}
                        </span>
                      </div>
                      <div style={{ fontSize: 12, color: m.description ? V.text : V.red, lineHeight: 1.5 }}>
                        {m.description || `(unavailable: ${m.error || 'unknown'})`}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Intention Cards */}
        {intentions && intentions.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: V.textDim, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Suggestions
            </div>
            {intentions.map((intent, i) => (
              <div
                key={i}
                style={{
                  padding: '14px 16px',
                  borderRadius: 14,
                  background: V.bgCard,
                  border: `1px solid ${V.border}`,
                  transition: 'all 0.15s',
                  cursor: 'pointer',
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = V.accent; e.currentTarget.style.boxShadow = `0 2px 12px ${V.accent}20`; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = V.border; e.currentTarget.style.boxShadow = 'none'; }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                  <span style={{ fontSize: 24, flexShrink: 0, lineHeight: 1 }}>
                    {intent.icon || '\uD83D\uDCA1'}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: V.text, marginBottom: 4 }}>
                      {intent.title}
                    </div>
                    {intent.description && (
                      <div style={{ fontSize: 13, color: V.textDim, lineHeight: 1.5 }}>
                        {intent.description}
                      </div>
                    )}
                    {/* Issue #1015: developer-facing playground surfaces the
                        pre-cached quick_response so you can see what the
                        agent will say if this card is clicked. */}
                    {intent.quick_response && (
                      <div style={{
                        marginTop: 8,
                        padding: '6px 10px',
                        borderRadius: 8,
                        background: 'rgba(88,86,214,0.08)',
                        borderLeft: `2px solid ${V.accent}`,
                        fontSize: 12,
                        color: V.text,
                        fontStyle: 'italic',
                        lineHeight: 1.4,
                      }}
                        title="Pre-cached quick_response — replayed as an agent-speech card before the agent boots (Issue #1015)"
                      >
                        🗣️ {intent.quick_response}
                      </div>
                    )}
                    {intent.hashtag && (
                      <span style={{
                        display: 'inline-block', marginTop: 6,
                        padding: '2px 8px', borderRadius: 6,
                        background: intent.matched ? 'rgba(52,199,89,0.1)' : 'rgba(255,149,0,0.1)',
                        color: intent.matched ? V.green : V.yellow,
                        fontSize: 11, fontWeight: 500,
                      }}
                        title={intent.matched ? 'Matches installed package' : 'No matching package'}
                      >
                        {intent.hashtag}{!intent.matched && ' ?'}
                      </span>
                    )}
                    {/* Issue #1015: dump any extra fields the LLM returned
                        beyond the canonical icon/title/description/hashtag/
                        quick_response set. Helps when iterating on the
                        prompt — new fields show up automatically without
                        a frontend code change. */}
                    {(() => {
                      const known = new Set(['icon', 'title', 'description', 'hashtag', 'matched', 'quick_response']);
                      const extras = Object.entries(intent).filter(([k]) => !known.has(k));
                      if (extras.length === 0) return null;
                      return (
                        <details style={{ marginTop: 6 }}>
                          <summary style={{ fontSize: 10, color: V.textDim, cursor: 'pointer', userSelect: 'none' }}>
                            + {extras.length} extra field{extras.length > 1 ? 's' : ''}
                          </summary>
                          <pre style={{
                            margin: '4px 0 0 0', padding: '6px 8px',
                            borderRadius: 6, background: 'rgba(0,0,0,0.04)',
                            fontSize: 10, color: V.textDim,
                            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                            overflow: 'auto', maxHeight: 200,
                            whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                          }}>
                            {JSON.stringify(Object.fromEntries(extras), null, 2)}
                          </pre>
                        </details>
                      );
                    })()}
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleStartSession(intent); }}
                    disabled={dispatching !== null}
                    style={{
                      padding: '8px 14px',
                      borderRadius: 10,
                      border: 'none',
                      background: dispatching === intent.title ? V.textDim : V.accent,
                      color: '#fff',
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: dispatching !== null ? 'wait' : 'pointer',
                      flexShrink: 0,
                      transition: 'all 0.2s',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {dispatching === intent.title ? 'Starting...' : 'Start Session'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Issue #1015: free-text dispatch — reuses ChatInputBar so users
            can either click an intention OR type their own prompt to start
            a session. Shown only AFTER the brief returns intentions (so
            the model picker doesn't clutter the upload-only state).
            Inherits the model selector from this page's local state. */}
        {intentions && intentions.length > 0 && (
          <div style={{
            marginTop: 8,
            border: `1px solid ${V.border}`,
            borderRadius: 16,
            background: V.bgCard,
            overflow: 'hidden',
          }}>
            <div style={{
              padding: '8px 16px 4px',
              fontSize: 11, fontWeight: 600,
              color: V.textDim,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
            }}>
              Or type your own prompt
            </div>
            <ChatInputBar
              onSend={(text, extraImageUrls) => handleFreeTextDispatch(text, extraImageUrls)}
              disabled={dispatching !== null}
              isExecuting={false}
              selectedModel={selectedModel}
              onModelChange={setSelectedModel}
              selectedScheme={selectedScheme}
              onSchemeChange={setSelectedScheme}
              selectedEffort={selectedEffort}
              onEffortChange={setSelectedEffort}
              selectedWorker={selectedWorker}
              onWorkerChange={setSelectedWorker}
            />
          </div>
        )}

        {/* Reset button after results */}
        {hasResults && !streaming && (
          <button
            onClick={() => {
              setImages([]);
              setTitle('');
              setSummary('');
              setIntentions(null);
              setMediaContext(null);
              setError(null);
              setTimings(null);
              setElapsed(0);
              abortRef.current?.();
            }}
            style={{
              padding: '10px 20px', borderRadius: 10,
              border: `1px solid ${V.border}`, background: 'transparent',
              color: V.textDim, fontSize: 13, fontWeight: 500,
              cursor: 'pointer', alignSelf: 'center',
            }}
          >
            Start Over
          </button>
        )}
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// BRIEF CONTEXT BLOCK — Issue #1459
// Renders the intention-brief title/summary that /exec seeded into this
// session, shown ABOVE the first user message so it's immediately visible
// when the session was born from /brief/stream. Silent when the session
// has no brief (follow-up turns, legacy sessions, direct chat) — zero
// regression for non-intention paths.
// ══════════════════════════════════════════════════════════════════════

function BriefContextBlock({ session }) {
  const brief = session?.metadata?.brief;
  if (!brief || (!brief.title && !brief.summary)) return null;
  return (
    <div style={{
      marginBottom: 16,
      padding: '10px 14px',
      borderRadius: 8,
      border: `1px solid ${V.border}`,
      background: 'rgba(88,86,214,0.06)',
      fontSize: 12,
      lineHeight: 1.5,
    }}>
      <div style={{
        fontSize: 10, fontWeight: 600, letterSpacing: 0.4,
        textTransform: 'uppercase', color: V.purple, marginBottom: 4,
      }}>
        Intention brief
      </div>
      {brief.title && (
        <div style={{ fontWeight: 600, color: V.text, marginBottom: brief.summary ? 4 : 0 }}>
          {brief.title}
        </div>
      )}
      {brief.summary && (
        <div style={{ color: V.textDim, whiteSpace: 'pre-wrap' }}>
          {brief.summary}
        </div>
      )}
    </div>
  );
}


// ══════════════════════════════════════════════════════════════════════
// CHAT PAGE — mirrors dashboard chat-layout
// ══════════════════════════════════════════════════════════════════════

function ChatPage({ sessionStore, sseEvents, viUserId }) {
  const { sessionId: urlSessionId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  // Card anchor: a session-badge jump lands at /playground/chat/<sid>?card=<log_card_id>.
  // Scroll to that widget_log card (stamped id=`log-card-<log_card_id>`) + briefly highlight,
  // instead of the default scroll-to-bottom.
  const cardAnchor = searchParams.get('card');
  // Opening-prompt anchor: the `open_main_session` bridge (a button inside a
  // widget's HTML) lands at /playground/chat/<sid>?ask=<text>. Auto-send it
  // ONCE the session view is ready, then strip the param — the in-bundle
  // equivalent of the host chat button's auto-sent opening line on the app.
  const askParam = searchParams.get('ask');
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [selectedSessionId, setSelectedSessionId] = useState(urlSessionId || null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [liveBlocks, setLiveBlocks] = useState([]);
  const [sending, setSending] = useState(false);
  // Rewind (#session-rewind). Tapping Rewind is LOCAL-ONLY and abandon-safe:
  // `rewindCut` = `{ key, rid, tappedAt }` hides the turn and everything below
  // it and `composerPrefill` refills the composer; NOTHING durable happens.
  // The truncate rides the NEXT send as the additive `rewind_to_rid` param, so
  // abandoning the edit (or reloading) leaves the session fully intact.
  const [rewindCut, setRewindCut] = useState(null);
  const [composerPrefill, setComposerPrefill] = useState(null);
  // Read inside `handleSend` without making it a dependency (it would churn the
  // callback identity on every rewind tap).
  const rewindCutRef = useRef(null);
  useEffect(() => { rewindCutRef.current = rewindCut; }, [rewindCut]);
  // Widget panel (task #4): SESSION-BOUND + persists across reopen. The suggest
  // result (suggestions + logged) is cached PER session id; entering a session
  // ALWAYS fires a silent update. "My Widgets" (`recentWidgets`) is GLOBAL
  // (session-independent) so it's ALWAYS shown — even before a session call lands.
  const [widgetSuggestMap, setWidgetSuggestMap] = useState({});
  const [recentWidgets, setRecentWidgets] = useState([]); // global "My Widgets"
  // sid → { optionId → 'queuing' }: the optimistic overlay for panel options
  // tapped this round (findings §6.4). Tapping an idle option sets its `id` to
  // 'queuing' locally so the badge appears instantly; the server's authoritative
  // `status` + `log_card_id` take over on the next suggest refresh. Keyed by the
  // option's FULL `id` (which folds the media key) so per-image options don't
  // collide. DROPPED on session change + on every applySilentSuggest land
  // (re-enter recovery, G4) so a withdrawn pending card correctly shows idle.
  const [optimisticStatusMap, setOptimisticStatusMap] = useState({});
  const widgetSuggestReq = useRef(0);
  // Always-current session id so an async suggest resolve binds to the session
  // that minted during the send (a brand-new chat has no id at fire time).
  const selectedSessionIdRef = useRef(selectedSessionId);
  useEffect(() => { selectedSessionIdRef.current = selectedSessionId; }, [selectedSessionId]);
  const sessionStreamMetricsRef = useRef({});
  const sessionCardTemplateRef = useRef({});

  const getSessionStreamMetricState = useCallback((sid) => {
    if (!sid) return null;
    const existing = sessionStreamMetricsRef.current[sid];
    if (existing) return existing;
    const flow = readPlaygroundClientFlow(sid) || {};
    const state = {
      sessionId: sid,
      clientFlowId: flow.client_flow_id || null,
      taskId: flow.taskId || flow.task_id || null,
      mediaType: flow.media_type || 'unknown',
      startedAtMs: Number(flow.dispatch_start_ms || flow.dispatch_ack_ms || Date.now()),
      connectedSent: false,
      firstByteSent: false,
      firstTokenSent: false,
      firstPaintSent: false,
      completedSent: false,
    };
    sessionStreamMetricsRef.current[sid] = state;
    return state;
  }, []);

  const sendSessionStreamBeacon = useCallback((sid, state, beacon) => {
    if (!sid || !state) return;
    api.sendClientE2EBeacon({
      source: 'web',
      surface: 'web',
      route: 'session_stream',
      media_type: state.mediaType || 'unknown',
      sessionId: sid,
      taskId: beacon.taskId || state.taskId || undefined,
      client_flow_id: state.clientFlowId || undefined,
      ...beacon,
    });
  }, []);
  // A SILENT, history-aware suggest refresh for a session — the backend
  // reconstructs the session's full transcript (prior turns + logs) + its latest
  // photo, so this yields REAL suggestions, not just the generic Collection. Never
  // raises attention (that's the exec/send path). Keeps cached suggestions only if
  // the backend returned none (e.g. an env with no Collection template).
  const applySilentSuggest = useCallback((sid) => {
    if (!sid) return;
    api.suggestWidgets({ sessionId: sid }).then((data) => {
      if (data.fallback_reason !== 'client_request_failed' && Array.isArray(data.recent)) {
        setRecentWidgets(data.recent);
      }
      // `classified` means the classifier RAN — adopt its list VERBATIM, even
      // when empty (right after a commit there is nothing left to log; keeping
      // the stale option would re-offer an already-logged item forever). Only
      // an unclassified (refresh-mode skip) empty result keeps the prior list.
      const fresh = Array.isArray(data.suggestions) ? data.suggestions : [];
      setWidgetSuggestMap(m => ({ ...m, [sid]: {
        data: {
          suggestions: (data.classified || fresh.length)
            ? fresh : (m[sid]?.data?.suggestions || []),
          logged_in_session: data.logged_in_session || [],
        },
        attention: m[sid]?.attention || false,
        loading: false,
        mediaUrls: m[sid]?.mediaUrls || [],
      } }));
      // The round settled and the server reported the panel state — drop ALL
      // optimistic overlays for this session (re-enter recovery, G4). The
      // server's authoritative `status` + `log_card_id` now own the display:
      // a withdrawn pending card yields `status:idle` → option recovers to not
      // queuing; a committed one yields `status:logged`.
      setOptimisticStatusMap(m => {
        if (!(sid in m)) return m;
        const { [sid]: _drop, ...rest } = m;
        return rest;
      });
    }).catch(() => {});
  }, []);
  // Entering a session ALWAYS triggers a SILENT, history-aware panel update.
  // Fires whenever the viewed session changes, so opening any session (even an
  // old one with no prior suggestions) immediately gets real suggestions.
  // Re-enter recovery (G4): DROP this session's optimistic overlay first so the
  // server-authoritative status wins — a withdrawn pending card shows idle again
  // rather than a stale "Queuing" badge left over from the previous visit.
  useEffect(() => {
    if (!selectedSessionId) return;
    setOptimisticStatusMap(m => {
      if (!(selectedSessionId in m)) return m;
      const { [selectedSessionId]: _drop, ...rest } = m;
      return rest;
    });
    applySilentSuggest(selectedSessionId);
  }, [selectedSessionId, applySilentSuggest]);
  // The CURRENT session's panel = its cached suggestions/logged + the GLOBAL
  // My Widgets (always shown, even when this session has no cached result yet).
  // The server-authoritative `status` is overlaid with the local optimistic
  // overlay (findings §6.4): a just-tapped idle option shows `queuing` instantly
  // until the next suggest refresh lands the real status. The overlay only
  // UPGRADES idle→queuing — it never overrides a server `logged`/`canceled`.
  const widgetSuggest = useMemo(() => {
    const cur = widgetSuggestMap[selectedSessionId] || EMPTY_WIDGET_SUGGEST;
    const haveAny = (cur.data?.suggestions?.length || cur.data?.logged_in_session?.length || recentWidgets.length);
    if (!haveAny) return { ...cur, data: null };
    const overlay = optimisticStatusMap[selectedSessionId] || null;
    const baseSuggestions = cur.data?.suggestions || [];
    const suggestions = overlay
      ? baseSuggestions.map((opt) => {
          const status = opt.status || (opt.queuing ? 'queuing' : 'idle');
          // Only optimistically advance an option the server still calls idle —
          // never mask an authoritative logged/canceled/queuing transition.
          if (status === 'idle' && opt.id && overlay[opt.id]) {
            return { ...opt, status: 'queuing' };
          }
          return opt;
        })
      : baseSuggestions;
    return {
      ...cur,
      data: {
        suggestions,
        logged_in_session: cur.data?.logged_in_session || [],
        recent: recentWidgets,
      },
    };
  }, [widgetSuggestMap, selectedSessionId, recentWidgets, optimisticStatusMap]);
  const [selectedModel, setSelectedModel] = useState(() => {
    try { return localStorage.getItem('playground-model') || DEFAULT_MODEL; } catch { return DEFAULT_MODEL; }
  });
  // Render-scheme picker for NEW sessions (existing sessions show a locked badge instead).
  const [selectedScheme, setSelectedScheme] = useState(() => {
    try { return localStorage.getItem('playground-render-scheme') || DEFAULT_RENDER_SCHEME; } catch { return DEFAULT_RENDER_SCHEME; }
  });
  // Reasoning-effort picker — PER-TURN (re-sent every message), persisted like the two above.
  const [selectedEffort, setSelectedEffort] = useState(() => {
    try { return localStorage.getItem('playground-reasoning-effort') || DEFAULT_REASONING_EFFORT; } catch { return DEFAULT_REASONING_EFFORT; }
  });
  // Worker-backend picker for NEW sessions — SESSION-BOUND (mirrors model/scheme), persisted.
  const [selectedWorker, setSelectedWorker] = useState(() => {
    try { return localStorage.getItem('playground-worker-backend') || DEFAULT_WORKER_BACKEND; } catch { return DEFAULT_WORKER_BACKEND; }
  });
  // Data-source mode for the current session view — developer debug.
  // auto (default): active → SSE, terminal → GET snapshot.
  // get / sse / get+sse: override both gates explicitly.
  const [fetchMode, setFetchMode] = useState(() => {
    try { return localStorage.getItem('playground-fetch-mode') || 'auto'; } catch { return 'auto'; }
  });
  const onChangeFetchMode = useCallback((next) => {
    setFetchMode(next);
    try { localStorage.setItem('playground-fetch-mode', next); } catch { /* ignore */ }
  }, []);
  // Session list status filter (#1458). 'auto' = grouped sort by bucket.
  const [statusFilter, setStatusFilter] = useState(() => {
    try { return localStorage.getItem('playground.sessionFilter') || 'auto'; } catch { return 'auto'; }
  });
  const onChangeStatusFilter = useCallback((next) => {
    setStatusFilter(next);
    try { localStorage.setItem('playground.sessionFilter', next); } catch { /* ignore */ }
  }, []);
  // Session list fold state (#1458) — persisted.
  const [sessionListCollapsed, setSessionListCollapsed] = useState(() => {
    try { return localStorage.getItem('playground.sessionListCollapsed') === '1'; } catch { return false; }
  });
  const toggleSessionListCollapsed = useCallback(() => {
    setSessionListCollapsed(prev => {
      const next = !prev;
      try { localStorage.setItem('playground.sessionListCollapsed', next ? '1' : '0'); } catch { /* ignore */ }
      return next;
    });
  }, []);
  const [usageOpen, setUsageOpen] = useState(false);
  const [usageData, setUsageData] = useState(null);
  const [usageLoading, setUsageLoading] = useState(false);
  const [usageTab, setUsageTab] = useState('billing'); // 'billing' | 'tools' — Session Usage table tabs
  const messagesEndRef = useRef(null);

  // Card anchor scroll — when arriving via a session-badge jump (?card=<log_card_id>),
  // scroll to that widget_log card + briefly highlight, then consume the param so a
  // later re-render doesn't re-jump. Retries for a few seconds while history renders.
  useEffect(() => {
    if (!cardAnchor) return;
    let cancelled = false;
    let tries = 0;
    const tick = () => {
      if (cancelled) return;
      const el = document.getElementById(`log-card-${cardAnchor}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const prevShadow = el.style.boxShadow;
        const prevTrans = el.style.transition;
        el.style.transition = 'box-shadow 0.4s ease';
        el.style.boxShadow = '0 0 0 3px #0A84FF';
        setTimeout(() => { el.style.boxShadow = prevShadow; el.style.transition = prevTrans; }, 1800);
        setSearchParams((p) => { const n = new URLSearchParams(p); n.delete('card'); return n; }, { replace: true });
        return;
      }
      if (tries++ < 24) setTimeout(tick, 250); // ~6s budget for the card to mount
    };
    const t = setTimeout(tick, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [cardAnchor, selectedSessionId, setSearchParams]);

  // Terminal session statuses — no SSE needed, content from single GET call
  const TERMINAL_STATUSES = useMemo(() => new Set(['completed', 'failed', 'stopped', 'timeout']), []);

  // ── Per-session SSE: only for active (non-terminal) sessions ──
  const sessionSseOnEvent = useCallback((event) => {
    const sid = event?.sessionId || selectedSessionIdRef.current;
    const state = getSessionStreamMetricState(sid);
    const taskId = event?.taskId || event?.task_id || state?.taskId || undefined;
    // `user_turn` (#2661): the runner emits this for each user message in a
    // multi-turn task (mid-stream injects) so SSE-replay reconstructs the user
    // bubbles the WAL otherwise lacks. Render it as a user block, deduped by
    // text against existing blocks (handleSend already added the live one; the
    // GET timeline prompt dedups by the same text downstream).
    if (event?.op === 'user_turn' || event?.type === 'user_turn') {
      const text = (event?.text || '').trim();
      if (text) {
        const ts = (typeof event?.ts === 'number' && event.ts > 0) ? event.ts : Date.now();
        setLiveBlocks(prev => {
          if (prev.some(b => b.type === 'user' && (b.text || '').trim() === text)) return prev;
          return [...prev, { type: 'user', text, mediaUrls: [], ts }];
        });
      }
      return;
    }
    const isFirstAnswer = isAnswerBearingSessionEvent(event, sessionCardTemplateRef.current);
    if (sessionStore?.processEvent) sessionStore.processEvent(event);
    if (sid && state && isFirstAnswer && !state.firstTokenSent) {
      state.firstTokenSent = true;
      const elapsedMs = Date.now() - state.startedAtMs;
      sendSessionStreamBeacon(sid, state, {
        event: 'client_first_token_received',
        stage: 'sse_first_byte',
        duration_ms: elapsedMs,
        sse_first_byte_ms: elapsedMs,
        taskId,
        eventType: sessionEventType(event),
      });
      const sendPaint = () => {
        if (state.firstPaintSent) return;
        state.firstPaintSent = true;
        const paintMs = Date.now() - state.startedAtMs;
        sendSessionStreamBeacon(sid, state, {
          event: 'client_first_visible_paint',
          stage: 'first_visible_paint',
          duration_ms: paintMs,
          first_visible_paint_ms: paintMs,
          paint_surface: 'web',
          taskId,
          eventType: sessionEventType(event),
        });
      };
      if (typeof requestAnimationFrame === 'function') {
        requestAnimationFrame(sendPaint);
      } else {
        setTimeout(sendPaint, 0);
      }
    }
    if (sid && state && (event?.type === 'exec_result' || event?.op === 'exec_result') && !state.completedSent) {
      state.completedSent = true;
      sendSessionStreamBeacon(sid, state, {
        event: 'completed',
        stage: 'total',
        duration_ms: Date.now() - state.startedAtMs,
        taskId,
        eventType: sessionEventType(event),
      });
    }
  }, [getSessionStreamMetricState, sessionStore, sendSessionStreamBeacon]);

  const sessionSseOnConnected = useCallback(({ sessionId }) => {
    const sid = sessionId || selectedSessionIdRef.current;
    const state = getSessionStreamMetricState(sid);
    if (!sid || !state || state.connectedSent) return;
    state.connectedSent = true;
    sendSessionStreamBeacon(sid, state, {
      event: 'client_sse_connected',
      stage: 'sse_first_byte',
      duration_ms: Date.now() - state.startedAtMs,
      taskId: state.taskId || undefined,
    });
  }, [getSessionStreamMetricState, sendSessionStreamBeacon]);

  const sessionSseOnFirstByte = useCallback(({ sessionId }) => {
    const sid = sessionId || selectedSessionIdRef.current;
    const state = getSessionStreamMetricState(sid);
    if (!sid || !state || state.firstByteSent) return;
    state.firstByteSent = true;
    const elapsedMs = Date.now() - state.startedAtMs;
    sendSessionStreamBeacon(sid, state, {
      event: 'client_sse_first_byte',
      stage: 'sse_first_byte',
      duration_ms: elapsedMs,
      sse_first_byte_ms: elapsedMs,
      taskId: state.taskId || undefined,
    });
  }, [getSessionStreamMetricState, sendSessionStreamBeacon]);
  // Derive SSE session: null for terminal sessions → hook disconnects.
  // Also null when sessions haven't loaded yet (selectedSession_ not found) —
  // avoids a wasted SSE connection attempt before we know the status.
  const selectedSession_ = sessions.find(s => s.sessionId === selectedSessionId);
  const sseSessionId = (() => {
    if (!selectedSessionId) return null;
    // Sessions not loaded yet — wait before connecting SSE
    if (sessions.length > 0 && !selectedSession_) return null;
    // Fetch mode decides: auto respects status; get/sse/get+sse overrides.
    if (!shouldConnectSSE(fetchMode, selectedSession_?.status, TERMINAL_STATUSES)) return null;
    return selectedSessionId;
  })();
  useSessionEventStream(viUserId, sseSessionId, {
    onEvent: sessionSseOnEvent,
    onConnected: sessionSseOnConnected,
    onFirstByte: sessionSseOnFirstByte,
  });

  // ── On-demand session detail (fetched when switching to a session) ──
  const [sessionDetailCache, setSessionDetailCache] = useState({});

  // Load session list from API — lightweight sidebar data only.
  const loadSessions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let apiSessions;
      try {
        apiSessions = await api.getSessions({ summary: true });
      } catch {
        apiSessions = await api.getSessionsByDevice(null, { summary: true });
      }
      // Normalize API sessions — sidebar data only (no result/timeline/artifacts)
      const normalized = (apiSessions || []).map(s => {
        const ts = new Date(s.started_at || s.dispatched_at || 0).getTime();
        return {
          sessionId: s.id,
          tasks: s.prompt ? [{ prompt: s.prompt, ts, sessionId: s.id, skillSlug: s.context?.skill_slug || null }] : [],
          latestTs: ts,
          status: s.status,
          title: s.title,
          // Issue #1459: carry session.metadata so BriefContextBlock can
          // render metadata.brief.{title,summary} above the first user
          // message for intention-brief-seeded sessions.
          metadata: s.metadata || null,
          result_html: s.result_html || '',
          result_summary: s.result_summary || '',
          context: s.context || null,
        };
      });
      // Preserve each session's FIRST prompt across refreshes. The summary's
      // `s.prompt` is overwritten to the LATEST turn every round (session_center.py
      // `session.prompt = prompt`), so a mid-turn `loadSessions` (fired by the
      // session_update SSE) would flip the sidebar + header title to the latest
      // prompt — the "header shows the latest prompt while turn 2 streams" bug. The
      // first user message never changes, so once we've captured a session's first
      // task, keep it; only `latestTs`/`status`/`title` update. (New sessions not yet
      // in state capture their current prompt as the first.)
      setSessions(prev => {
        const prevById = new Map(prev.map(p => [p.sessionId, p]));
        return normalized
          .map(n => {
            const old = prevById.get(n.sessionId);
            return old && old.tasks?.[0]?.prompt ? { ...n, tasks: old.tasks } : n;
          })
          .sort((a, b) => b.latestTs - a.latestTs);
      });
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (viUserId) loadSessions(); }, [loadSessions, viUserId]);

  // Auto-refresh session list when session_update SSE events arrive.
  // This ensures Flutter-initiated sessions appear in the sidebar without manual refresh,
  // and selected session data updates when a session completes.
  const lastSessionUpdateRef = useRef(0);
  useEffect(() => {
    if (!sseEvents || sseEvents.length === 0) return;
    const latest = sseEvents[sseEvents.length - 1];
    if (!latest || latest._ts <= lastSessionUpdateRef.current) return;
    if (latest.type !== 'session_update') return;
    lastSessionUpdateRef.current = latest._ts;
    // Invalidate detail cache for this session — status changed, need fresh data
    const updatedSid = latest.session_id;
    if (updatedSid && detailCacheRef.current[updatedSid]) {
      setSessionDetailCache(prev => {
        const next = { ...prev };
        delete next[updatedSid];
        return next;
      });
    }
    // Debounce: avoid rapid reloads for burst events
    const timer = setTimeout(() => loadSessions(), 500);
    return () => clearTimeout(timer);
  }, [sseEvents, loadSessions]);

  // Sync URL sessionId → local state
  useEffect(() => {
    setSelectedSessionId(urlSessionId || null);
  }, [urlSessionId]);

  // Fetch full session detail on demand (for completed sessions — no SSE replay).
  // Uses a ref for the cache check to avoid recreating selectSession on every cache update.
  const detailCacheRef = useRef(sessionDetailCache);
  useEffect(() => { detailCacheRef.current = sessionDetailCache; }, [sessionDetailCache]);

  // `force` bypasses the cache — used by Rewind, whose composer preview needs
  // the round's media URLs re-signed (the playground fetches `?format=signed`,
  // whose GCS signatures expire ~1h; a stale one renders a broken thumbnail).
  const fetchSessionDetail = useCallback(async (sid, { force = false } = {}) => {
    if (!sid) return null;
    if (!force && detailCacheRef.current[sid]) return detailCacheRef.current[sid];
    try {
      const detail = await api.getSession(sid);
      setSessionDetailCache(prev => ({ ...prev, [sid]: detail }));
      return detail;
    } catch (err) {
      console.warn('[session-detail] fetch failed:', sid, err.message);
      return null;
    }
  }, []);

  // Navigate helper — sync local state + URL + switch sessionStore session scope
  const selectSession = useCallback((sid) => {
    setSelectedSessionId(sid);
    // Read model from session context when selecting an existing session
    if (sid) {
      const sess = sessions.find(s => s.sessionId === sid);
      setSelectedModel(sess?.context?.model || DEFAULT_MODEL);
      // For terminal sessions, fetch full detail (no SSE will replay cards)
      if (sess && TERMINAL_STATUSES.has(sess.status)) {
        fetchSessionDetail(sid);
      }
    } else {
      try { setSelectedModel(localStorage.getItem('playground-model') || DEFAULT_MODEL); } catch (_) { setSelectedModel(DEFAULT_MODEL); }
    }
    // Switch sessionStore session scope: keeps cards/tasks belonging to the new session
    // (preserves live thinking cards from cross-client sessions like Flutter),
    // drops cards from other sessions to prevent leakage.
    if (sessionStore?.switchSession) sessionStore.switchSession(sid);
    else if (sessionStore?.reset) sessionStore.reset(sid);
    if (sid) {
      navigate(`/playground/chat/${sid}`);
    } else {
      navigate('/playground/chat');
    }
  }, [navigate, sessionStore, sessions, TERMINAL_STATUSES, fetchSessionDetail]);

  // ── Send message via Managed Agent runner ──
  const handleSend = useCallback(async (text, mediaUrls, sessionIdOverride, options = {}) => {
    // `resume` (#3490): a Continue on a stopped session — empty prompt, server authors the continue.
    const { resume = false } = options;
    // Prefer explicit sessionIdOverride (passed by card-reply handlers so the
    // follow-up dispatch lands on the card's OWN session) over the state
    // `selectedSessionId` which can lag behind rapid navigate() calls.
    const effectiveSessionId = sessionIdOverride || selectedSessionId;
    // Runner-selection hashtags (#exec_*) are backend routing directives, not user
    // content. Strip any from the displayed user bubble so the UI shows a clean message;
    // the full text still goes to dispatchExec (api-server resolves + strips the tag).
    const displayText = (text || '').replace(/\s*#exec_\w+\b/gi, '').replace(/\s{2,}/g, ' ').trim();
    // A resume carries no user text — don't push an empty user bubble.
    if (!resume) setLiveBlocks(prev => [...prev, {
      type: 'user', text: displayText, mediaUrls: mediaUrls || [], ts: Date.now(),
    }]);

    // Widget panel (task #4): fire the standalone suggest classifier in PARALLEL
    // with the turn — independent of the chat agent. Skip when this send IS a
    // widget action (a panel-tap text carries a #widget_ tag) or when there is
    // nothing to classify. Latest-wins via the request counter.
    const isWidgetActionSend = /#widget_[a-z]/i.test(text || '');
    if (!isWidgetActionSend && ((text || '').trim() || (mediaUrls && mediaUrls.length))) {
      const reqId = ++widgetSuggestReq.current;
      if (effectiveSessionId) {
        setWidgetSuggestMap(m => ({ ...m, [effectiveSessionId]: { ...(m[effectiveSessionId] || EMPTY_WIDGET_SUGGEST), loading: true } }));
      }
      api.suggestWidgets({
        sessionId: effectiveSessionId || undefined,
        prompt: text || '',
        imageUrl: firstImageHandle(mediaUrls),
      }).then((data) => {
        if (reqId !== widgetSuggestReq.current) return; // a newer send superseded this
        // Bind the result to the session this send belongs to — `effectiveSessionId`
        // when known, else the id minted during the send (read fresh at resolve
        // time). Remember the capture's media so a later panel tap re-attaches it.
        const sid = effectiveSessionId || selectedSessionIdRef.current;
        if (!sid) return;
        if (data.fallback_reason !== 'client_request_failed' && Array.isArray(data.recent)) {
          setRecentWidgets(data.recent); // global My Widgets
        }
        const fresh = Array.isArray(data.suggestions) ? data.suggestions : [];
        const classified = data.classified === true;
        setWidgetSuggestMap(m => ({ ...m, [sid]: {
          data: {
            suggestions: (classified || fresh.length)
              ? fresh : (m[sid]?.data?.suggestions || []),
            logged_in_session: data.fallback_reason === 'client_request_failed'
              ? (m[sid]?.data?.logged_in_session || [])
              : (data.logged_in_session || []),
          },
          attention: fresh.length > 0 ? true : (m[sid]?.attention || false),
          loading: false,
          mediaUrls: mediaUrls || [],
        } }));
      }).catch((err) => {
        if (reqId !== widgetSuggestReq.current) return;
        console.warn('suggestWidgets failed:', err);
        const sid = effectiveSessionId || selectedSessionIdRef.current;
        if (sid) setWidgetSuggestMap(m => (m[sid] ? { ...m, [sid]: { ...m[sid], loading: false } } : m));
      });
    }

    // If a task is actively running, inject input instead of dispatching new exec.
    // The input is buffered and applied at the next SDK turn boundary.
    const running = (() => {
      if (!sessionStore?.tasks) return null;
      const entries = Object.entries(sessionStore.tasks);
      for (let i = entries.length - 1; i >= 0; i--) {
        const [id, t] = entries[i];
        if (t.status === 'loading' || t.status === 'streaming') return { taskId: id, sessionId: t.sessionId };
      }
      return null;
    })();

    if (running && effectiveSessionId) {
      try {
        await api.injectInput(effectiveSessionId, running.taskId, text, mediaUrls);
      } catch (err) {
        console.error('injectInput failed:', err);
      }
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
      return;
    }

    setSending(true);
    try {
      const opts = {
        prompt: text,
        mediaUrls: mediaUrls?.length ? mediaUrls : undefined,
      };
      if (resume) opts.resume = true;
      if (effectiveSessionId) opts.sessionId = effectiveSessionId;
      // Rewind (#session-rewind): the durable truncate rides THIS dispatch —
      // the server drops the round with this `_rid` plus every round after it,
      // then runs the edited turn as the next turn. Only sent when the rewound
      // turn had a server round id; a live/legacy turn rewinds client-side only.
      const pendingRewind = rewindCutRef.current;
      if (pendingRewind?.rid && effectiveSessionId) opts.rewindToRid = pendingRewind.rid;
      // Pass the UI-selected model; `auto` resolves to undefined so the server
      // picks the admin-configured runner model (dispatchExec omits a falsy model).
      const resolved = resolveAutoModel(selectedModel);
      if (resolved) opts.model = resolved;
      // Render-scheme + reasoning-effort overrides — SESSION-BOUND, only meaningful when STARTING a
      // new session (no effectiveSessionId). An existing session keeps both bound server-side, so a
      // follow-up sends neither (the server rehydrates from the session context).
      if (!effectiveSessionId) {
        const resolvedScheme = resolveAutoScheme(selectedScheme);
        if (resolvedScheme) opts.renderScheme = resolvedScheme;
        // Worker backend — SESSION-BOUND like render_scheme; omitted when `auto` so the
        // server resolves it exactly as today (released clients never send it).
        const resolvedWorker = resolveAutoWorker(selectedWorker);
        if (resolvedWorker) opts.workerBackend = resolvedWorker;
        // Runner applies effort only to catalog reasoning models; "none" everywhere else.
        opts.reasoningEffort = resolveAutoEffort(
          effectiveModelId(selectedModel, selectedScheme), selectedEffort,
        );
      }
      // a2ui renderer capabilities (DEC-8) — the web is always capable, but we advertise it on every
      // send (create + follow-up): it is exactly what an external a2ui 1.0 client sends, and the
      // scheme resolution reads it on create. `options.a2uiAction` carries a component tap's v1
      // `follow_up` envelope back through the standard exec return channel.
      opts.a2ui = a2uiRendererCapabilities();
      if (options.a2uiAction) opts.a2uiAction = options.a2uiAction;
      const result = await api.dispatchExec(opts);
      // Rewind consumed: the edited turn is dispatched, so drop the local cut.
      // MUST clear here — the server recycles the round number, so a stale cut
      // key (`…-round-N`) would hide the freshly re-sent turn on the next
      // detail refresh. Left set on failure so a retry still carries the rid.
      if (pendingRewind) { rewindCutRef.current = null; setRewindCut(null); }
      // Set active session so sessionStore card events get scoped
      if (result?.sessionId && sessionStore?.setActiveSessionId) {
        const newSid = result.sessionId;
        sessionStore.setActiveSessionId(newSid);
        if (newSid !== selectedSessionId) {
          setSelectedSessionId(newSid);
          navigate(`/playground/chat/${newSid}`);
        }
      }
      // Refresh session list from API — the session exists in DB immediately.
      setTimeout(() => loadSessions(), 1000);
    } catch (err) {
      console.error('dispatchExec failed:', err);
      // Out of credits (the backend /exec 402-gates): the web playground has no purchase
      // modal, so surface a clear top-up notice instead of a raw error (the app shows UpsellSheet).
      const isCredits = err?.status === 402 || /insufficient|credit/i.test(err?.message || '');
      setLiveBlocks(prev => [...prev, {
        type: 'error',
        text: isCredits ? "You're out of credits — top up your balance to continue this session." : err.message,
        ts: Date.now(),
      }]);
    } finally {
      setSending(false);
    }
    setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
  }, [selectedSessionId, selectedModel, selectedScheme, selectedEffort, sessionStore, selectSession, navigate, loadSessions]);

  // a2ui return channel (DEC-8): a Button/chip tap on an a2ui_surface card posts a v1 `follow_up`
  // envelope via `vi-a2ui-action`. It rides the composer as a real exec turn — the envelope goes on
  // the exec body as `a2ui_action`, and the server maps its `userMessage` onto the turn's prompt.
  useEffect(() => {
    const onA2uiAction = (e) => {
      const detail = e?.detail || {};
      const envelope = detail.envelope;
      const userMessage = envelope?.action?.userMessage || '';
      const sid = detail.sessionId || selectedSessionId || undefined;
      if (!userMessage) return;
      handleSend(userMessage, undefined, sid, { a2uiAction: envelope });
    };
    window.addEventListener('vi-a2ui-action', onA2uiAction);
    return () => window.removeEventListener('vi-a2ui-action', onA2uiAction);
  }, [handleSend, selectedSessionId]);

  // One-shot: auto-send the `?ask=` opening prompt once the session view is
  // ready, then strip it from the URL. Keyed by sid+text so reloading the same
  // URL never re-sends, while a different ask still fires. Uses handleSend with
  // an explicit sessionIdOverride (same idiom as the vi-card-reply path), so it
  // lands on the route's session regardless of selection settling. This is the
  // web half of the `open_main_session` bridge — a button inside a widget's
  // HTML opens its main session AND runs an opening line.
  const askSentRef = useRef('');
  useEffect(() => {
    const ask = (askParam || '').trim();
    if (!ask || !urlSessionId || loading) return;
    if (selectedSessionId !== urlSessionId) return;
    const key = `${urlSessionId}:${ask}`;
    if (askSentRef.current === key) return;
    askSentRef.current = key;
    try { handleSend(ask, [], urlSessionId); } catch (err) { console.warn('[chat] auto-ask send failed:', err); }
    setSearchParams((p) => { const n = new URLSearchParams(p); n.delete('ask'); return n; }, { replace: true });
  }, [askParam, urlSessionId, loading, selectedSessionId, handleSend, setSearchParams]);

  // Listen for ask-user-question Submit replies bubbled up by TemplateCardPreview.
  // Submit is treated like the user typed the answer + the option's hashtags and
  // pressed Send. Options carry skill/widget hashtags (#skill_<slug>, #widget_*) and the
  // next turn routes accordingly. Runner selection is server-side (admin default), not
  // per-option — the legacy #exec_agent / #exec_fast routing tags are retired (#2795).
  //
  //   option   →  "<label> <hashtags space-joined>"  (e.g. "Build site #web-builder")
  //   custom   →  "<text>"
  // Multi-tab answers join with newlines.
  useEffect(() => {
    const onCardReply = (e) => {
      const detail = e?.detail || {};
      if (detail.template !== 'ask-user-question') return;
      const answers = detail.answers || {};

      // Action-bearing options short-circuit the prompt-send path and run
      // a client-side action instead. SKILL.md / runner closing-contract
      // tell the agent which actions exist; the host decides how each one
      // resolves on the surface it lives on.
      //
      // The `action_navigate_to_*` family is the standard host-navigation
      // mechanism — currently `action_navigate_to_widget/<widget_id>` for
      // returning to a widget content view after a capture turn. Any
      // future "go to X" affordance lives in this family too.
      for (const tabId of Object.keys(answers)) {
        const a = answers[tabId];
        if (!a || typeof a.action !== 'string' || !a.action) continue;
        const action = a.action;
        const navMatch = /^action_navigate_to_widget\/(.+)$/.exec(action);
        // Back-compat — drop after agents have rolled to the new contract.
        const isLegacyGoBack = action === 'go_back_to_widget';
        if (navMatch || isLegacyGoBack) {
          // Optimistic answered-state write so the card freezes in its
          // picked state instead of waiting for an SSE echo.
          const replyCardId = detail.cardId;
          const cardSessionId = detail.sessionId || selectedSessionId;
          const cardState = sessionStore?.cards?.[replyCardId];
          if (replyCardId && cardSessionId && cardState?.template) {
            const fullData = { ...(cardState.data || {}), status: 'answered', answers };
            sessionStore.processEvent?.({
              op: 'replace_card',
              cardId: replyCardId,
              sessionId: cardSessionId,
              template: cardState.template,
              data: fullData,
            });
            sessionStore.processEvent?.({
              op: 'finalize_card',
              cardId: replyCardId,
              sessionId: cardSessionId,
            });
            api.updateSessionCard(cardSessionId, replyCardId, cardState.template, fullData)
              .catch((err) => console.warn('[vi-card-reply:action] persist failed:', err));
          }
          // Resolve target widget from the session context (the session
          // already knows which widget fired this capture). Land on the
          // studio page in User Data mode — the runtime that replaced the
          // removed Widget Use surface.
          const sess = sessionStore?.sessions?.[cardSessionId];
          const ctx = sess?.context || sess?.exec_context || {};
          const wuc = ctx.widget_use_contract || {};
          const targetWidgetId = navMatch ? navMatch[1] : (wuc.widget_id || ctx.widget_id || null);
          if (targetWidgetId) {
            navigate(`/playground/widget/studio/${targetWidgetId}?data=user`);
          } else {
            navigate('/playground/widget/studio');
          }
          return;
        }
      }

      const parts = [];
      for (const tabId of Object.keys(answers)) {
        const a = answers[tabId];
        if (!a) continue;
        if (a.kind === 'option') {
          const label = a.label || a.optionId || '';
          const tags = Array.isArray(a.hashtags) && a.hashtags.length > 0
            ? a.hashtags.filter((h) => typeof h === 'string' && h.startsWith('#')).join(' ')
            : '';
          parts.push(tags ? `${label} ${tags}` : label);
        } else if (a.kind === 'custom') {
          parts.push(a.text);
        }
      }
      const text = parts.filter(Boolean).join('\n');
      if (!text) return;
      // Pass the card's OWN sessionId so the follow-up dispatch lands on
      // the right session — guards against stale selectedSessionId after
      // a rapid navigate(). Falls back to current state if the card
      // pre-dates the sessionId plumbing.
      const cardSessionId = detail.sessionId || selectedSessionId;
      try { handleSend(text, [], cardSessionId); } catch (err) { console.warn('vi-card-reply send failed:', err); }

      // Persist the answered state. Optimistic local write first so the UI
      // flips to the highlighted/answered state immediately — no wait for the
      // SSE echo of the POST (which is where the pre-#1291-refactor highlight
      // bug lived). The POST still runs in background for DB persistence so
      // the share-URL and page-reload paths see the answered state.
      const replyCardId = detail.cardId;
      const cardState = sessionStore?.cards?.[replyCardId];
      if (replyCardId && cardSessionId && cardState?.template) {
        const fullData = { ...(cardState.data || {}), status: 'answered', answers };

        // 1. Local optimistic update — iframe re-mounts once with the answered
        //    snapshot; the answered-branch restore path in the template HTML
        //    re-applies .auq-selected from `answers`.
        sessionStore.processEvent?.({
          op: 'replace_card',
          cardId: replyCardId,
          sessionId: cardSessionId,
          template: cardState.template,
          data: fullData,
        });
        sessionStore.processEvent?.({
          op: 'finalize_card',
          cardId: replyCardId,
          sessionId: cardSessionId,
        });

        // 2. Persist to server (fire-and-forget). When its SSE echo arrives,
        //    useSessionStore's replace_card handler is idempotent — no flicker.
        api.updateSessionCard(cardSessionId, replyCardId, cardState.template, fullData)
          .catch((err) => console.warn('[vi-card-reply] card update failed:', err));
      }
    };
    window.addEventListener('vi-card-reply', onCardReply);
    return () => window.removeEventListener('vi-card-reply', onCardReply);
  }, [handleSend, selectedSessionId, navigate, sessionStore]);

  // Native session blocks report host verbs through `vi-card-action`.
  // Playground must honour the same Open / Save contract as App.jsx; merely
  // dispatching the event leaves both controls visibly clickable but inert.
  useEffect(() => {
    const onCardAction = (event) => {
      handlePlaygroundCardAction(event?.detail, { fallbackSessionId: selectedSessionId });
    };
    window.addEventListener('vi-card-action', onCardAction);
    return () => window.removeEventListener('vi-card-action', onCardAction);
  }, [selectedSessionId]);

  // A LogCard verb landed (log/cancel committed server-side) → refresh the
  // widget panel for that session so its Logged section flips pending → ✓
  // immediately, not on the next round. (LogCard dispatches the event after a
  // successful host write — same window-event contract as vi-card-reply.)
  useEffect(() => {
    const onLogChanged = (e) => {
      const sid = e?.detail?.sessionId;
      if (sid) applySilentSuggest(sid);
    };
    window.addEventListener('vi-widget-log-changed', onLogChanged);
    return () => window.removeEventListener('vi-widget-log-changed', onLogChanged);
  }, [applySilentSuggest]);

  // ── Execution state — detect running tasks for stop button ──
  const activeTask = useMemo(() => {
    if (!sessionStore?.tasks) return null;
    const entries = Object.entries(sessionStore.tasks);
    // Include 'queued' + 'error' in addition to 'loading'/'streaming' so
    // Stop stays reachable when a task is waiting to run or already
    // failed. Especially important on errors: if FAST aborts → SDK
    // hand-off also fails, the server may keep the task row alive and
    // the user needs a way to explicitly cancel without reloading. Also
    // catches tasks stuck queued behind a failed dispatch.
    for (let i = entries.length - 1; i >= 0; i--) {
      const [id, t] = entries[i];
      if (t.status === 'loading' || t.status === 'streaming' ||
          t.status === 'queued' || t.status === 'error') {
        return { taskId: id, sessionId: t.sessionId, status: t.status };
      }
    }
    return null;
  }, [sessionStore?.tasks]);

  // EVERY conversation round: when the active task finishes (running → idle), fire
  // a SILENT history-aware refresh so the panel lazily stays current (incl.
  // anything the agent just logged). Mirrors the iPhone busy→idle trigger.
  const prevActiveTaskRef = useRef(null);
  useEffect(() => {
    const had = prevActiveTaskRef.current;
    prevActiveTaskRef.current = activeTask;
    if (had && !activeTask && selectedSessionId) applySilentSuggest(selectedSessionId);
  }, [activeTask, selectedSessionId, applySilentSuggest]);

  const handleStop = useCallback(async () => {
    if (!activeTask) return;
    try {
      await api.stopSession(activeTask.sessionId || selectedSessionId, activeTask.taskId);
    } catch (err) {
      console.error('stopSession failed:', err);
    }
    // Locally mark the task as 'stopped' so the UI clears its spinner /
    // processing pill immediately even if the server's stop signal
    // doesn't round-trip a `exec_stopped` event (e.g., FAST already
    // finished erroring; manager.abortTask is a no-op). Without this,
    // tasks stuck in 'error' would stay showing a Stop button that
    // never goes away.
    sessionStore?.markTaskStopped?.(activeTask.taskId);
  }, [activeTask, selectedSessionId, sessionStore]);

  // ── Stream live cards from sessionStore (append new, update existing, keep all) ──
  const seenCardIdsRef = useRef(new Set());
  useEffect(() => {
    if (!sessionStore?.cards) return;
    const orderedIds = sessionStore.orderedCardIds || [];
    const newBlocks = [];
    for (const cardId of orderedIds) {
      const card = sessionStore.cards[cardId];
      if (!card) continue;
      if (!seenCardIdsRef.current.has(cardId)) {
        seenCardIdsRef.current.add(cardId);
        if (!(typeof card.createdAt === 'number' && card.createdAt > 0)) {
          console.warn(
            `[Playground:ts] live card cardId=${cardId} template=${card.template} ` +
            `has no createdAt — falling back to Date.now() (will display as now). ` +
            `Upstream store failed to resolve a WAL timestamp; check useSessionStore.js logs.`,
          );
        }
        const blockTs = (typeof card.createdAt === 'number' && card.createdAt > 0) ? card.createdAt : Date.now();
        newBlocks.push({ type: 'card', card: { cardId, ...card }, ts: blockTs });
      }
    }
    // Append new cards (don't replace existing ones — preserves thinking cards + order)
    if (newBlocks.length > 0) {
      setLiveBlocks(prev => [...prev, ...newBlocks]);
    }
    // Update existing card data in-place (for streaming updates).
    // Also refresh ts if the card's createdAt was corrected by a WAL
    // merge (e.g. eager thinking card overwritten by authoritative ts).
    setLiveBlocks(prev => prev.map(b => {
      if (b.type !== 'card') return b;
      const updated = sessionStore.cards[b.card.cardId];
      if (!updated) return b;
      const newTs = (typeof updated.createdAt === 'number' && updated.createdAt > 0) ? updated.createdAt : b.ts;
      return { ...b, ts: newTs, card: { ...b.card, ...updated } };
    }));
  }, [sessionStore?.cards, sessionStore?.orderedCardIds]);

  // ── Rewind (#session-rewind) ──────────────────────────────────────────────
  // Copy this user turn's text. Stays synchronous: the clipboard write must
  // happen in the same task as the click or iOS Safari drops it.
  const handleCopyTurn = useCallback((msg) => copyWithFallback(msg?.text || ''), []);

  // Rewind to a user turn: hide it and everything below, and put its input
  // (text + media, images and video alike) back in the composer. LOCAL ONLY —
  // no request is made; abandoning the edit leaves the session untouched and a
  // reload re-hydrates the full transcript. The truncate rides the next send.
  const handleRewind = useCallback(async (msg) => {
    if (!msg?.key) return;
    const tappedAt = Date.now();
    setRewindCut({ key: msg.key, rid: msg.rid || null, tappedAt });

    // The historical half of the transcript is filtered inside UnifiedTimeline's
    // memo, but `liveBlocks` is independent state — physically drop the cut
    // ones, and un-see their cards. Without the `seenCardIdsRef` prune the
    // card-append effect treats them as already-appended and never re-adds them
    // if the session later replays.
    setLiveBlocks(prev => {
      const kept = prev.filter(b => {
        if ((b.ts || 0) < (msg.ts || 0)) return true;
        if (b.type === 'card' && b.card?.cardId) seenCardIdsRef.current.delete(b.card.cardId);
        return false;
      });
      return kept.length === prev.length ? prev : kept;
    });

    // Refill the composer. The playground reads sessions with `?format=signed`,
    // whose GCS signatures expire ~1h, so re-fetch the detail first and prefill
    // from the FRESH round when we can resolve it — a stale signature would
    // render a broken thumbnail in the composer strip. (Persistence is safe
    // either way: the server canonicalizes any URL shape back to `media://`.)
    let mediaUrls = msg.mediaUrls || [];
    if (mediaUrls.length && selectedSessionId) {
      try {
        const fresh = await fetchSessionDetail(selectedSessionId, { force: true });
        const round = (fresh?.timeline || []).find(r => r && msg.rid && r._rid === msg.rid);
        const freshUrls = round ? roundMediaUrls(round) : [];
        if (freshUrls.length) mediaUrls = freshUrls;
      } catch (err) {
        console.warn('[rewind] detail refresh failed, using cached media URLs:', err?.message || err);
      }
    }
    setComposerPrefill({ text: msg.text || '', mediaUrls, tappedAt });
  }, [selectedSessionId, fetchSessionDetail]);

  // Clear live blocks when switching between sessions (not on initial assignment).
  // "null → sid" (new chat dispatch) keeps blocks; "sidA → sidB" clears them.
  const prevSessionIdRef = useRef(null);
  useEffect(() => {
    const prev = prevSessionIdRef.current;
    prevSessionIdRef.current = selectedSessionId;
    if (prev && prev !== selectedSessionId) {
      setLiveBlocks([]);
      if (seenCardIdsRef?.current) seenCardIdsRef.current.clear();
      // A cut belongs to the session it was taken in.
      setRewindCut(null);
    }
  }, [selectedSessionId]);

  const selectedSession = sessions.find(s => s.sessionId === selectedSessionId);
  const selectedSessionDetail = selectedSessionId ? sessionDetailCache[selectedSessionId] : null;
  const selectedSessionFromDetail = useMemo(
    () => sessionDetailToPlaygroundSession(selectedSessionDetail, selectedSessionId),
    [selectedSessionDetail, selectedSessionId],
  );
  const selectedSessionFromRoute = useMemo(() => {
    if (!selectedSessionId) return null;
    return {
      sessionId: selectedSessionId,
      tasks: [],
      latestTs: 0,
      status: 'unknown',
      title: null,
      metadata: null,
      result_html: '',
      result_summary: '',
      context: null,
      result: null,
      timeline: [],
      artifacts: [],
    };
  }, [selectedSessionId]);
  const selectedSessionForView = selectedSession || selectedSessionFromDetail || selectedSessionFromRoute;

  // Merge sidebar summary with detail cache to get full session data.
  // For terminal sessions: detail comes from GET /sessions/{id} (cached).
  // For active sessions: SSE replays cards live, detail not needed.
  const selectedSessionFull = useMemo(() => {
    if (!selectedSessionForView) return null;
    const detail = sessionDetailCache[selectedSessionForView.sessionId];
    if (!detail) return selectedSessionForView; // not yet fetched — sidebar data only
    // Merge: sidebar fields + detail fields (detail has result, timeline, artifacts)
    return {
      ...selectedSessionForView,
      result: detail.result || null,
      timeline: detail.timeline || [],
      artifacts: detail.artifacts || [],
      context: detail.context || selectedSessionForView.context,
      // Issue #1459: prefer detail.metadata when present — sidebar's summary
      // projection is also authoritative, but detail is the freshest snapshot.
      metadata: detail.metadata || selectedSessionForView.metadata || null,
    };
  }, [selectedSessionForView, sessionDetailCache]);

  // URL-loaded sessions must remain actionable even if the sidebar list fails
  // or has not loaded yet. Fetch the owner snapshot directly from the route id.
  useEffect(() => {
    if (!selectedSessionId || selectedSession || sessionDetailCache[selectedSessionId]) return;
    fetchSessionDetail(selectedSessionId);
  }, [selectedSessionId, selectedSession, sessionDetailCache, fetchSessionDetail]);

  // Auto-fetch detail. Default (auto): only terminal sessions. Manual override
  // (get / get+sse): fetch regardless of status. SSE-only: skip entirely.
  useEffect(() => {
    if (!selectedSessionId || !selectedSessionForView) return;
    if (!shouldFetchGet(fetchMode, selectedSessionForView.status, TERMINAL_STATUSES)) return;
    if (!sessionDetailCache[selectedSessionId]) {
      fetchSessionDetail(selectedSessionId);
    }
  }, [selectedSessionId, selectedSessionForView, TERMINAL_STATUSES, sessionDetailCache, fetchSessionDetail, fetchMode]);

  // On fetchMode change, invalidate cached snapshot + clear SSE-derived state
  // for the current session so each mode renders from a fresh fetch/replay.
  // Dev-tool ergonomics: switching to SSE mid-session replays WAL from scratch.
  useEffect(() => {
    if (!selectedSessionId) return;
    setSessionDetailCache(prev => {
      if (!prev[selectedSessionId]) return prev;
      const next = { ...prev };
      delete next[selectedSessionId];
      return next;
    });
    setLiveBlocks([]);
    if (seenCardIdsRef?.current) seenCardIdsRef.current.clear();
    if (sessionStore?.reset) sessionStore.reset(selectedSessionId);
    else if (sessionStore?.switchSession) sessionStore.switchSession(selectedSessionId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchMode]);

  // Derive session content from API session data — ONLY for completed sessions.
  // If we have live blocks (user is actively chatting), skip historical data to avoid
  // dual-source conflicts (different timestamps, ordering issues, duplication).
  const [sessionTasks, setSessionTasks] = useState([]);
  useEffect(() => {
    if (!selectedSessionId || !selectedSessionFull) { setSessionTasks([]); return; }
    const s = selectedSessionFull;
    const ts = s.latestTs || 0;
    // SSE-only mode: the live store fills cards as events arrive, so the
    // legacy `task.result = result_summary` fallback would duplicate the
    // agent's raw token stream (incl. closing-card prediction tag) on
    // top of the live cards. Suppress only that fallback; the user
    // prompt + photo still need to come from the sidebar projection.
    const suppressResultFallback = fetchMode === 'sse';

    // Timeline-based rendering: one task per round
    if (s.timeline && s.timeline.length > 0) {
      // Compute per-round boundaries to filter accumulated cards.
      // The API accumulates ALL cards across rounds (round N has cards from rounds 1..N).
      // Use previous round's completion ts as lower bound to isolate this round's cards.
      const roundBoundaries = s.timeline.map((r, i) => {
        if (i === 0) return 0;
        const prevTs = s.timeline[i - 1].ts ? new Date(s.timeline[i - 1].ts).getTime() : 0;
        return prevTs;
      });

      // Issue #1338: result.cards is the single source of truth. Timeline
      // rounds store only {cardId: {ts}} refs; resolve each ref against
      // result.cards by id. Legacy sessions still carry full card objects
      // inline (detected via presence of `template`) — use those in place.
      const resultCardsStore = (s.result && s.result.cards) ? s.result.cards : {};

      const tasks = s.timeline.map((round, idx) => {
        const roundTs = round.ts ? new Date(round.ts).getTime() : (ts + idx);
        const requestTs = round.request_ts ? new Date(round.request_ts).getTime() : null;
        const lowerBound = roundBoundaries[idx]; // cards before this belong to earlier rounds

        // Collect cards — filter out accumulated cards from previous rounds
        const cardEntries = round.cards ? Object.entries(round.cards) : [];
        const parsedCards = {};
        let earliestCardTs = roundTs;
        for (const [id, entry] of cardEntries) {
          // Resolve ref vs legacy full card. Refs carry only {ts}; legacy
          // entries carry template/data/status.
          const isLegacyFullCard = entry && typeof entry === 'object' && 'template' in entry;
          const full = isLegacyFullCard ? entry : resultCardsStore[id];
          if (!full) continue; // ref with no resolution — skip
          const rawTs = (entry && entry.ts) || full.ts;
          const cardTs = rawTs ? (typeof rawTs === 'string' ? new Date(rawTs).getTime() : rawTs) : roundTs;
          // Skip cards that belong to a previous round (ts before this round's boundary)
          if (lowerBound > 0 && cardTs < lowerBound) continue;
          parsedCards[id] = { ...full, status: full.status || 'finalized', ts: cardTs };
          if (cardTs < earliestCardTs) earliestCardTs = cardTs;
        }
        // Prompt timestamp priority: request_ts > earliest card - 1 > round completion
        const promptTs = requestTs || (Object.keys(parsedCards).length > 0 ? earliestCardTs - 1 : roundTs);

        const task = {
          taskId: `${s.sessionId}-round-${round.round || idx + 1}`,
          sessionId: s.sessionId,
          // Server round id (`exec_session_bridge` writes `_rid = task_id`, or
          // `f"{task_id}:{i}"` for a multi-round harvest). This is the value
          // `rewind_to_rid` carries on re-send. Legacy rounds have none.
          rid: round._rid || null,
          prompt: round.prompt || '',
          skillSlug: s.tasks[0]?.skillSlug || null,
          ts: promptTs,
          requestTs: promptTs,
          mediaUrls: roundMediaUrls(round),
          cards: parsedCards,
          result: null,
          durationMs: null,
        };
        // If no cards, show summary as text result — but suppress in
        // SSE mode where the live store already renders the synthetic
        // raw-speech card.
        if (!hasContentCards(task.cards) && round.summary && !suppressResultFallback) {
          task.result = round.summary;
        }
        return task;
      });
      setSessionTasks(tasks);
      return;
    }

    // Legacy flat rendering (single-round or no timeline)
    const contextPhotos = s.context?.photos || [];
    const mediaUrls = contextPhotos.map(p => typeof p === 'string' ? p : p?.url).filter(Boolean);

    // Collect card timestamps first to place prompt before earliest card
    const legacyCards = {};
    let legacyEarliestCardTs = ts;
    if (s.result?.cards) {
      for (const [id, card] of Object.entries(s.result.cards)) {
        const cardTs = card.ts ? (typeof card.ts === 'string' ? new Date(card.ts).getTime() : card.ts) : ts;
        legacyCards[id] = { ...card, status: card.status || 'finalized', ts: cardTs };
        if (cardTs < legacyEarliestCardTs) legacyEarliestCardTs = cardTs;
      }
    }
    const legacyPromptTs = Object.keys(legacyCards).length > 0 ? legacyEarliestCardTs - 1 : ts;

    const syntheticTask = {
      taskId: s.sessionId,
      sessionId: s.sessionId,
      prompt: s.tasks[0]?.prompt || s.title || '',
      skillSlug: s.tasks[0]?.skillSlug || null,
      ts: legacyPromptTs,
      requestTs: legacyPromptTs,
      mediaUrls,
      cards: legacyCards,
      result: null,
      durationMs: null,
    };
    // If result_html exists and no cards, create a freeform-html card
    if (s.result_html && !hasContentCards(syntheticTask.cards)) {
      syntheticTask.cards['result-html'] = {
        template: 'freeform-html',
        data: { html: s.result_html },
        status: 'finalized',
        ts,
      };
    }
    // If no cards at all, use result_summary as text result — but
    // suppress in SSE mode (live store has the raw-speech card).
    if (!hasContentCards(syntheticTask.cards) && s.result_summary && !suppressResultFallback) {
      syntheticTask.result = s.result_summary;
    }
    setSessionTasks([syntheticTask]);
  }, [selectedSessionId, selectedSessionFull, fetchMode]);

  const { allCards, cardTaskMap, sortedTasks } = sessionTasks.length > 0
    ? deduplicateCards(sessionTasks)
    : { allCards: {}, cardTaskMap: {}, sortedTasks: [] };

  // #1458 — sessions filtered/sorted per the status-filter dropdown.
  const visibleSessions = useMemo(
    () => filterSessions(sessions, statusFilter),
    [sessions, statusFilter],
  );

  return (
    <div className="pg-chat-root" style={{ display: 'flex', width: '100%', height: '100%' }}>
      {/* ── Session Sidebar (chat-sidebar) ── */}
      <div className="pg-chat-sidebar" style={{
        width: sessionListCollapsed ? 24 : 220,
        background: V.bgCard,
        display: 'flex', flexDirection: 'column', flexShrink: 0,
        overflow: 'hidden',
        transition: 'width 0.15s ease',
      }}>
        {sessionListCollapsed ? (
          <div style={{
            flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
            writingMode: 'vertical-rl', transform: 'rotate(180deg)',
            fontSize: 10, fontWeight: 600, letterSpacing: '0.15em',
            textTransform: 'uppercase', color: V.textDim, userSelect: 'none',
          }}>Sessions</div>
        ) : (<>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 12px', borderBottom: `1px solid ${V.border}`,
        }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: V.text }}>Sessions</span>
          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            <SessionListKebabMenu onDeleteAll={async () => {
              if (!window.confirm('Delete ALL your sessions? This soft-deletes every session you own. Cannot be undone from the UI.')) return;
              try {
                const r = await api.authFetch('/api/users/sessions/mine', { method: 'DELETE' });
                if (!r.ok) throw new Error(`HTTP ${r.status}`);
                selectSession(null); setLiveBlocks([]); seenCardIdsRef.current.clear();
                await loadSessions();
              } catch (e) {
                alert(`Delete failed: ${e?.message || e}`);
              }
            }} />
            <button onClick={() => { selectSession(null); setLiveBlocks([]); seenCardIdsRef.current.clear(); if (sessionStore?.reset) sessionStore.reset(); }} style={{
              background: V.accent, border: 'none', borderRadius: 4,
              color: '#fff', cursor: 'pointer', fontSize: 13, padding: '2px 8px', fontWeight: 600,
            }} title="New session">+</button>
            <button onClick={loadSessions} style={{
              background: 'none', border: `1px solid ${V.border}`, borderRadius: 4,
              color: V.textDim, cursor: 'pointer', fontSize: 13, padding: '2px 6px',
            }} title="Refresh">↻</button>
          </div>
        </div>

        {/* #1458 — Status filter */}
        <div style={{ padding: '8px 12px', borderBottom: `1px solid ${V.border}` }}>
          <select
            aria-label="Filter sessions by status"
            value={statusFilter}
            onChange={e => onChangeStatusFilter(e.target.value)}
            style={{
              width: '100%', background: V.bg, color: V.text,
              border: `1px solid ${V.border}`, borderRadius: 4,
              padding: '4px 6px', fontSize: 11, outline: 'none',
              fontFamily: V.fontUi,
            }}
          >
            {STATUS_FILTER_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        {/* Session list */}
        <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
          {loading && <div style={{ padding: 16, fontSize: 12, color: V.textDim }}>Loading...</div>}
          {error && <div style={{ padding: 16, fontSize: 12, color: V.red }}>Error: {error}</div>}
          {!loading && !error && sessions.length === 0 && (
            <div style={{ padding: 16, fontSize: 12, color: V.textDim }}>No sessions found</div>
          )}
          {visibleSessions.map(session => {
            const isActive = session.sessionId === selectedSessionId;
            const firstPrompt = session.tasks[0]?.prompt || '(no prompt)';
            const firstSkill = session.tasks[0]?.skillSlug;
            const statusBucket = getStatusBucket(session.status);
            const statusColor = STATUS_COLORS[statusBucket];
            // Derive hero image from ``context.photos`` (first entry).
            // ``summary=true`` returns the full ``context`` so we don't
            // need a separate derived ``thumbnail_url`` field.
            const photos = session.context?.photos;
            let thumb = null;
            if (Array.isArray(photos) && photos.length > 0) {
              const p = photos[0];
              const raw = typeof p === 'string' ? p : (p?.url || null);
              thumb = raw ? normalizeMediaUrl(raw) : null;
            }
            return (
              <div
                key={session.sessionId}
                onClick={() => selectSession(session.sessionId)}
                style={{
                  padding: '8px 12px 8px 9px', cursor: 'pointer',
                  borderLeft: `3px solid ${statusColor}`,
                  borderBottom: `1px solid ${V.border}`,
                  background: isActive ? V.accentDim : 'transparent',
                  transition: 'background 0.1s',
                  display: 'flex', alignItems: 'flex-start', gap: 8,
                }}
                onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = V.bgHover; }}
                onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = isActive ? V.accentDim : 'transparent'; }}
              >
                {thumb ? (
                  <img
                    src={thumb}
                    alt=""
                    style={{
                      width: 40, height: 40, borderRadius: 6,
                      objectFit: 'cover', flexShrink: 0,
                      background: V.bgHover,
                    }}
                    onError={e => { e.target.style.display = 'none'; }}
                  />
                ) : null}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap', color: isActive ? V.accent : V.text,
                  }}>
                    {firstPrompt}
                  </div>
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    marginTop: 3, fontSize: 10, color: V.textDim,
                  }}>
                    {firstSkill && (
                      <span style={{
                        background: 'rgba(175,82,222,0.12)', color: V.purple,
                        padding: '0 5px', borderRadius: 3, fontSize: 10,
                      }}>
                        {formatSkillTag(firstSkill)}
                      </span>
                    )}
                    <span>{session.tasks.length} task{session.tasks.length > 1 ? 's' : ''}</span>
                    <span>{formatTime(session.latestTs)}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Session info panel */}
        <div style={{
          minHeight: 180, maxHeight: usageOpen ? 400 : 180, borderTop: `1px solid ${V.border}`, overflowY: 'auto',
          padding: '10px 12px', fontSize: 12, flexShrink: 0, color: V.text,
          transition: 'max-height 0.2s',
        }}>
          {!selectedSessionForView ? (
            <>
              <div style={{ color: V.textDim, fontSize: 11, padding: '8px 0', textAlign: 'center' }}>
                Select a session for details
              </div>
            </>
          ) : (
            <>
              <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em', color: V.textDim, marginBottom: 2 }}>
                Session ID
              </div>
              <div style={{ wordBreak: 'break-all', fontSize: 12 }}>
                {selectedSessionForView.sessionId}
              </div>
              <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em', color: V.textDim, marginTop: 8, marginBottom: 2 }}>
                Tasks ({selectedSessionForView.tasks.length})
              </div>
              {selectedSessionForView.tasks.map((t, i) => (
                <div key={i} style={{
                  padding: '4px 0', borderBottom: `1px solid ${V.border}`,
                  fontSize: 11, color: V.textDim,
                }}>
                  {(t.prompt || '').slice(0, 40)}{(t.prompt || '').length > 40 ? '…' : ''}
                  {t.durationMs ? ` (${formatDuration(t.durationMs)})` : ''}
                </div>
              ))}
              {/* Usage details — opens a modal */}
              <button
                onClick={async () => {
                  setUsageOpen(true);
                  setUsageLoading(true);
                  try {
                    const data = await api.request(`/api/users/me/usage/sessions/${selectedSessionForView.sessionId}?page_size=100`);
                    setUsageData(data);
                  } catch { setUsageData(null); }
                  setUsageLoading(false);
                }}
                style={{
                  marginTop: 10, width: '100%', padding: '6px 0', border: `1px solid ${V.border}`,
                  borderRadius: 6, background: 'transparent',
                  color: V.text, fontSize: 11, fontWeight: 500,
                  cursor: 'pointer', transition: 'all 0.15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = V.accent; e.currentTarget.style.color = V.accent; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = V.border; e.currentTarget.style.color = V.text; }}
              >
                View Usage
              </button>
              {/* Usage Modal */}
              {usageOpen && (
                <div style={{
                  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                  background: 'rgba(0,0,0,0.5)', zIndex: 9999,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }} onClick={() => setUsageOpen(false)}>
                  <div style={{
                    background: V.bgCard, borderRadius: 12, padding: 24, width: '90%', maxWidth: 800, maxHeight: '80vh',
                    overflow: 'auto', border: `1px solid ${V.border}`,
                  }} onClick={e => e.stopPropagation()}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                      <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Session Usage</h3>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <button onClick={async () => {
                          const usageSessionId = selectedSessionForView?.sessionId;
                          if (!usageSessionId) return;
                          setUsageLoading(true);
                          try {
                            const data = await api.request(`/api/users/me/usage/sessions/${usageSessionId}?page_size=100`);
                            setUsageData(data);
                          } catch { /* ignore */ }
                          setUsageLoading(false);
                        }} style={{ background: 'none', border: `1px solid ${V.border}`, borderRadius: 4, color: V.textDim, cursor: 'pointer', fontSize: 11, padding: '2px 8px' }}>Refresh</button>
                        <button onClick={() => setUsageOpen(false)} style={{ background: 'none', border: 'none', color: V.textDim, cursor: 'pointer', fontSize: 18, lineHeight: 1 }}>×</button>
                      </div>
                    </div>
                    {usageLoading && <div style={{ color: V.textDim }}>Loading...</div>}
                    {usageData && !usageLoading && (
                      <>
                        <div style={{ fontSize: 13, marginBottom: 16 }}>
                          <span style={{ fontWeight: 600, fontSize: 20 }}>${Number(usageData.total_cost || 0).toFixed(4)}</span>
                          <span style={{ color: V.textDim, marginLeft: 12 }}>{usageData.billing_count} billing items · {usageData.tool_count} tool calls</span>
                        </div>
                        {/* Per-model composition of the total above — derived CLIENT-SIDE from the
                            same billing_items the Cost Breakdown tab lists (no API change). For
                            `model`/`runtime` rows `name` IS the model id, so grouping by it answers
                            "which model did this session's dollars go to" without opening a tab. */}
                        {(() => {
                          const rows = Object.values(
                            (usageData.billing_items || [])
                              .filter(b => b.kind === 'model' || b.kind === 'runtime')
                              .reduce((acc, b) => {
                                const name = b.name || '(unnamed)';
                                const g = acc[name] || (acc[name] = { name, calls: 0, tokens: 0, cost: 0 });
                                g.calls += 1;
                                g.tokens += Number(b.quantity || 0);
                                g.cost += Number(b.cost || 0);
                                return acc;
                              }, {})
                          ).sort((a, b) => b.cost - a.cost);
                          if (!rows.length) {
                            return <div style={{ color: V.textDim, fontSize: 12, marginBottom: 16 }}>No model spend</div>;
                          }
                          return (
                            <div style={{ overflowX: 'auto', marginBottom: 16 }}>
                              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                                <thead>
                                  <tr style={{ color: V.textDim, borderBottom: `2px solid ${V.border}` }}>
                                    <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 600 }}>Model</th>
                                    <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 600 }}>Calls</th>
                                    <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 600 }}>Tokens</th>
                                    <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 600 }}>Cost</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {rows.map(r => (
                                    <tr key={r.name} style={{ borderBottom: `1px solid ${V.border}33` }}>
                                      <td style={{ padding: '4px 8px', whiteSpace: 'nowrap' }}>{r.name}</td>
                                      <td style={{ padding: '4px 8px', textAlign: 'right', color: V.textDim }}>{r.calls}</td>
                                      <td style={{ padding: '4px 8px', textAlign: 'right', color: V.textDim }}>{r.tokens.toLocaleString()}</td>
                                      <td style={{ padding: '4px 8px', textAlign: 'right', fontWeight: 600 }}>${r.cost.toFixed(4)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          );
                        })()}
                        {/* Tabs: Cost Breakdown (default) | MCP Tool Calls */}
                        <div style={{ display: 'flex', gap: 4, marginBottom: 12, borderBottom: `1px solid ${V.border}` }}>
                          {[['billing', `Cost Breakdown (${usageData.billing_count})`], ['tools', `MCP Tool Calls (${usageData.tool_count})`]].map(([k, label]) => (
                            <button key={k} onClick={() => setUsageTab(k)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '6px 12px', fontSize: 12, fontWeight: 600, color: usageTab === k ? V.text : V.textDim, borderBottom: usageTab === k ? '2px solid #6366f1' : '2px solid transparent', marginBottom: -1 }}>{label}</button>
                          ))}
                        </div>
                        {usageTab === 'billing' && (
                        <div style={{ overflowX: 'auto' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                            <thead>
                              <tr style={{ color: V.textDim, borderBottom: `2px solid ${V.border}` }}>
                                <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 600 }}>Name</th>
                                <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 600 }}>Type</th>
                                <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 600 }}>Quantity</th>
                                <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 600 }}>Cost</th>
                                <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 600 }}>User Cost</th>
                                <th style={{ textAlign: 'center', padding: '6px 8px', fontWeight: 600 }}>Status</th>
                                <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 600 }}>Time</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(usageData.billing_items || []).map((b, i) => (
                                <tr key={i} style={{ borderBottom: `1px solid ${V.border}33` }}>
                                  <td style={{ padding: '4px 8px', whiteSpace: 'nowrap' }}>{b.name || '-'}</td>
                                  <td style={{ padding: '4px 8px', color: V.textDim }}>{b.kind === 'model' ? 'Model' : b.kind === 'runtime' ? 'Runtime' : b.kind === 'service' ? 'Service' : b.kind === 'mcp' ? 'MCP' : b.kind}</td>
                                  <td style={{ padding: '4px 8px', textAlign: 'right', color: V.textDim }}>{b.quantity != null ? `${Number(b.quantity).toLocaleString()} ${b.unit || ''}`.trim() : '-'}</td>
                                  <td style={{ padding: '4px 8px', textAlign: 'right', color: V.textDim }}>${Number(b.raw_cost || 0).toFixed(6)}</td>
                                  <td style={{ padding: '4px 8px', textAlign: 'right', fontWeight: 600 }}>${Number(b.cost || 0).toFixed(6)}</td>
                                  <td style={{ padding: '4px 8px', textAlign: 'center' }}>{b.status === 'success' ? <span style={{ color: '#22c55e' }}>ok</span> : <span style={{ color: '#ef4444' }}>{b.status || '-'}</span>}</td>
                                  <td style={{ padding: '4px 8px', whiteSpace: 'nowrap', fontSize: 11, color: V.textDim }}>{b.occurred_at ? new Date(b.occurred_at).toLocaleString('zh-CN') : '-'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        )}
                        {usageTab === 'tools' && (
                        <div style={{ overflowX: 'auto' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                            <thead>
                              <tr style={{ color: V.textDim, borderBottom: `2px solid ${V.border}` }}>
                                <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 600 }}>Tool</th>
                                <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 600 }}>Task ID</th>
                                <th style={{ textAlign: 'center', padding: '6px 8px', fontWeight: 600 }}>Status</th>
                                <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 600 }}>Latency</th>
                                <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 600 }}>Auth</th>
                                <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 600 }}>Input</th>
                                <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 600 }}>Output</th>
                                <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 600 }}>Time</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(usageData.tool_calls || []).map((t, i) => (
                                <tr key={i} style={{ borderBottom: `1px solid ${V.border}33` }}>
                                  <td style={{ padding: '4px 8px', whiteSpace: 'nowrap' }}>{t.tool_name || '-'}</td>
                                  <td style={{ padding: '4px 8px', whiteSpace: 'nowrap', fontSize: 10, color: V.textDim }}>{t.external_task_id || '-'}</td>
                                  <td style={{ padding: '4px 8px', textAlign: 'center' }}>{t.result_status === 'success' ? <span style={{ color: '#22c55e' }}>ok</span> : t.result_status === 'pending_async' ? <span style={{ color: '#3b82f6' }}>pending</span> : t.result_status === 'partial' ? <span title={t.error_message || ''} style={{ color: '#eab308', cursor: 'help' }}>partial</span> : <span title={t.error_message || ''} style={{ color: '#ef4444', cursor: 'help' }}>{t.result_status || 'error'}</span>}</td>
                                  <td style={{ padding: '4px 8px', textAlign: 'right', color: V.textDim }}>{t.latency_ms != null ? `${t.latency_ms}ms` : '-'}</td>
                                  <td style={{ padding: '4px 8px', color: V.textDim }}>{t.token_kind || '-'}</td>
                                  <td style={{ padding: '4px 8px', textAlign: 'right' }}>{t.input_tokens || '-'}</td>
                                  <td style={{ padding: '4px 8px', textAlign: 'right' }}>{t.output_tokens || '-'}</td>
                                  <td style={{ padding: '4px 8px', whiteSpace: 'nowrap', fontSize: 11, color: V.textDim }}>{new Date(t.created_at).toLocaleString('zh-CN')}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        )}
                      </>
                    )}
                    {!usageData && !usageLoading && (
                      <div style={{ color: V.textDim }}>No usage data</div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        </>)}
      </div>

      {/* #1458 — Session list fold boundary */}
      <CollapsibleBoundary
        collapsed={sessionListCollapsed}
        onToggle={toggleSessionListCollapsed}
        side="right"
        ariaLabel="Toggle session list"
        borderColor={V.border}
        iconColor={V.textDim}
        hoverBg={V.bgHover}
      />

      {/* ── Chat Area (chat-area) ── */}
      <div className="pg-chat-area" style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {/* Session info header */}
        {selectedSessionForView && <SessionInfoHeader session={selectedSessionForView} fetchMode={fetchMode} onChangeFetchMode={onChangeFetchMode} widgetPanel={{
          suggest: widgetSuggest.data,
          attention: widgetSuggest.attention,
          loading: widgetSuggest.loading,
          onSeen: () => setWidgetSuggestMap(m => (m[selectedSessionId] ? { ...m, [selectedSessionId]: { ...m[selectedSessionId], attention: false } } : m)),
          onOpen: () => {
            // Opening the panel re-runs the silent history-aware refresh — one
            // source for suggestions (adopted verbatim when classified, so a
            // committed log's option doesn't linger), logged/pending state and
            // global My Widgets.
            const sid = selectedSessionForView?.sessionId || selectedSessionId;
            if (sid) applySilentSuggest(sid);
          },
          onAction: (txt, mediaRefs, optionId) => {
            // Optimistic overlay (§6.4): mark THIS option's `id` queuing so its
            // badge appears instantly; the server's authoritative status takes
            // over on the next suggest refresh. Keyed by id (folds the media
            // key) so per-image options don't collide.
            if (selectedSessionId && optionId) {
              setOptimisticStatusMap(m => ({
                ...m,
                [selectedSessionId]: { ...(m[selectedSessionId] || {}), [optionId]: 'queuing' },
              }));
            }
            // Per-image binding (G1): attach THIS option's own media_refs so two
            // options bound to two different images each log their own image.
            // Fall back to the round's capture media (recent-tile quick-log /
            // refresh-mode options that carry no per-option media).
            const media = (Array.isArray(mediaRefs) && mediaRefs.length)
              ? mediaRefs
              : (widgetSuggest.mediaUrls || []);
            handleSend(txt, media);
          },
          // A "Logged in this session" chip JUMPS to its widget_log card (the
          // card-anchored ?card= scroll) — it must NOT re-log (would duplicate).
          onJumpToCard: (lcid) => {
            if (!lcid) return;
            setSearchParams((p) => { const n = new URLSearchParams(p); n.set('card', lcid); return n; });
          },
        }} onDelete={async (sid) => {
          if (!window.confirm('Delete this session? This soft-deletes it (and any widget data it produced). Cannot be undone from the UI.')) return;
          try {
            const r = await api.authFetch(`/api/users/sessions/${encodeURIComponent(sid)}`, { method: 'DELETE' });
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            selectSession(null); setLiveBlocks([]); seenCardIdsRef.current.clear();
            await loadSessions();
          } catch (e) {
            alert(`Delete failed: ${e?.message || e}`);
          }
        }} />}
        {/* Messages */}
        <div className="pg-chat-messages" style={{ flex: 1, overflowY: 'auto', padding: '16px 24px' }}>
          {/* widget_main scroll-away cover hero (web twin of the app's
              _buildWidgetCoverHero) — null on every non-widget_main session.
              Resolves the widget from session.context.widget_use_contract. */}
          <WidgetChatCover session={selectedSessionFull || selectedSessionForView} />
          {/* Issue #1459: intention-brief context block — visible above the
              first user input when the session was seeded from /brief/stream.
              Uses selectedSessionFull so detail-fetch refreshes of
              session.metadata are reflected. */}
          <BriefContextBlock session={selectedSessionFull} />
          {!selectedSessionForView && liveBlocks.length === 0 ? (
            <div style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              height: '100%', color: V.textDim, fontSize: 14, gap: 8,
            }}>
              <span style={{ fontSize: 28, opacity: 0.3 }}>💬</span>
              <span>Start a new conversation or select a session</span>
            </div>
          ) : sortedTasks.length === 0 && liveBlocks.length === 0 ? (
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              height: '100%', color: V.textDim, fontSize: 14,
            }}>
              No messages in this session
            </div>
          ) : (
            <UnifiedTimeline
              sortedTasks={sortedTasks}
              allCards={allCards}
              cardTaskMap={cardTaskMap}
              liveBlocks={liveBlocks}
              sending={sending}
              rewindCut={rewindCut}
              onRewind={handleRewind}
              onCopy={handleCopyTurn}
            />
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* ── Continue: a terminal (stopped/failed) session resumes as the next turn. The
            backend /exec re-checks credits (402 → the out-of-credits notice above); native
            message_history makes it a faithful continue, not a from-scratch regenerate. ── */}
        {!activeTask && ['failed', 'stopped'].includes(getStatusBucket(selectedSessionForView?.status)) && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '6px 0 2px' }}>
            <button
              onClick={() => handleSend('', [], selectedSessionForView?.id || selectedSessionId, { resume: true })}
              disabled={sending}
              style={{
                display: 'flex', alignItems: 'center', gap: 6, padding: '7px 18px', borderRadius: 18,
                border: '1px solid rgba(0,0,0,0.15)', background: '#fff', color: '#111',
                fontSize: 13, fontWeight: 600, cursor: sending ? 'default' : 'pointer', opacity: sending ? 0.5 : 1,
              }}
            >
              ↻ Continue
            </button>
          </div>
        )}

        {/* ── Chat Input Bar — with inline model selector ── */}
        <ChatInputBar
          onSend={handleSend} disabled={sending} isExecuting={!!activeTask} onStop={handleStop}
          selectedModel={!selectedSessionId ? selectedModel : undefined}
          onModelChange={!selectedSessionId ? setSelectedModel : undefined}
          lockedModel={selectedSessionForView?.context?.model}
          selectedScheme={!selectedSessionId ? selectedScheme : undefined}
          onSchemeChange={!selectedSessionId ? setSelectedScheme : undefined}
          lockedScheme={selectedSessionForView?.context?.render_scheme}
          selectedEffort={!selectedSessionId ? selectedEffort : undefined}
          onEffortChange={!selectedSessionId ? setSelectedEffort : undefined}
          lockedEffort={selectedSessionForView?.context?.reasoning_effort}
          selectedWorker={!selectedSessionId ? selectedWorker : undefined}
          onWorkerChange={!selectedSessionId ? setSelectedWorker : undefined}
          lockedWorker={selectedSessionForView?.context?.worker_backend}
          prefill={composerPrefill}
        />
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// CARDS PAGE — mirrors dashboard tpl-layout (templates page)
// ══════════════════════════════════════════════════════════════════════

const CARD_ICONS = {
  'freeform-html': '\u{1F310}', 'debug-html': '\u{1F6E0}', 'nutrition-card': '\u{1F957}', 'shopping-list': '\u{1F6D2}',
  'comparison-table': '\u2696\uFE0F', 'hero-image': '\u{1F5BC}\uFE0F', 'image-analysis': '\u{1F50D}',
  'calendar-event': '\u{1F4C5}', 'map-pins': '\u{1F4CD}', 'quiz': '\u2753',
  'conversation': '\u{1F4AC}', 'thinking-process': '\u{1F9E0}', 'place-card': '\u{1F3EA}',
  'info-card': '\u2139\uFE0F', 'checklist': '\u2705', 'recipe': '\u{1F373}',
  'weather': '\u2600\uFE0F', 'steps-guide': '\u{1F4CB}', 'image-gallery': '\u{1F5BC}\uFE0F',
  'text-result': '\u{1F4DD}',
};

function CardsPage() {
  const [templates, setTemplates] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [testData, setTestData] = useState('');
  const [testCard, setTestCard] = useState(null);
  const [serverExamples, setServerExamples] = useState({});
  const [renderKey, setRenderKey] = useState(0);

  // Load the bundled template catalog (vendored from the retired
  // expert-packages _templates set — see services/templateRenderer.js).
  const loadData = useCallback(() => {
    setLoading(true);
    setError(null);
    clearTemplateCache();
    listBundledTemplates()
      .then(list => {
        setTemplates(list);
        const examples = {};
        for (const t of list) {
          if (t.example) examples[t.$id] = t.example;
        }
        setServerExamples(examples);
      })
      .catch(e => { setError(e.message); })
      .finally(() => { setLoading(false); });
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Filter templates
  const filtered = filter
    ? templates.filter(t =>
        (t.$id || '').toLowerCase().includes(filter.toLowerCase()) ||
        (t.description || '').toLowerCase().includes(filter.toLowerCase()) ||
        (t.category || '').toLowerCase().includes(filter.toLowerCase())
      )
    : templates;

  // Group by category
  const groups = {};
  for (const t of filtered) {
    const groupKey = t.category || 'other';
    if (!groups[groupKey]) groups[groupKey] = [];
    groups[groupKey].push(t);
  }
  const groupEntries = Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));

  const selectedTpl = templates.find(t => t.$id === selectedId);

  // Generate sample data when template selected
  useEffect(() => {
    if (!selectedTpl) return;
    const data = generateSampleData(selectedTpl, serverExamples);
    setTestData(JSON.stringify(data, null, 2));
    setTestCard({
      cardId: 'preview-' + selectedTpl.$id,
      template: selectedTpl.$id,
      data: data,
      status: 'finalized',
    });
  }, [selectedId, serverExamples]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleRender() {
    if (!selectedTpl) return;
    try {
      const data = JSON.parse(testData);
      setRenderKey(k => k + 1); // force re-render even with same data
      setTestCard({
        cardId: 'preview-' + selectedTpl.$id,
        template: selectedTpl.$id,
        data: data,
        status: 'finalized',
      });
    } catch (e) {
      alert('Invalid JSON: ' + e.message);
    }
  }

  return (
    <div style={{ display: 'flex', width: '100%', height: '100%' }}>
      {/* ── Template Sidebar (tpl-sidebar) ── */}
      <div style={{
        width: 260, background: V.bgCard, borderRight: `1px solid ${V.border}`,
        display: 'flex', flexDirection: 'column', flexShrink: 0,
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 12px', borderBottom: `1px solid ${V.border}`,
        }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: V.text }}>Cards</span>
          <button onClick={loadData} style={{
            background: 'none', border: `1px solid ${V.border}`, borderRadius: 4,
            color: V.textDim, cursor: 'pointer', fontSize: 13, padding: '2px 6px',
          }} title="Refresh">↻</button>
        </div>
        <div style={{ padding: '8px 10px' }}>
          <input
            type="text"
            placeholder="Filter..."
            value={filter}
            onChange={e => setFilter(e.target.value)}
            style={{
              width: '100%', padding: '6px 10px', background: V.bg,
              border: `1px solid ${V.border}`, borderRadius: 6,
              color: V.text, fontSize: 12, outline: 'none',
            }}
          />
        </div>
        <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
          {loading && <div style={{ padding: 16, fontSize: 12, color: V.textDim }}>Loading...</div>}
          {error && <div style={{ padding: 16, fontSize: 12, color: V.red }}>Error: {error}</div>}
          {groupEntries.map(([group, items]) => (
            <div key={group}>
              <div style={{
                padding: '10px 12px 4px', fontSize: 10, fontWeight: 600,
                textTransform: 'uppercase', letterSpacing: '0.06em', color: V.textDim,
              }}>
                {group}
              </div>
              {items.map(t => {
                const isActive = t.$id === selectedId;
                return (
                  <div
                    key={t.$id}
                    onClick={() => setSelectedId(t.$id)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      padding: '8px 12px', cursor: 'pointer',
                      background: isActive ? V.accentDim : 'transparent',
                      transition: 'background 0.1s',
                    }}
                    onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = V.bgHover; }}
                    onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = isActive ? V.accentDim : 'transparent'; }}
                  >
                    <span style={{ fontSize: 16, width: 20, textAlign: 'center' }}>
                      {CARD_ICONS[t.$id] || '\u{1F4C4}'}
                    </span>
                    <div style={{ overflow: 'hidden', flex: 1 }}>
                      <div style={{ fontSize: 12, fontWeight: 500, color: isActive ? V.accent : V.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {t.$id}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 1 }}>
                        {t.version && (
                          <span style={{ fontSize: 10, color: V.accent, fontFamily: 'monospace' }}>v{t.version}</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* ── Template Main (tpl-main) ── */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 24, color: V.text }}>
        {!selectedTpl ? (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            height: '100%', color: V.textDim, fontSize: 14,
          }}>
            Select a card to view details
          </div>
        ) : (
          <div>
            {/* Header */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <h3 style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>{selectedTpl.$id}</h3>
                  {selectedTpl.version && (
                    <span style={{ fontSize: 11, fontFamily: 'monospace', padding: '1px 8px', borderRadius: 4, background: 'rgba(88,86,214,0.12)', color: V.accent }}>v{selectedTpl.version}</span>
                  )}
                </div>
              </div>
              <div style={{ marginTop: 4, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <span style={{ background: 'rgba(0,122,255,0.1)', color: V.blue, padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 500 }}>
                  {selectedTpl.category || '-'}
                </span>
                <span style={{ background: 'rgba(175,82,222,0.1)', color: V.purple, padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 500 }}>
                  {selectedTpl.renderer || 'html'}
                </span>
                {selectedTpl.streamable && (
                  <span style={{ background: 'rgba(52,199,89,0.1)', color: V.green, padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 500 }}>streamable</span>
                )}
              </div>
              {selectedTpl.description && (
                <p style={{ marginTop: 8, color: V.textDim, fontSize: 13 }}>{selectedTpl.description}</p>
              )}
            </div>

            {/* 2-column layout: Example Data | Rendered */}
            <div style={{
              display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12,
              minHeight: 300,
            }}>
              {/* Column 1: Example Data (editable) */}
              <div style={{
                background: V.bgCard, border: `1px solid ${V.border}`,
                borderRadius: 8, overflow: 'hidden', display: 'flex', flexDirection: 'column',
              }}>
                <div style={{
                  padding: '8px 12px', fontSize: 11, fontWeight: 600,
                  textTransform: 'uppercase', letterSpacing: '0.04em',
                  color: V.textDim, borderBottom: `1px solid ${V.border}`,
                }}>
                  Example Data
                </div>
                <div style={{ padding: 8, flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <textarea
                    value={testData}
                    onChange={e => setTestData(e.target.value)}
                    style={{
                      flex: 1, width: '100%', minHeight: 200, resize: 'vertical',
                      background: V.bg, border: `1px solid ${V.border}`, borderRadius: 6,
                      color: V.text, padding: 8, fontSize: 12, fontFamily: V.font,
                      lineHeight: 1.5, outline: 'none',
                    }}
                  />
                  <button
                    onClick={handleRender}
                    style={{
                      marginTop: 6, width: '100%', padding: '6px 12px',
                      background: V.accent, color: '#fff', border: 'none',
                      borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 500,
                    }}
                  >
                    Render
                  </button>
                </div>
              </div>

              {/* Column 2: Rendered preview */}
              <div style={{
                background: V.bgCard, border: `1px solid ${V.border}`,
                borderRadius: 8, overflow: 'hidden', display: 'flex', flexDirection: 'column',
              }}>
                <div style={{
                  padding: '8px 12px', fontSize: 11, fontWeight: 600,
                  textTransform: 'uppercase', letterSpacing: '0.04em',
                  color: V.textDim, borderBottom: `1px solid ${V.border}`,
                }}>
                  Rendered
                </div>
                <div style={{ flex: 1, overflowY: 'auto', background: '#fff', color: '#111' }}>
                  {testCard ? <CardRenderer key={renderKey} card={testCard} /> : (
                    <div style={{ padding: 16, color: '#999', fontSize: 13 }}>Click Render to preview</div>
                  )}
                </div>
              </div>
            </div>

            {/* Slots info */}
            {selectedTpl.slots && Object.keys(selectedTpl.slots).length > 0 && (
              <div style={{ marginTop: 16 }}>
                <div style={{
                  fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
                  letterSpacing: '0.05em', color: V.textDim, marginBottom: 8,
                }}>
                  Slots ({Object.keys(selectedTpl.slots).length})
                </div>
                <div style={{
                  background: V.bgCard, border: `1px solid ${V.border}`,
                  borderRadius: 8, overflow: 'hidden',
                }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'left', padding: '10px 14px', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: V.textDim, borderBottom: `1px solid ${V.border}`, fontWeight: 500 }}>Name</th>
                        <th style={{ textAlign: 'left', padding: '10px 14px', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: V.textDim, borderBottom: `1px solid ${V.border}`, fontWeight: 500 }}>Type</th>
                        <th style={{ textAlign: 'left', padding: '10px 14px', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: V.textDim, borderBottom: `1px solid ${V.border}`, fontWeight: 500 }}>Required</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(selectedTpl.slots).map(([name, slot]) => (
                        <tr key={name}>
                          <td style={{ padding: '8px 14px', borderBottom: `1px solid ${V.border}`, color: V.text, fontFamily: V.font, fontSize: 12 }}>{name}</td>
                          <td style={{ padding: '8px 14px', borderBottom: `1px solid ${V.border}`, color: V.textDim }}>{slot.type || '-'}</td>
                          <td style={{ padding: '8px 14px', borderBottom: `1px solid ${V.border}`, color: V.textDim }}>{slot.required ? 'yes' : 'no'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Sample data per template (matches what each React component expects) ──

const SAMPLE_DATA = {
  'weather': {
    location: 'San Francisco, CA',
    current: { temp: 18, condition: 'Partly Cloudy', icon: '⛅', humidity: 65, wind: '12 km/h', unit: 'C' },
    forecast: [
      { day: 'Mon', high: 20, low: 13, condition: 'Sunny', icon: '☀️' },
      { day: 'Tue', high: 17, low: 11, condition: 'Cloudy', icon: '☁️' },
      { day: 'Wed', high: 15, low: 10, condition: 'Rain', icon: '🌧️' },
      { day: 'Thu', high: 19, low: 12, condition: 'Partly Cloudy', icon: '⛅' },
      { day: 'Fri', high: 22, low: 14, condition: 'Sunny', icon: '☀️' },
    ],
  },
  'recipe': {
    title: 'Classic Margherita Pizza',
    image_url: 'https://images.unsplash.com/photo-1604068549290-dea0e4a305ca?w=600&q=80',
    prep_time: '20 min',
    cook_time: '12 min',
    servings: 4,
    ingredients: [
      { amount: '500g', item: 'Pizza dough' },
      { amount: '200ml', item: 'San Marzano tomato sauce' },
      { amount: '250g', item: 'Fresh mozzarella' },
      { amount: '10', item: 'Fresh basil leaves' },
      { amount: '2 tbsp', item: 'Extra virgin olive oil' },
    ],
    steps: [
      'Preheat oven to 250°C (480°F) with a pizza stone inside.',
      'Stretch dough into a 12-inch round on floured surface.',
      'Spread tomato sauce evenly, leaving a 1-inch border.',
      'Tear mozzarella into pieces and distribute over sauce.',
      'Bake for 10-12 minutes until crust is golden and cheese bubbles.',
      'Top with fresh basil and drizzle with olive oil. Serve immediately.',
    ],
  },
  'checklist': {
    title: 'Weekend Trip Packing',
    items: [
      { text: 'Passport & ID', checked: true, category: 'Documents' },
      { text: 'Travel insurance printout', checked: false, category: 'Documents' },
      { text: 'T-shirts (×3)', checked: true, category: 'Clothing' },
      { text: 'Jacket', checked: false, category: 'Clothing' },
      { text: 'Phone charger', checked: true, category: 'Electronics' },
      { text: 'Camera', checked: false, category: 'Electronics' },
      { text: 'Toothbrush', checked: false, category: 'Toiletries' },
      { text: 'Sunscreen', checked: false, category: 'Toiletries' },
    ],
  },
  'info-card': {
    title: 'Golden Gate Bridge',
    subtitle: 'Iconic suspension bridge',
    icon: '🌉',
    fields: [
      { label: 'Location', value: 'San Francisco, CA' },
      { label: 'Opened', value: 'May 27, 1937' },
      { label: 'Total Length', value: '2,737 m (8,981 ft)', highlight: true },
      { label: 'Architect', value: 'Joseph Strauss & Irving Morrow' },
      { label: 'Daily Traffic', value: '~100,000 vehicles' },
    ],
    footer: 'Designated as a National Historic Landmark in 1987.',
    accent_color: '#e3513c',
  },
  'image-gallery': {
    title: 'Mountain Landscapes',
    images: [
      { url: 'https://images.unsplash.com/photo-1506744038136-46273834b3fb?w=600&q=80', caption: 'Yosemite Valley at sunrise', alt: 'Yosemite Valley' },
      { url: 'https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=600&q=80', caption: 'Alpine peaks in morning light', alt: 'Alpine mountains' },
      { url: 'https://images.unsplash.com/photo-1519681393784-d120267933ba?w=600&q=80', caption: 'Starry night over mountains', alt: 'Mountain night sky' },
    ],
  },
  'nutrition-card': {
    food_name: 'Grilled Salmon Bowl',
    photo_url: 'https://images.unsplash.com/photo-1467003909585-2f8a72700288?w=600&q=80',
    calories: 520,
    protein_g: 38,
    carbs_g: 45,
    fat_g: 18,
    fiber_g: 6,
    serving_size: '1 bowl (350g)',
    health_score: 8.5,
    recommendation: 'Excellent source of omega-3 fatty acids and lean protein. Great post-workout meal.',
    daily_percent: { calories: 26, protein: 76, carbs: 15, fat: 23 },
  },
  'shopping-list': {
    title: 'Weekly Groceries',
    items: [
      { name: 'Organic eggs', quantity: '1 dozen', category: 'Dairy', price_estimate: '$5.99' },
      { name: 'Whole milk', quantity: '1 gallon', category: 'Dairy', price_estimate: '$4.49' },
      { name: 'Chicken breast', quantity: '2 lbs', category: 'Meat', price_estimate: '$8.99' },
      { name: 'Salmon fillet', quantity: '1 lb', category: 'Meat', price_estimate: '$12.99' },
      { name: 'Broccoli', quantity: '2 heads', category: 'Produce', price_estimate: '$3.49' },
      { name: 'Avocados', quantity: '4', category: 'Produce', price_estimate: '$5.99' },
      { name: 'Sourdough bread', quantity: '1 loaf', category: 'Bakery', price_estimate: '$4.99' },
    ],
    total_estimate: '$46.93',
    store_suggestion: 'Whole Foods Market',
  },
  'place-card': {
    name: 'Tartine Bakery',
    category: 'Bakery & Cafe',
    rating: 4.6,
    price_level: 2,
    address: '600 Guerrero St, San Francisco, CA 94110',
    phone: '+1 (415) 487-2600',
    hours: 'Mon-Sun: 7:30 AM - 7:00 PM',
    image_url: 'https://images.unsplash.com/photo-1509440159596-0249088772ff?w=600&q=80',
    coordinates: { lat: 37.7614, lng: -122.4241 },
    tags: ['Bakery', 'Coffee', 'Pastries', 'Brunch'],
    url: 'https://www.tartinebakery.com',
  },
  'map-pins': {
    title: 'Best Coffee Shops Nearby',
    markers: [
      { id: '1', lat: 37.7749, lng: -122.4194, label: 'Blue Bottle Coffee', description: 'Third-wave coffee roaster', rating: 4.5, icon: '☕' },
      { id: '2', lat: 37.7694, lng: -122.4262, label: 'Sightglass Coffee', description: 'Artisan roaster with open loft', rating: 4.4, icon: '☕' },
      { id: '3', lat: 37.7614, lng: -122.4241, label: 'Ritual Coffee Roasters', description: 'Single-origin specialists', rating: 4.3, icon: '☕' },
    ],
  },
  'quiz': {
    question: 'What is the tallest mountain in the solar system?',
    image_url: 'https://images.unsplash.com/photo-1614728263952-84ea256f9679?w=600&q=80',
    options: [
      { id: 'a', text: 'Mount Everest (Earth)', correct: false },
      { id: 'b', text: 'Olympus Mons (Mars)', correct: true },
      { id: 'c', text: 'Maxwell Montes (Venus)', correct: false },
      { id: 'd', text: 'Boösaule Montes (Io)', correct: false },
    ],
    explanation: 'Olympus Mons on Mars stands at approximately 21.9 km (13.6 mi) tall, making it nearly 2.5 times the height of Mount Everest.',
  },
  'conversation': {
    topic: 'Planning a weekend hike',
    messages: [
      { role: 'user', content: 'Can you suggest a good day hike near San Francisco?' },
      { role: 'ai', content: 'I recommend the Dipsea Trail in Marin County! It\'s about 7 miles one-way from Mill Valley to Stinson Beach, with beautiful ocean views and redwood groves.' },
      { role: 'user', content: 'How difficult is it?' },
      { role: 'ai', content: 'It\'s rated moderate to strenuous. The first section has ~700 stairs, but the views from the ridgeline are spectacular. Allow 3-4 hours and bring plenty of water.' },
    ],
    input_placeholder: 'Ask a follow-up question...',
  },
  'comparison-table': {
    title: 'Laptop Comparison',
    items: [
      { name: 'MacBook Air M3', highlight: true, verdict: 'Best Overall', rating: 4.7, price: '$1,099', pros: ['Fanless design', 'All-day battery', 'Great display'], cons: ['Only 2 ports', 'Base model 8GB RAM'], attributes: { Chip: 'Apple M3', RAM: '8-24 GB', Storage: '256GB-2TB', Weight: '1.24 kg' } },
      { name: 'ThinkPad X1 Carbon', verdict: 'Best for Business', rating: 4.5, price: '$1,399', pros: ['Amazing keyboard', 'Multiple ports', 'MIL-SPEC durable'], cons: ['Shorter battery', 'Heavier'], attributes: { Chip: 'Intel Ultra 7', RAM: '16-32 GB', Storage: '512GB-2TB', Weight: '1.12 kg' } },
      { name: 'Dell XPS 13', verdict: 'Most Compact', rating: 4.3, price: '$999', pros: ['Ultra-compact', 'Great price', 'OLED option'], cons: ['Limited ports', 'Webcam angle'], attributes: { Chip: 'Intel Ultra 5', RAM: '16 GB', Storage: '512GB-1TB', Weight: '1.17 kg' } },
    ],
  },
  'thinking-process': {
    title: 'Analyzing your photo...',
    steps: [
      { title: 'Image Recognition', description: 'Identifying objects, text, and scene context in the photo.' },
      { title: 'Scene Analysis', description: 'Determining the environment: indoor office with natural lighting and multiple monitors.' },
      { title: 'Detail Extraction', description: 'Reading visible text, identifying brands, and noting spatial relationships.' },
      { title: 'Context Synthesis', description: 'Combining observations into a coherent understanding of the scene.' },
    ],
    conclusion: 'The photo shows a modern workspace with dual monitors, a mechanical keyboard, and natural light from a nearby window.',
  },
  'calendar-event': {
    title: 'Team Standup Meeting',
    start_time: 'Mon, March 11 · 9:30 AM',
    end_time: '10:00 AM PST',
    location: 'Zoom (link in calendar invite)',
    description: 'Daily sync — Engineering team reviews progress, blockers, and plans for the day.',
    attendees: ['SC', 'JW', 'ML', 'KR', 'AP'],
    status: 'pending',
    status_label: 'Pending',
  },
  'image-analysis': {
    photo_url: 'https://images.unsplash.com/photo-1477959858617-67f85cf4f1df?w=800&q=80',
    title: 'Urban Street Scene',
    description: 'Outdoor urban street with commercial storefronts during golden hour.',
    detected_objects: [
      { label: 'Car', confidence_pct: 97 },
      { label: 'Pedestrian', confidence_pct: 94 },
      { label: 'Traffic Light', confidence_pct: 91 },
      { label: 'Storefront', confidence_pct: 88 },
      { label: 'Sign', confidence_pct: 85 },
    ],
    tags: ['urban', 'street', 'golden-hour', 'commercial', 'outdoor'],
  },
  'document-scan': {
    title: 'Business Card Scan',
    photo_url: 'https://images.unsplash.com/photo-1611532736597-de2d4265fba3?w=600&q=80',
    fields: [
      { label: 'Name', value: 'Sarah Chen' },
      { label: 'Title', value: 'Senior Product Designer' },
      { label: 'Company', value: 'Acme Corp' },
      { label: 'Email', value: 'sarah@acme.co' },
      { label: 'Phone', value: '+1 (415) 555-0123' },
    ],
    raw_text: 'Sarah Chen\nSenior Product Designer\nAcme Corp\nsarah@acme.co\n+1 (415) 555-0123',
  },
  'evolution-suggestion': {
    skill_name: 'Interior Design Assistant',
    suggestion: 'Add color palette extraction from room photos to suggest complementary furniture and accent colors.',
    reason: 'Users frequently ask about color coordination after receiving room analysis results.',
    impact: 'high',
  },
  'file-download': {
    filename: 'room-analysis-report.pdf',
    size: '2.4 MB',
    type: 'pdf',
    url: '#',
    description: 'Detailed room analysis with measurements and recommendations.',
  },
  'memory-update': {
    items: [
      { field: 'Preferred style', value: 'Modern minimalist with warm accents' },
      { field: 'Budget range', value: '$5,000 - $10,000' },
      { field: 'Room focus', value: 'Living room renovation' },
    ],
  },
  'plant-animal-id': {
    title: 'Monstera Deliciosa',
    subtitle: 'Swiss Cheese Plant',
    icon: '🌿',
    fields: [
      { label: 'Family', value: 'Araceae' },
      { label: 'Origin', value: 'Central America' },
      { label: 'Light', value: 'Bright indirect', highlight: true },
      { label: 'Water', value: 'Weekly, let soil dry between' },
      { label: 'Toxicity', value: 'Toxic to pets' },
    ],
  },
  'session-summary': {
    title: 'Session Complete',
    subtitle: 'Room analysis finished',
    icon: '✅',
    fields: [
      { label: 'Duration', value: '3 min 42 sec' },
      { label: 'Cards generated', value: '5', highlight: true },
      { label: 'Photos analyzed', value: '3' },
      { label: 'Recommendations', value: '8 items' },
    ],
    footer: 'Results saved to your history.',
  },
  'step-guide': {
    title: 'How to Measure Your Room',
    steps: [
      { title: 'Clear the space', description: 'Remove furniture from walls for accurate measurements.' },
      { title: 'Measure wall lengths', description: 'Use a tape measure along each wall, noting doors and windows.' },
      { title: 'Note ceiling height', description: 'Measure floor to ceiling in at least two spots.' },
      { title: 'Photograph each wall', description: 'Take straight-on photos of each wall for reference.' },
    ],
    conclusion: 'With these measurements, we can generate accurate furniture placement suggestions.',
  },
  'text-result': {
    title: 'Analysis Summary',
    content: 'Your living room has excellent natural light from the south-facing windows. The 4.2m × 5.8m dimensions allow for a generous seating arrangement. Consider a sectional sofa along the east wall with a coffee table centered in the space.',
  },
  'video-player': {
    title: 'Room Walkthrough',
    url: 'https://www.w3schools.com/html/mov_bbb.mp4',
    poster: 'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=600&q=80',
  },
  'hero-image': {
    image_url: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=800&q=80',
    title: 'Sunset at Ocean Beach',
    subtitle: 'San Francisco, CA',
    description: 'Golden hour light over the Pacific, captured during an evening walk along the shoreline.',
    actions: [
      { label: 'Save', action: 'download_file', icon: '💾' },
      { label: 'Share', action: 'share', icon: '🔗' },
    ],
  },
  'ai-generation': {
    type: 'image',
    title: 'Generating artwork from drawing...',
    status: 'generating',
    detail: 'Sending to Gemini API...',
    startedAt: Date.now() - 12000,
  },
  'freeform-html': {
    html: '<div style="font-family: system-ui, sans-serif;">' +
      '<h2 style="margin: 0 0 12px; font-size: 20px;">Custom HTML Content</h2>' +
      '<p style="color: #555; line-height: 1.6;">This is a <strong>freeform HTML</strong> card. It renders arbitrary HTML content streamed from the agent — useful for rich formatting that doesn\'t fit a structured template.</p>' +
      '<ul style="color: #444; padding-left: 20px; line-height: 1.8;">' +
        '<li>Supports <em>any</em> valid HTML</li>' +
        '<li>Tables, lists, images, styled text</li>' +
        '<li>Used as fallback when no template matches</li>' +
      '</ul>' +
      '<table style="width: 100%; border-collapse: collapse; margin-top: 12px; font-size: 13px;">' +
        '<tr style="background: #f5f5f5;"><th style="text-align: left; padding: 8px; border: 1px solid #ddd;">Feature</th><th style="text-align: left; padding: 8px; border: 1px solid #ddd;">Status</th></tr>' +
        '<tr><td style="padding: 8px; border: 1px solid #ddd;">Streaming</td><td style="padding: 8px; border: 1px solid #ddd;">✅ Supported</td></tr>' +
        '<tr><td style="padding: 8px; border: 1px solid #ddd;">Interactivity</td><td style="padding: 8px; border: 1px solid #ddd;">❌ Static only</td></tr>' +
      '</table>' +
    '</div>',
  },
  // Sibling of freeform-html: same renderer (PersistentHtmlRenderer), same HTML payload shape,
  // but frontend buckets these into a collapsible Debug panel. Used by the runner agent-speak
  // hook to publish tool I/O without cluttering the main card list. See Issue #1007.
  'debug-html': {
    html: '<div style="font-family: ui-monospace, monospace; font-size: 12px; padding: 10px 12px; border-left: 3px solid #4a90e2; background: #fafafa; border-radius: 4px;">' +
      '<div style="font-weight: 600; margin-bottom: 6px;">🛠 mcp__shared_tools__vlm_analyze</div>' +
      '<div style="color: #888; margin-bottom: 4px;">INPUT</div>' +
      '<pre style="white-space: pre-wrap; margin: 0 0 8px 0;">{"image_url":"...","prompt":"describe outfit"}</pre>' +
      '<div style="color: #888; margin-bottom: 4px;">OUTPUT <span style="font-weight: normal;">⏱ 1.2s · 312 chars</span></div>' +
      '<pre style="white-space: pre-wrap; margin: 0;">{"description":"A person wearing a blue jacket..."}</pre>' +
    '</div>',
  },
};

function generateSampleData(tpl, serverExamples = {}) {
  const id = tpl.$id || '';
  // 1. Prefer server-provided example data (hot-updatable via /package-drive)
  if (serverExamples[id]) return serverExamples[id];
  // 2. Fallback to local hardcoded examples
  if (SAMPLE_DATA[id]) return SAMPLE_DATA[id];
  // 3. Final fallback: generate from slot types
  const data = {};
  const slots = tpl.slots || {};
  for (const [name, slot] of Object.entries(slots)) {
    if (slot.type === 'string') data[name] = 'Sample ' + name;
    else if (slot.type === 'number') data[name] = 42;
    else if (slot.type === 'boolean') data[name] = true;
    else if (slot.type === 'array') data[name] = [];
    else data[name] = null;
  }
  return data;
}

// ══════════════════════════════════════════════════════════════════════
// TOOLS PAGE — native tools from skill-executor
// ══════════════════════════════════════════════════════════════════════

// Tier → pill color. Palette per Issue #1466: green=light, blue=standard, purple=heavy.
// Used by ToolsPage tool metadata.
function TierChip({ tier }) {
  if (!tier) return null;
  const color = tier === 'light' ? '#2f855a' : tier === 'standard' ? '#2563eb' : '#7c3aed';
  return (
    <span style={{
      fontSize: 10, padding: '1px 6px', borderRadius: 4,
      background: `${color}15`, color, textTransform: 'uppercase', letterSpacing: '0.04em',
      fontFamily: V.fontUi, fontWeight: 600,
    }}>{tier}</span>
  );
}

function ToolsPage() {
  const [tools, setTools] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    api.authFetch('/api/runner/dashboard/native-tools')
      .then(r => r.json())
      .catch(() => ({ tools: [] }))
      .then(data => {
        const list = data.tools || [];
        setTools(list);
        if (list.length > 0 && !selected) setSelected(list[0]);
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredTools = tools.filter(t => {
    if (search) {
      const q = search.toLowerCase();
      if (!t.name.toLowerCase().includes(q) && !(t.description || '').toLowerCase().includes(q)) return false;
    }
    return true;
  });

  // Group by category for the sidebar
  const byCategory = filteredTools.reduce((acc, t) => {
    const key = t.category || 'other';
    (acc[key] = acc[key] || []).push(t);
    return acc;
  }, {});
  const categoryOrder = ['search', 'memory', 'vision', 'generation', 'web', 'integration', 'interact', 'filesystem', 'cards', 'system', 'other'];
  const sortedCategories = Object.keys(byCategory).sort(
    (a, b) => (categoryOrder.indexOf(a) + 1 || 99) - (categoryOrder.indexOf(b) + 1 || 99),
  );

  const S = {
    sidebar: { width: 280, borderRight: `1px solid ${V.border}`, background: V.bgCard, display: 'flex', flexDirection: 'column' },
    item: (active) => ({
      padding: '10px 14px', cursor: 'pointer', borderBottom: `1px solid ${V.border}`,
      background: active ? V.accentDim : 'transparent', transition: 'background 0.15s',
    }),
    sectionHeader: { padding: '8px 14px', fontWeight: 600, borderBottom: `1px solid ${V.border}`, color: V.textDim, textTransform: 'uppercase', letterSpacing: '0.03em', fontSize: 11, background: V.bg },
    field: { display: 'grid', gridTemplateColumns: '130px 1fr', gap: 12, padding: '8px 0', borderBottom: `1px solid ${V.border}`, fontSize: 13 },
    codeBlock: { background: V.bg, border: `1px solid ${V.border}`, borderRadius: 6, padding: 12, fontSize: 12, fontFamily: V.font, overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word' },
  };

  if (loading) return <div style={{ padding: 40, color: V.textDim }}>Loading tools...</div>;

  return (
    <div style={{ display: 'flex', height: '100%' }}>
      <div style={S.sidebar}>
        <div style={{ padding: '12px 14px', borderBottom: `1px solid ${V.border}` }}>
          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>
            Tools ({tools.length})
          </div>
          <input
            type="text"
            placeholder="Search tools..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ width: '100%', padding: '6px 8px', fontSize: 12, border: `1px solid ${V.border}`, borderRadius: 4, background: V.bg, color: V.text, fontFamily: V.fontUi, boxSizing: 'border-box' }}
          />
        </div>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {sortedCategories.length === 0 ? (
            <div style={{ padding: 20, color: V.textDim, fontSize: 12, textAlign: 'center' }}>No tools match the filter.</div>
          ) : sortedCategories.map(cat => (
            <div key={cat}>
              <div style={S.sectionHeader}>{cat} ({byCategory[cat].length})</div>
              {byCategory[cat].map(t => {
                const isActive = selected?.name === t.name;
                return (
                  <div key={t.name} style={S.item(isActive)}
                    onClick={() => setSelected(t)}
                    onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = V.bgHover; }}
                    onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: 13, fontWeight: 500 }}>{t.icon} {t.name}</span>
                    </div>
                    {t.latency && (
                      <div style={{ fontSize: 10, color: V.textDim, marginTop: 3, fontFamily: V.font }}>{t.latency}</div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
      <div style={{ flex: 1, overflow: 'auto', padding: 24 }}>
        {selected ? (
          <div style={{ maxWidth: 820 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <h2 style={{ fontSize: 22, fontWeight: 600, margin: 0 }}>{selected.icon} {selected.name}</h2>
              <TierChip tier={selected.tier} />
            </div>
            <div style={{ fontSize: 12, color: V.textDim, marginBottom: 18, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <span><strong>category:</strong> {selected.category}</span>
              {selected.latency && <span><strong>latency:</strong> {selected.latency}</span>}
              {selected.cost && <span><strong>cost:</strong> {selected.cost}</span>}
              {selected.sourceFile && (
                <span><strong>source:</strong> <code style={{ background: V.bgHover, padding: '1px 5px', borderRadius: 3 }}>{selected.sourceFile}</code></span>
              )}
            </div>

            <div style={{ fontSize: 13, lineHeight: 1.55, marginBottom: 18, whiteSpace: 'pre-wrap' }}>{selected.description}</div>

            {selected.notes && (
              <div style={{
                background: 'rgba(0,122,255,0.06)', border: '1px solid rgba(0,122,255,0.15)', borderRadius: 8,
                padding: '10px 12px', marginBottom: 18, fontSize: 12, color: V.text, lineHeight: 1.5,
              }}>
                <div style={{ fontSize: 10, fontWeight: 600, color: V.blue, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>Notes</div>
                {selected.notes}
              </div>
            )}

            {selected.input_schema?.properties && Object.keys(selected.input_schema.properties).length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: V.textDim, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Parameters</div>
                <div style={{ border: `1px solid ${V.border}`, borderRadius: 6, padding: '0 14px', background: V.bgCard }}>
                  {Object.entries(selected.input_schema.properties).map(([name, schema]) => {
                    const required = (selected.input_schema.required || []).includes(name);
                    const typeLabel = Array.isArray(schema.type) ? schema.type.join(' | ') : (schema.type || 'any');
                    const enumVals = Array.isArray(schema.enum) ? schema.enum : null;
                    return (
                      <div key={name} style={S.field}>
                        <div>
                          <code style={{ background: V.bgHover, padding: '2px 6px', borderRadius: 4, fontSize: 12, fontFamily: V.font }}>{name}</code>
                          {required && <span style={{ color: V.red, marginLeft: 6, fontSize: 11 }} title="required">*</span>}
                          <div style={{ fontSize: 10, color: V.textDim, marginTop: 3, fontFamily: V.font }}>
                            {typeLabel}
                            {schema.default !== undefined && <> · default: <code>{JSON.stringify(schema.default)}</code></>}
                          </div>
                        </div>
                        <div style={{ fontSize: 12, color: V.text, lineHeight: 1.5 }}>
                          {schema.description || <span style={{ color: V.textDim }}>—</span>}
                          {enumVals && (
                            <div style={{ marginTop: 3, fontSize: 11, color: V.textDim }}>
                              one of: {enumVals.map(v => <code key={v} style={{ background: V.bgHover, padding: '0 4px', borderRadius: 3, marginRight: 4, fontFamily: V.font }}>{JSON.stringify(v)}</code>)}
                            </div>
                          )}
                          {schema.items?.type && (
                            <div style={{ marginTop: 3, fontSize: 11, color: V.textDim }}>items: <code style={{ fontFamily: V.font }}>{schema.items.type}</code></div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <details style={{ marginTop: 18 }}>
              <summary style={{ fontSize: 11, fontWeight: 600, color: V.textDim, textTransform: 'uppercase', letterSpacing: '0.04em', cursor: 'pointer', outline: 'none' }}>Raw input schema</summary>
              <pre style={{ ...S.codeBlock, marginTop: 8 }}>{JSON.stringify(selected.input_schema, null, 2)}</pre>
            </details>
          </div>
        ) : (
          <div style={{ padding: 40, color: V.textDim, textAlign: 'center' }}>Select a tool</div>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// APIs PAGE — API key health checks
// ══════════════════════════════════════════════════════════════════════

function ApisPage() {
  const [apis, setApis] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.authFetch('/api/runner/dashboard/health/all')
      .then(r => r.json())
      .then(d => { setApis(d.apis || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const statusColor = (s) => s === 'ok' ? V.green : s === 'warn' ? V.yellow : V.red;

  if (loading) return <div style={{ padding: 40, color: V.textDim }}>Loading API status...</div>;

  return (
    <div style={{ padding: 20, overflowY: 'auto', height: '100%' }}>
      <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>API Keys</h2>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
        {apis.map((a, i) => (
          <div key={i} style={{ background: V.bgCard, border: `1px solid ${V.border}`, borderRadius: 10, padding: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span style={{ fontWeight: 600, fontSize: 14 }}>{a.api || a.name}</span>
              <span style={{
                display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 11,
                background: statusColor(a.status) + '22', color: statusColor(a.status),
              }}>{a.status}</span>
            </div>
            {a.envVars?.map((ev, j) => (
              <div key={j} style={{ fontSize: 12, color: V.textDim, marginTop: 4 }}>
                <code>{ev.envVar}</code>: {ev.set ? <span style={{ color: V.green }}>set ({ev.masked})</span> : <span style={{ color: V.red }}>missing</span>}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// COMPOSIO PAGE — Google OAuth connections via Composio
// ══════════════════════════════════════════════════════════════════════

function ConnectionsPage() {
  const { apps: composioApps, statuses, loading, error: loadError, reload } = useComposioApps();
  const [connecting, setConnecting] = useState(null);
  const [error, setError] = useState(null);
  const [sources, setSources] = useState([]);
  const loadSources = useCallback(() => {
    api.getSources().then(d => setSources(d?.sources || [])).catch(() => setSources([]));
  }, []);
  useEffect(() => { loadSources(); }, [loadSources]);

  // "Learn about me" (S4) is a separate consent from the connection itself: nothing is scanned until on.
  const setLearn = async (kind, learn, forget = false) => {
    setError(null);
    try {
      await api.setSourceConsent(kind, learn, forget);
      loadSources();
    } catch (e) {
      setError(`${kind}: ${e?.message || 'Could not update learning'}`);
    }
  };

  const handleConnect = async (app) => {
    setError(null);
    setConnecting(app);
    try {
      const data = await api.composioConnect(app);
      if (data?.redirect_url) {
        window.open(data.redirect_url, '_blank');
        setTimeout(() => reload(), 5000);
      }
    } catch (e) {
      console.error('Composio connect failed:', e);
      setError(`${app}: ${e?.message || 'Connect failed'}`);
    }
    setConnecting(null);
  };

  const handleDisconnect = async (app) => {
    const s = statuses[app];
    if (!s?.connection_id) return;
    setError(null);
    try {
      await api.composioDisconnect(s.connection_id);
      reload();
    } catch (e) {
      console.error('Composio disconnect failed:', e);
      setError(`${app}: ${e?.message || 'Disconnect failed'}`);
    }
  };

  if (loading) return <div style={{ padding: 40, color: V.textDim }}>Loading connections...</div>;
  const shownError = error || loadError;

  return (
    <div style={{ padding: 20, overflowY: 'auto', height: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
        <h2 style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>Connections</h2>
        <button onClick={() => { setError(null); reload(); loadSources(); }} title="Refresh connection status" style={{
          padding: '6px 14px', borderRadius: 6, fontSize: 12, cursor: 'pointer',
          border: `1px solid ${V.border}`, background: V.bgCard, color: V.text,
        }}>↻ Refresh</button>
      </div>
      <p style={{ fontSize: 13, color: V.textDim, marginBottom: 16 }}>
        Connect accounts via OAuth (Composio) to enable agent tools. For mail, &ldquo;Learn about me&rdquo; is a separate
        switch: the agent only scans your mail for profile facts and dates while it is on.
      </p>
      {shownError && (
        <div style={{
          marginBottom: 16, padding: '10px 14px', borderRadius: 8, fontSize: 13,
          background: V.red + '11', border: `1px solid ${V.red}44`, color: V.red,
        }}>{shownError}</div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 12 }}>
        {composioApps.map(app => {
          const s = statuses[app.key];
          const connected = s?.connected === true;
          const src = sources.find(x => x.kind === app.key);
          const isMail = app.key === 'gmail' || app.key === 'outlook';
          return (
            <div key={app.key} style={{ background: V.bgCard, border: `1px solid ${V.border}`, borderRadius: 10, padding: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 20 }}>{app.icon}</span>
                  <span style={{ fontWeight: 600, fontSize: 14 }}>{app.name}</span>
                </div>
                <span style={{
                  display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 11,
                  background: connected ? V.green + '22' : V.red + '22',
                  color: connected ? V.green : V.red,
                }}>{connected ? 'Connected' : 'Not Connected'}</span>
              </div>
              <div style={{ fontSize: 12, color: V.textDim, marginBottom: 12 }}>{app.scopes.join(', ')}</div>
              {connected ? (
                <button onClick={() => handleDisconnect(app.key)} style={{
                  padding: '6px 16px', borderRadius: 6, fontSize: 12, cursor: 'pointer',
                  border: `1px solid ${V.red}44`, background: V.red + '11', color: V.red,
                }}>Disconnect</button>
              ) : (
                <button onClick={() => handleConnect(app.key)} disabled={connecting === app.key} style={{
                  padding: '6px 16px', borderRadius: 6, fontSize: 12, cursor: 'pointer',
                  border: `1px solid ${V.accent}`, background: V.accent, color: '#fff',
                  opacity: connecting === app.key ? 0.5 : 1,
                }}>{connecting === app.key ? 'Connecting...' : 'Connect'}</button>
              )}
              {isMail && (
                <div style={{ marginTop: 12, paddingTop: 10, borderTop: `1px solid ${V.border}`, fontSize: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                    <span>Learn about me: <b style={{ color: src?.learn ? V.green : V.textDim }}>{src?.learn ? 'on' : 'off'}</b></span>
                    {src?.learn ? (
                      <span style={{ display: 'flex', gap: 6 }}>
                        <button onClick={() => setLearn(app.key, false)} style={{ fontSize: 11, cursor: 'pointer' }}>Stop</button>
                        <button onClick={() => setLearn(app.key, false, true)} style={{ fontSize: 11, cursor: 'pointer', color: V.red }}>Stop + forget</button>
                      </span>
                    ) : (
                      <button onClick={() => setLearn(app.key, true)} style={{ fontSize: 11, cursor: 'pointer' }}>Turn on</button>
                    )}
                  </div>
                  <div style={{ color: V.textDim, marginTop: 4, fontFamily: 'monospace', fontSize: 11 }}>
                    {src?.scanned_at ? `scanned ${src.scanned_at.slice(0, 16)} · ${src.stats?.facts ?? 0} facts · ${src.stats?.dated ?? 0} dated · ${src.stats?.filtered_senders ?? 0} senders filtered` : 'never scanned'}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// USAGE PAGE
// ══════════════════════════════════════════════════════════════════════

function UsagePage() {
  const [balance, setBalance] = useState(null);
  const [sessions, setSessions] = useState({ items: [], total: 0, page: 1, page_size: 20 });
  const [loading, setLoading] = useState(true);
  const [expandedSession, setExpandedSession] = useState(null);
  const [details, setDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [usageTab, setUsageTab] = useState('billing'); // 'billing' | 'tools' — session detail table tabs
  const [sessionFilter, setSessionFilter] = useState('');

  useEffect(() => {
    api.request('/api/users/me/profile', {}, { retries: 1, timeout: 5000 })
      .then(d => setBalance(d?.balance ?? 0))
      .catch(() => {});
  }, []);

  const fetchSessions = async (page = 1, filterOverride) => {
    setLoading(true);
    try {
      const filter = filterOverride !== undefined ? filterOverride : sessionFilter;
      let url = `/api/users/me/usage/sessions?page=${page}&page_size=20`;
      if (filter.trim()) url += `&session_id=${encodeURIComponent(filter.trim())}`;
      const data = await api.request(url);
      setSessions(data || { items: [], total: 0, page, page_size: 20 });
    } catch { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => { fetchSessions(); }, []);

  const fetchSessionDetail = async (sessionId) => {
    setDetailsLoading(true);
    try {
      const data = await api.request(`/api/users/me/usage/sessions/${sessionId}?page_size=100`);
      setDetails(data);
    } catch {
      setDetails(null);
    }
    setDetailsLoading(false);
  };

  const toggleSession = async (sessionId) => {
    if (expandedSession === sessionId) {
      setExpandedSession(null);
      setDetails(null);
      return;
    }
    setExpandedSession(sessionId);
    setDetailsLoading(true);
    try {
      const data = await api.request(`/api/users/me/usage/sessions/${sessionId}?page_size=100`);
      // Guard against stale response if user switched sessions during fetch
      setExpandedSession(cur => {
        if (cur === sessionId) setDetails(data);
        return cur;
      });
    } catch {
      setExpandedSession(cur => {
        if (cur === sessionId) setDetails(null);
        return cur;
      });
    }
    setDetailsLoading(false);
  };

  return (
    <div style={{ padding: 20, overflowY: 'auto', height: '100%' }}>
      <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>Usage</h2>

      {/* Balance card */}
      <div style={{
        background: V.bgCard, border: `1px solid ${V.border}`, borderRadius: 10,
        padding: 16, marginBottom: 20,
      }}>
        <div style={{ fontSize: 12, color: V.textDim }}>Balance</div>
        <div style={{ fontSize: 24, fontWeight: 700, color: V.text }}>
          {balance != null ? `$${Number(balance).toFixed(2)}` : '...'}
          <span style={{ fontSize: 12, color: V.textDim, marginLeft: 6 }}>USD</span>
        </div>
      </div>

      {/* Session filter */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center' }}>
        <h3 style={{ fontSize: 14, fontWeight: 600, margin: 0 }}>Sessions</h3>
        <input
          placeholder="Search Session ID..."
          value={sessionFilter}
          onChange={e => setSessionFilter(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') fetchSessions(1); }}
          style={{
            flex: 1, maxWidth: 260, padding: '4px 8px', fontSize: 12,
            border: `1px solid ${V.border}`, borderRadius: 4,
            background: V.bgCard, color: V.text, outline: 'none',
          }}
        />
        <button
          onClick={() => fetchSessions(1)}
          style={{
            padding: '4px 10px', fontSize: 12, border: `1px solid ${V.border}`,
            borderRadius: 4, background: 'transparent', color: V.text, cursor: 'pointer',
          }}
        >Search</button>
        {sessionFilter && (
          <button
            onClick={() => { setSessionFilter(''); fetchSessions(1, ''); }}
            style={{
              padding: '4px 10px', fontSize: 12, border: `1px solid ${V.border}`,
              borderRadius: 4, background: 'transparent', color: V.textDim, cursor: 'pointer',
            }}
          >Clear</button>
        )}
      </div>
      {loading && <div style={{ color: V.textDim }}>Loading...</div>}
      {!loading && sessions.items.length === 0 && (
        <div style={{ color: V.textDim, padding: 20, textAlign: 'center' }}>No usage records</div>
      )}
      {sessions.items.map(s => (
        <div key={s.session_id} style={{ marginBottom: 8 }}>
          <div
            onClick={() => toggleSession(s.session_id)}
            style={{
              background: V.bgCard, border: `1px solid ${V.border}`, borderRadius: 8,
              padding: '12px 16px', cursor: 'pointer',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}
          >
            <div>
              <span style={{ fontFamily: 'monospace', fontSize: 11, wordBreak: 'break-all' }}>
                {s.session_id}
              </span>
              <span style={{ fontSize: 11, color: V.textDim, marginLeft: 12 }}>
                {s.request_count} calls
              </span>
            </div>
            <div style={{ textAlign: 'right' }}>
              <span style={{ fontWeight: 600 }}>${Number(s.total_cost).toFixed(4)}</span>
              <div style={{ fontSize: 10, color: V.textDim }}>
                {new Date(s.last_call_at).toLocaleString('zh-CN')}
              </div>
            </div>
          </div>

          {/* Expanded detail */}
          {expandedSession === s.session_id && (
            <div style={{
              background: V.bg, border: `1px solid ${V.border}`, borderTop: 'none',
              borderRadius: '0 0 8px 8px', padding: 12,
            }}>
              {detailsLoading && <div style={{ color: V.textDim, fontSize: 12 }}>Loading details...</div>}
              {details && !detailsLoading && (
                <>
                  <div style={{ fontSize: 12, marginBottom: 12, color: V.textDim }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>
                        Total: <span style={{ fontWeight: 600, color: V.text }}>${Number(details.total_cost || 0).toFixed(4)}</span> | {details.billing_count} billing items · {details.tool_count} tool calls
                      </span>
                      <button
                      onClick={(e) => { e.stopPropagation(); fetchSessionDetail(s.session_id); }}
                      style={{
                        padding: '2px 8px', fontSize: 11, border: `1px solid ${V.border}`,
                        borderRadius: 4, background: 'transparent', color: V.textDim, cursor: 'pointer',
                      }}
                    >Refresh</button>
                    </div>
                  </div>
                  {/* Tabs: Cost Breakdown (default) | MCP Tool Calls */}
                  <div style={{ display: 'flex', gap: 4, marginBottom: 10, borderBottom: `1px solid ${V.border}` }}>
                    {[['billing', `Cost Breakdown (${details.billing_count})`], ['tools', `MCP Tool Calls (${details.tool_count})`]].map(([k, label]) => (
                      <button key={k} onClick={(e) => { e.stopPropagation(); setUsageTab(k); }} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 10px', fontSize: 11, fontWeight: 600, color: usageTab === k ? V.text : V.textDim, borderBottom: usageTab === k ? '2px solid #6366f1' : '2px solid transparent', marginBottom: -1 }}>{label}</button>
                    ))}
                  </div>
                  {usageTab === 'billing' && (
                  <div style={{ overflowX: 'auto', marginBottom: 16 }}>
                    <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse', minWidth: 700 }}>
                      <thead>
                        <tr style={{ color: V.textDim, borderBottom: `2px solid ${V.border}` }}>
                          <th style={{ textAlign: 'left', padding: '4px 6px', fontWeight: 600 }}>Name</th>
                          <th style={{ textAlign: 'left', padding: '4px 6px', fontWeight: 600 }}>Type</th>
                          <th style={{ textAlign: 'right', padding: '4px 6px', fontWeight: 600 }}>Quantity</th>
                          <th style={{ textAlign: 'right', padding: '4px 6px', fontWeight: 600 }}>Cost</th>
                          <th style={{ textAlign: 'right', padding: '4px 6px', fontWeight: 600 }}>User Cost</th>
                          <th style={{ textAlign: 'center', padding: '4px 6px', fontWeight: 600 }}>Status</th>
                          <th style={{ textAlign: 'left', padding: '4px 6px', fontWeight: 600 }}>Time</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(details.billing_items || []).map((b, i) => (
                          <tr key={i} style={{ borderBottom: `1px solid ${V.border}22` }}>
                            <td style={{ padding: '3px 6px', whiteSpace: 'nowrap' }}>{b.name || '-'}</td>
                            <td style={{ padding: '3px 6px', color: V.textDim }}>{b.kind === 'model' ? 'Model' : b.kind === 'runtime' ? 'Runtime' : b.kind === 'service' ? 'Service' : b.kind === 'mcp' ? 'MCP' : b.kind}</td>
                            <td style={{ padding: '3px 6px', textAlign: 'right', color: V.textDim }}>{b.quantity != null ? `${Number(b.quantity).toLocaleString()} ${b.unit || ''}`.trim() : '-'}</td>
                            <td style={{ padding: '3px 6px', textAlign: 'right', color: V.textDim }}>${Number(b.raw_cost || 0).toFixed(6)}</td>
                            <td style={{ padding: '3px 6px', textAlign: 'right', fontWeight: 600 }}>${Number(b.cost || 0).toFixed(6)}</td>
                            <td style={{ padding: '3px 6px', textAlign: 'center' }}>{b.status === 'success' ? <span style={{ color: '#22c55e' }}>ok</span> : <span title={b.error || ''} style={{ color: '#ef4444' }}>{b.status || '-'}</span>}</td>
                            <td style={{ padding: '3px 6px', whiteSpace: 'nowrap', fontSize: 10, color: V.textDim }}>{b.occurred_at ? new Date(b.occurred_at).toLocaleString('zh-CN') : '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  )}
                  {usageTab === 'tools' && (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse', minWidth: 700 }}>
                      <thead>
                        <tr style={{ color: V.textDim, borderBottom: `2px solid ${V.border}` }}>
                          <th style={{ textAlign: 'left', padding: '4px 6px', fontWeight: 600 }}>Tool</th>
                          <th style={{ textAlign: 'left', padding: '4px 6px', fontWeight: 600 }}>Task ID</th>
                          <th style={{ textAlign: 'center', padding: '4px 6px', fontWeight: 600 }}>Status</th>
                          <th style={{ textAlign: 'right', padding: '4px 6px', fontWeight: 600 }}>Latency</th>
                          <th style={{ textAlign: 'left', padding: '4px 6px', fontWeight: 600 }}>Auth</th>
                          <th style={{ textAlign: 'right', padding: '4px 6px', fontWeight: 600 }}>Input</th>
                          <th style={{ textAlign: 'right', padding: '4px 6px', fontWeight: 600 }}>Output</th>
                          <th style={{ textAlign: 'left', padding: '4px 6px', fontWeight: 600 }}>Time</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(details.tool_calls || []).map((t, i) => (
                          <tr key={i} style={{ borderBottom: `1px solid ${V.border}22` }}>
                            <td style={{ padding: '3px 6px', whiteSpace: 'nowrap' }}>{t.tool_name || '-'}</td>
                            <td style={{ padding: '3px 6px', whiteSpace: 'nowrap', fontSize: 10, color: V.textDim }}>{t.external_task_id || '-'}</td>
                            <td style={{ padding: '3px 6px', textAlign: 'center' }}>{t.result_status === 'success' ? <span style={{ color: '#22c55e' }}>ok</span> : t.result_status === 'pending_async' ? <span style={{ color: '#3b82f6' }}>pending</span> : t.result_status === 'partial' ? <span title={t.error_message || ''} style={{ color: '#eab308', cursor: 'help' }}>partial</span> : <span title={t.error_message || ''} style={{ color: '#ef4444', cursor: 'help' }}>{t.result_status || 'error'}</span>}</td>
                            <td style={{ padding: '3px 6px', textAlign: 'right', color: V.textDim }}>{t.latency_ms != null ? `${t.latency_ms}ms` : '-'}</td>
                            <td style={{ padding: '3px 6px', color: V.textDim }}>{t.token_kind || '-'}</td>
                            <td style={{ padding: '3px 6px', textAlign: 'right' }}>{t.input_tokens || '-'}</td>
                            <td style={{ padding: '3px 6px', textAlign: 'right' }}>{t.output_tokens || '-'}</td>
                            <td style={{ padding: '3px 6px', whiteSpace: 'nowrap', fontSize: 10, color: V.textDim }}>{new Date(t.created_at).toLocaleString('zh-CN')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      ))}

      {/* Pagination */}
      {sessions.total > sessions.page_size && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
          <button
            disabled={sessions.page <= 1}
            onClick={() => fetchSessions(sessions.page - 1)}
            style={{
              padding: '4px 12px', border: `1px solid ${V.border}`, borderRadius: 4,
              background: 'transparent', color: V.text, cursor: 'pointer', fontSize: 12,
            }}
          >Prev</button>
          <span style={{ fontSize: 12, color: V.textDim, lineHeight: '28px' }}>
            {sessions.page} / {Math.ceil(sessions.total / sessions.page_size)}
          </span>
          <button
            disabled={sessions.page * sessions.page_size >= sessions.total}
            onClick={() => fetchSessions(sessions.page + 1)}
            style={{
              padding: '4px 12px', border: `1px solid ${V.border}`, borderRadius: 4,
              background: 'transparent', color: V.text, cursor: 'pointer', fontSize: 12,
            }}
          >Next</button>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// SESSION LIST KEBAB — "..." dropdown shown left of the "+ New Session"
// button. Single item for now ("Delete All Sessions"); easy to extend.
// ══════════════════════════════════════════════════════════════════════

function SessionListKebabMenu({ onDeleteAll }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen((v) => !v)}
        title="More"
        aria-label="More session actions"
        style={{
          background: 'none', border: `1px solid ${V.border}`, borderRadius: 4,
          color: V.textDim, cursor: 'pointer', fontSize: 13, padding: '2px 6px',
        }}
      >⋯</button>
      {open && (
        <div style={{
          position: 'absolute', top: 24, right: 0, minWidth: 180,
          background: V.bgCard, border: `1px solid ${V.border}`,
          borderRadius: 6, boxShadow: '0 4px 14px rgba(0,0,0,0.18)', padding: 4, zIndex: 5,
        }}>
          <button
            onClick={() => { setOpen(false); onDeleteAll?.(); }}
            style={{
              width: '100%', textAlign: 'left',
              background: 'transparent', color: V.red || '#FF3B30',
              border: 'none', borderRadius: 4, padding: '6px 10px',
              fontSize: 12, fontWeight: 600, cursor: 'pointer',
            }}
          >Delete All Sessions</button>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// MEMORY PAGE
// ══════════════════════════════════════════════════════════════════════

// MemoryPage — the user's v083 memory (log → reflect → inject): the injected
// brief + distilled typed rows + recent episodic sessions, from
// /api/users/memory-profile. Read-mostly; a row can be forgotten (soft archive).
// (Replaces the legacy Anthropic Memory Store file browser — memory is now
// server-side + runner-agnostic; see api-server user_memory.py.)
const MEM_TYPES = [
  ['identity', 'About you', '#0A84FF'],
  ['preference', 'Preferences', '#5E5CE6'],
  ['constraint', 'Important constraints', '#FF3B30'],
  ['goal', 'Goals', '#AF52DE'],
  ['instruction', 'How I should respond', '#FF9F0A'],
  ['relationship', 'People & things', '#32ADE6'],
  ['domain_fact', 'Widget details', '#34C759'],
];

const BRIEF_CAP_CHARS = 2000; // design §2.4: brief ≤ ~2k chars

// Developer view of the memory system (S5a): what a turn is injected, the typed profile rows with
// provenance, and the episodic log the rows were learned from. Three columns on wide screens.
function MemoryPage() {
  const [data, setData] = useState(null);
  const [logs, setLogs] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [filter, setFilter] = useState('');
  const [openLog, setOpenLog] = useState(null);
  const [showRaw, setShowRaw] = useState(false);
  const PAGE = 20;

  const reload = useCallback(() => {
    setLoading(true);
    api.getMemoryProfile({ logsOffset: 0, logsLimit: PAGE })
      .then((d) => { setData(d); setLogs(d?.session_logs || []); setHasMore(!!d?.session_logs_has_more); })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const loadMore = useCallback(async () => {
    setLoadingMore(true);
    try {
      const d = await api.getMemoryProfile({ logsOffset: logs.length, logsLimit: PAGE });
      setLogs((prev) => [...prev, ...(d?.session_logs || [])]);
      setHasMore(!!d?.session_logs_has_more);
    } finally { setLoadingMore(false); }
  }, [logs.length]);

  const forget = useCallback(async (id) => {
    setData((prev) => (prev ? { ...prev, memories: prev.memories.filter((m) => m.id !== id) } : prev));
    try { await api.forgetMemoryRow(id); } catch { reload(); }
  }, [reload]);

  const unfilter = useCallback(async (kind, domain) => {
    try { await api.unfilterSourceSender(kind, domain); } finally { reload(); }
  }, [reload]);

  const memories = data?.memories || [];
  const brief = (data?.injected_brief || data?.brief || '').trim();
  const q = filter.trim().toLowerCase();
  const shown = q ? memories.filter((m) => `${m.subject} ${m.content} ${m.source || ''}`.toLowerCase().includes(q)) : memories;
  const groups = MEM_TYPES
    .map(([t, label, color]) => ({ t, label, color, items: shown.filter((m) => m.type === t) }))
    .filter((g) => g.items.length);
  const agenda = data?.agenda || [];
  const sources = data?.sources || [];

  const card = { background: V.bgCard, border: `1px solid ${V.border}`, borderRadius: 10, padding: '12px 14px' };
  const head = (title, extra) => (
    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 8 }}>
      <span style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: V.textDim, fontWeight: 600 }}>{title}</span>
      {extra}
    </div>
  );
  const tag = (label, color = V.textDim, title) => (
    <span title={title} style={{ fontSize: 10, color, background: `${color}1A`, borderRadius: 6, padding: '1px 6px', whiteSpace: 'nowrap' }}>{label}</span>
  );
  const mono = { fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 10.5, color: V.textDim };
  const btn = { background: 'transparent', color: V.textDim, border: `1px solid ${V.border}`, borderRadius: 6, padding: '4px 9px', fontSize: 11, cursor: 'pointer' };
  const day = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '');

  return (
    <div style={{ height: '100%', overflow: 'auto', background: V.bg, color: V.text, fontFamily: V.fontUi }}>
      <div style={{ padding: '18px 20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
          <h2 style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>Memory</h2>
          {tag('developer view', '#FF9F0A')}
          <span style={{ flex: 1 }} />
          <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter profile rows…"
            style={{ width: 200, background: V.bgCard, color: V.text, border: `1px solid ${V.border}`, borderRadius: 6, padding: '5px 8px', fontSize: 12 }} />
          <button onClick={reload} style={btn}>↻ Refresh</button>
        </div>
        <p style={{ color: V.textDim, fontSize: 12, margin: '0 0 14px' }}>
          Profile (typed rows) · Memory (episodes + agenda) · Sources — and the brief a turn actually receives.
          {data?.brief_updated_at ? ` Brief consolidated ${new Date(data.brief_updated_at).toLocaleString()}.` : ''}
        </p>
        {loading && <div style={{ color: V.textDim, fontSize: 12 }}>Loading…</div>}
        {!loading && !data && <div style={{ ...card, color: V.red }}>Could not load memory.</div>}

        {!loading && data && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 14, alignItems: 'start' }}>
            {/* ── Column 1: what the model sees ── */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={card}>
                {head('Injected brief', (
                  <span style={{ ...mono, color: brief.length > BRIEF_CAP_CHARS ? V.red : V.textDim }}>
                    {brief.length} / {BRIEF_CAP_CHARS} chars
                    <button onClick={() => setShowRaw((v) => !v)} style={{ ...btn, marginLeft: 8, padding: '1px 6px', fontSize: 10 }}>{showRaw ? 'injected' : 'cached row'}</button>
                  </span>
                ))}
                <div style={{ fontSize: 12.5, lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>
                  {(showRaw ? data.brief : brief) || <span style={{ color: V.textDim }}>(empty)</span>}
                </div>
              </div>

              <div style={card}>
                {head('Agenda — upcoming', <span style={mono}>{agenda.length}</span>)}
                {agenda.length === 0 && <div style={{ fontSize: 12, color: V.textDim }}>Nothing upcoming.</div>}
                {agenda.map((a) => (
                  <div key={a.id} style={{ display: 'flex', gap: 8, alignItems: 'baseline', padding: '5px 0', borderTop: `1px solid ${V.border}`, fontSize: 12.5 }}>
                    <span style={{ ...mono, flex: '0 0 112px' }}>{a.when_local || '—'}</span>
                    <span style={{ flex: 1 }}>{a.title}</span>
                    {a.repeat !== 'none' && tag(a.repeat)}
                    {tag(a.kind, a.kind === 'reminder' ? '#FF9F0A' : '#32ADE6')}
                    {a.source && tag(a.source, V.textDim, a.source_ref || '')}
                  </div>
                ))}
              </div>

              <div style={card}>
                {head('Sources', <a href="/playground/connections" style={{ fontSize: 11, color: V.accent }}>Connections →</a>)}
                {sources.length === 0 && <div style={{ fontSize: 12, color: V.textDim }}>No source has "learn about me" on.</div>}
                {sources.map((src) => (
                  <div key={src.kind} style={{ padding: '6px 0', borderTop: `1px solid ${V.border}` }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12.5 }}>
                      <b>{src.kind}</b>
                      {tag(src.learn ? 'learning' : 'off', src.learn ? '#34C759' : V.textDim)}
                      <span style={mono}>{src.scanned_at ? `scanned ${src.scanned_at.slice(0, 16).replace('T', ' ')}` : 'never scanned'}</span>
                    </div>
                    <div style={{ ...mono, marginTop: 3 }}>{JSON.stringify(src.stats)}</div>
                    {src.widened_senders?.length > 0 && (
                      <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                        <span style={{ fontSize: 11, color: V.textDim }} title="Opened by the agent for the user's own request — learned from even in Promotions/Social">Widened on demand:</span>
                        {src.widened_senders.map((d) => (
                          <span key={d} style={{ ...mono, border: `1px solid ${V.border}`, borderRadius: 6, padding: '0 6px' }}>{d}</span>
                        ))}
                      </div>
                    )}
                    {src.filtered_senders.length > 0 && (
                      <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                        <span style={{ fontSize: 11, color: V.textDim }}>Auto-filtered senders:</span>
                        {src.filtered_senders.map((d) => (
                          <span key={d} style={{ ...mono, border: `1px solid ${V.border}`, borderRadius: 6, padding: '0 4px 0 6px' }}>
                            {d} <button title="Undo — read this sender again" onClick={() => unfilter(src.kind, d)} style={{ border: 'none', background: 'transparent', color: V.accent, cursor: 'pointer', fontSize: 11 }}>undo</button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* ── Column 2: profile rows ── */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {groups.length === 0 && <div style={{ ...card, fontSize: 12, color: V.textDim }}>No profile rows{q ? ' match' : ''}.</div>}
              {groups.map((g) => (
                <div key={g.t} style={card}>
                  {head(<><span style={{ display: 'inline-block', width: 7, height: 7, borderRadius: 4, background: g.color, marginRight: 6 }} />{g.label}</>, <span style={mono}>{g.items.length}</span>)}
                  {g.items.map((m) => (
                    <div key={m.id} style={{ padding: '6px 0', borderTop: `1px solid ${V.border}` }}>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                        <div style={{ flex: 1, fontSize: 12.5, lineHeight: 1.5 }}>{m.content}</div>
                        <button onClick={() => forget(m.id)} title="Forget (archived as user_forget; never re-learned)" style={{ background: 'transparent', border: 'none', color: V.textDim, cursor: 'pointer', fontSize: 14, lineHeight: 1 }}>×</button>
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 3 }}>
                        {m.polarity === 'positive' && tag('likes', '#34C759')}
                        {m.polarity === 'negative' && tag('avoids', '#FF3B30')}
                        {tag(m.confidence || 'inferred', m.confidence === 'explicit' ? '#5E5CE6' : V.textDim)}
                        {tag(`sal ${m.salience}`)}
                        {tag(m.evidence_count == null ? 'ev legacy' : `ev ${m.evidence_count}`)}
                        {m.source && tag(m.source, m.source === 'email' ? '#EA4335' : V.textDim, m.source_ref || '')}
                        {m.sensitivity === 'sensitive' && tag('sensitive', '#FF9500')}
                        {m.domain && tag(m.domain, '#34C759')}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>

            {/* ── Column 3: episodes (paged, lazy images) ── */}
            <div style={card}>
              {head('Episodes — session logs', <span style={mono}>{logs.length}{hasMore ? '+' : ''}</span>)}
              {logs.length === 0 && <div style={{ fontSize: 12, color: V.textDim }}>No session logs yet.</div>}
              {logs.map((l) => {
                const open = openLog === l.id;
                const text = l.summary_plain || l.summary || '';
                return (
                  <div key={l.id} style={{ padding: '8px 0', borderTop: `1px solid ${V.border}` }}>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 3 }}>
                      <span style={mono}>{day(l.created_at)}</span>
                      {tag(l.session_type)}
                      {l.summary_has_markup && tag('raw markup in stored summary', '#FF3B30', 'Logged before the LOG fix — the agent still sees this raw text until the session is re-logged')}
                      <span style={{ flex: 1 }} />
                      <button onClick={() => setOpenLog(open ? null : l.id)} style={{ ...btn, padding: '1px 6px', fontSize: 10 }}>{open ? 'less' : 'more'}</button>
                    </div>
                    <div style={{
                      fontSize: 12.5, lineHeight: 1.5,
                      ...(open ? {} : { display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }),
                    }}>{text || <span style={{ color: V.textDim }}>(no text)</span>}</div>
                    {open && l.facts?.length > 0 && (
                      <ul style={{ margin: '4px 0 0', paddingLeft: 16, fontSize: 11.5, color: V.textDim }}>
                        {l.facts.map((f, i) => <li key={i}>{f}</li>)}
                      </ul>
                    )}
                    {open && l.summary_has_markup && (
                      <pre style={{ ...mono, whiteSpace: 'pre-wrap', wordBreak: 'break-all', maxHeight: 160, overflow: 'auto', background: V.bg, padding: 6, borderRadius: 6, marginTop: 6 }}>{l.summary}</pre>
                    )}
                    {l.media?.length > 0 && (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(72px, 1fr))', gap: 6, marginTop: 6 }}>
                        {l.media.map((md, i) => (
                          <div key={i} title={md.desc || ''} style={{ position: 'relative', aspectRatio: '1', borderRadius: 6, overflow: 'hidden', background: V.bg, border: `1px solid ${V.border}` }}>
                            {md.url && (md.media_type || 'image') === 'image'
                              ? <img src={md.url} alt={md.desc || md.kind || 'media'} loading="lazy" decoding="async" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                              : <div style={{ ...mono, padding: 4, fontSize: 9.5, lineHeight: 1.3, overflow: 'hidden', height: '100%' }}>{md.media_type === 'video' ? '🎬 ' : '🖼 '}{md.desc || md.ref}</div>}
                            <span style={{ position: 'absolute', left: 3, bottom: 3, fontSize: 9, color: '#fff', background: 'rgba(0,0,0,0.55)', borderRadius: 4, padding: '0 4px' }}>{md.kind}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {open && l.media?.some((md) => md.desc) && (
                      <div style={{ marginTop: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {l.media.map((md, i) => md.desc && <div key={i} style={{ fontSize: 11, color: V.textDim }}>[{md.kind}] {md.desc}</div>)}
                      </div>
                    )}
                  </div>
                );
              })}
              {hasMore && (
                <button onClick={loadMore} disabled={loadingMore} style={{ ...btn, width: '100%', marginTop: 8 }}>{loadingMore ? 'Loading…' : 'Load older episodes'}</button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// ERRORS PAGE — preview UI for Issue #1520 error-handling primitives
// ══════════════════════════════════════════════════════════════════════

// Minimal error primitives (equivalent to Flutter ErrorBanner/ErrorFullPage)
function ErrorBanner({ message, icon = '⚠️', primaryLabel, onPrimary, secondaryLabel, onSecondary, background = V.red }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10,
      padding: '10px 14px', background, color: '#fff',
      fontSize: 13, fontWeight: 500,
    }}>
      <span style={{ fontSize: 16 }}>{icon}</span>
      <span style={{ flex: 1 }}>{message}</span>
      {primaryLabel && (
        <button
          onClick={onPrimary}
          style={{
            background: 'transparent', color: '#fff', border: 'none',
            textDecoration: 'underline', fontWeight: 600, cursor: 'pointer', fontSize: 13,
          }}
        >{primaryLabel}</button>
      )}
      {secondaryLabel && (
        <button
          onClick={onSecondary}
          style={{
            background: 'transparent', color: '#fff', border: 'none',
            textDecoration: 'underline', fontWeight: 600, cursor: 'pointer', fontSize: 13,
          }}
        >{secondaryLabel}</button>
      )}
    </div>
  );
}

function ErrorFullPage({ title, message, icon = '⚠️', primaryLabel, onPrimary, secondaryLabel, onSecondary, tertiaryLabel, onTertiary }) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      padding: 40, background: V.bg, minHeight: 340, borderRadius: 8,
    }}>
      <div style={{ fontSize: 56, marginBottom: 16, color: V.red }}>{icon}</div>
      <div style={{ fontSize: 20, fontWeight: 600, color: V.text, marginBottom: 8, textAlign: 'center' }}>{title}</div>
      {message && (
        <div style={{ fontSize: 14, color: V.textDim, marginBottom: 24, textAlign: 'center', maxWidth: 380 }}>{message}</div>
      )}
      {primaryLabel && (
        <button
          onClick={onPrimary}
          style={{
            width: 260, padding: '12px 16px', marginBottom: 10, borderRadius: 8,
            background: V.accent, color: '#fff', border: 'none', fontWeight: 600, fontSize: 14, cursor: 'pointer',
          }}
        >{primaryLabel}</button>
      )}
      {secondaryLabel && (
        <button
          onClick={onSecondary}
          style={{
            width: 260, padding: '12px 16px', marginBottom: 6, borderRadius: 8,
            background: V.bgCard, color: V.text, border: `1px solid ${V.border}`, fontWeight: 500, fontSize: 14, cursor: 'pointer',
          }}
        >{secondaryLabel}</button>
      )}
      {tertiaryLabel && (
        <button
          onClick={onTertiary}
          style={{
            padding: '8px 12px', background: 'transparent',
            color: V.text, border: 'none', fontWeight: 500, fontSize: 13, cursor: 'pointer',
          }}
        >{tertiaryLabel}</button>
      )}
    </div>
  );
}

function ErrorsPage() {
  const [flash, setFlash] = useState('');
  const [expanded, setExpanded] = useState(null); // scenario id currently previewing
  const [forcedOffline, setForcedOffline] = useState(false);

  const ping = useCallback((label) => {
    setFlash(`${label} — ${new Date().toLocaleTimeString()}`);
    setTimeout(() => setFlash(''), 2400);
  }, []);

  const scenarios = [
    {
      id: 'e14_1',
      code: 'E14.1',
      title: 'No internet',
      prd: 'Persistent top banner on connectivity loss, auto-dismiss on restore, input disabled while offline.',
      mode: 'banner',
      render: () => (
        <ErrorBanner
          message="No internet connection"
          icon="📶"
          background={V.red}
        />
      ),
    },
    {
      id: 'e4_2',
      code: 'E4.2',
      title: 'All images upload failed',
      prd: 'Full-screen error with Retry All / Go Back to Camera (no more hanging on readyForBrief).',
      mode: 'fullPage',
      render: () => (
        <ErrorFullPage
          icon="☁️"
          title="Upload failed"
          message="All images failed to upload. Check your connection and try again."
          primaryLabel="Retry All"
          onPrimary={() => ping('Retry All')}
          secondaryLabel="Go Back to Camera"
          onSecondary={() => ping('Go Back to Camera')}
        />
      ),
    },
    {
      id: 'e6_5',
      code: 'E6.5',
      title: 'Waiting stuck (no cards after exec)',
      prd: '30s timeout → "taking longer than usual" banner with Keep Waiting / Cancel.',
      mode: 'banner',
      render: () => (
        <ErrorBanner
          icon="⏳"
          message="Taking longer than usual..."
          background={V.yellow}
          primaryLabel="Keep Waiting"
          onPrimary={() => ping('Keep Waiting')}
          secondaryLabel="Cancel"
          onSecondary={() => ping('Cancel')}
        />
      ),
    },
    {
      id: 'e7_2',
      code: 'E7.2',
      title: 'SSE max reconnect exhausted',
      prd: 'Full-screen "Connection lost" with Retry Connection / View in History / Go Home (no silent-fail).',
      mode: 'fullPage',
      render: () => (
        <ErrorFullPage
          icon="📡"
          title="Connection lost"
          message="We couldn't reach the server after multiple attempts."
          primaryLabel="Retry Connection"
          onPrimary={() => ping('Retry Connection')}
          secondaryLabel="View in History"
          onSecondary={() => ping('View in History')}
          tertiaryLabel="Go Home"
          onTertiary={() => ping('Go Home')}
        />
      ),
    },
  ];

  const primitives = [
    {
      id: 'banner_plain',
      title: 'ErrorBanner — message only',
      render: () => <ErrorBanner message="Something went wrong" />,
    },
    {
      id: 'banner_actions',
      title: 'ErrorBanner — 2 actions',
      render: () => (
        <ErrorBanner
          message="Reconnecting..."
          background={V.blue}
          icon="🔄"
          primaryLabel="Retry"
          onPrimary={() => ping('Retry')}
          secondaryLabel="Cancel"
          onSecondary={() => ping('Cancel')}
        />
      ),
    },
    {
      id: 'fullpage_3',
      title: 'ErrorFullPage — 3 actions',
      render: () => (
        <ErrorFullPage
          title="Connection lost"
          message="We couldn't reach the server."
          primaryLabel="Retry Connection"
          onPrimary={() => ping('primary')}
          secondaryLabel="View in History"
          onSecondary={() => ping('secondary')}
          tertiaryLabel="Go Home"
          onTertiary={() => ping('tertiary')}
        />
      ),
    },
  ];

  return (
    <div style={{ height: '100%', overflowY: 'auto', padding: 24, background: V.bg, color: V.text, fontFamily: V.fontUi }}>
      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        <h1 style={{ fontSize: 22, fontWeight: 600, marginBottom: 6 }}>Error Playground</h1>
        <p style={{ fontSize: 13, color: V.textDim, marginBottom: 20 }}>
          Preview the error UI primitives and PRD scenarios for <a
            href="https://github.com/flair-home-stylist/vi_agent/issues/1520"
            target="_blank" rel="noreferrer"
            style={{ color: V.accent, textDecoration: 'none', fontWeight: 500 }}
          >Issue #1520</a>.
          Reference; final production rendering lives on the Flutter client.
        </p>

        {forcedOffline && (
          <div style={{ marginBottom: 14, borderRadius: 8, overflow: 'hidden' }}>
            <ErrorBanner message="No internet connection (simulated)" icon="📶" background={V.red} />
          </div>
        )}

        {flash && (
          <div style={{
            marginBottom: 14, padding: '8px 12px', borderRadius: 6,
            background: V.accentDim, color: V.accent, fontSize: 12, fontWeight: 500,
          }}>{flash}</div>
        )}

        <Section title="Live toggles">
          <Row
            title="Force offline banner"
            subtitle={forcedOffline ? 'Offline banner shown above for reference.' : 'Toggle to visualize the persistent offline banner.'}
            right={
              <input
                type="checkbox"
                checked={forcedOffline}
                onChange={(e) => setForcedOffline(e.target.checked)}
                style={{ width: 18, height: 18, cursor: 'pointer' }}
              />
            }
          />
        </Section>

        <Section title="Primitives">
          {primitives.map((p) => (
            <Card
              key={p.id}
              title={p.title}
              expanded={expanded === p.id}
              onToggle={() => setExpanded(expanded === p.id ? null : p.id)}
              renderBody={p.render}
            />
          ))}
        </Section>

        <Section title="Scenarios (PRD)">
          {scenarios.map((s) => (
            <Card
              key={s.id}
              title={`${s.code} — ${s.title}`}
              subtitle={s.prd}
              badge={s.mode}
              expanded={expanded === s.id}
              onToggle={() => setExpanded(expanded === s.id ? null : s.id)}
              renderBody={s.render}
            />
          ))}
        </Section>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginTop: 20 }}>
      <div style={{
        fontSize: 11, fontWeight: 600, letterSpacing: '0.08em',
        textTransform: 'uppercase', color: V.textDim, marginBottom: 8, paddingLeft: 4,
      }}>{title}</div>
      {children}
    </div>
  );
}

function Row({ title, subtitle, right }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10,
      background: V.bgCard, border: `1px solid ${V.border}`,
      borderRadius: 10, padding: '12px 14px', marginBottom: 10,
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: V.text }}>{title}</div>
        {subtitle && (
          <div style={{ fontSize: 12, color: V.textDim, marginTop: 2 }}>{subtitle}</div>
        )}
      </div>
      {right}
    </div>
  );
}

function Card({ title, subtitle, badge, expanded, onToggle, renderBody }) {
  return (
    <div style={{
      background: V.bgCard, border: `1px solid ${V.border}`,
      borderRadius: 10, marginBottom: 10, overflow: 'hidden',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '12px 14px',
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: V.text }}>{title}</span>
            {badge && (
              <span style={{
                fontSize: 10, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase',
                color: V.textDim, border: `1px solid ${V.border}`, padding: '2px 6px', borderRadius: 4,
              }}>{badge}</span>
            )}
          </div>
          {subtitle && (
            <div style={{ fontSize: 12, color: V.textDim, marginTop: 2 }}>{subtitle}</div>
          )}
        </div>
        <button
          onClick={onToggle}
          style={{
            padding: '6px 12px', borderRadius: 6, fontSize: 12, fontWeight: 500, cursor: 'pointer',
            background: expanded ? V.accent : V.bgHover,
            color: expanded ? '#fff' : V.text,
            border: `1px solid ${expanded ? V.accent : V.border}`,
          }}
        >{expanded ? 'Hide' : 'Preview'}</button>
      </div>
      {expanded && (
        <div style={{ borderTop: `1px solid ${V.border}`, background: V.bg }}>
          {renderBody()}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// LAYOUT — wraps NavSidebar + Outlet for route-based page switching
// ══════════════════════════════════════════════════════════════════════

function PlaygroundLayout() {
  // #1458 — NavSidebar fold, persisted in localStorage
  const [navCollapsed, setNavCollapsed] = useState(() => {
    try { return localStorage.getItem('playground.leftPanelCollapsed') === '1'; } catch { return false; }
  });
  const toggleNavCollapsed = useCallback(() => {
    setNavCollapsed(prev => {
      const next = !prev;
      try { localStorage.setItem('playground.leftPanelCollapsed', next ? '1' : '0'); } catch { /* ignore */ }
      return next;
    });
  }, []);
  return (
    <div className="pg-layout-root" style={{
      display: 'flex', height: '100vh', width: '100%',
      fontFamily: V.fontUi, background: V.bg, color: V.text,
      overflow: 'hidden',
    }}>
      <NavSidebar collapsed={navCollapsed} />
      <CollapsibleBoundary
        collapsed={navCollapsed}
        onToggle={toggleNavCollapsed}
        side="right"
        ariaLabel="Toggle navigation panel"
        borderColor={V.border}
        iconColor={V.textDim}
        hoverBg={V.bgHover}
      />
      <main className="pg-layout-main" style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
        <Outlet />
      </main>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// SHARED SESSION VIEW — public read-only view (no auth required)
// ══════════════════════════════════════════════════════════════════════

function SharedSessionView() {
  const { viUserId, sessionId, token } = useParams();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rawMode, setRawMode] = useState(false);
  const [perCardRaw, setPerCardRaw] = useState({});
  // Issue #1471: share view is GET-only. The Auto/GET/SSE/GET+SSE
  // dropdown that lived here before was a lie — there is no public SSE
  // endpoint, so SSE/GET+SSE silently collapsed to a single-shot GET and
  // confused viewers. The GET payload now carries full card state for
  // failed sessions, so the selector has no purpose on this view.

  useEffect(() => {
    setLoading(true);
    setError(null);
    // Token-based URL: /share/{token} → /api/public/share/{token}
    // Legacy URL: /share/{viUserId}/{sessionId} → /api/public/sessions/{viUserId}/{sessionId}
    const url = token
      ? `${api.getBaseUrl()}/api/public/share/${encodeURIComponent(token)}`
      : `${api.getBaseUrl()}/api/public/sessions/${encodeURIComponent(viUserId)}/${encodeURIComponent(sessionId)}`;
    fetch(url)
      .then(r => { if (!r.ok) throw new Error('Session not found'); return r.json(); })
      .then(data => {
        // FU-2: public-share fetch bypasses api.getSession, so normalize the
        // card template names here too — keeps the /playground/share/... view
        // visually consistent with the authenticated /playground/chat/... GET
        // view. Single source of truth: TEMPLATE_ALIASES in templateRenderer.js.
        data = normalizeSessionDetailCards(data);
        // Normalize to the same shape ChatPage uses
        const ts = new Date(data.started_at || 0).getTime();
        let html = data.result_html || '';
        if (!html && data.result?.cards) {
          const firstCard = Object.values(data.result.cards)[0];
          if (firstCard?.data?.html) html = firstCard.data.html;
        }
        setSession({
          sessionId: data.id,
          tasks: data.prompt ? [{ prompt: data.prompt, ts, sessionId: data.id, skillSlug: data.context?.skill_slug || null }] : [],
          latestTs: ts,
          status: data.status,
          title: data.title,
          result: data.result || null,
          result_html: html,
          result_summary: data.result_summary || '',
          timeline: data.timeline || [],
          artifacts: data.artifacts || [],
          thumbnail_url: data.thumbnail_url || null,
          context: data.context || null,
        });
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [viUserId, sessionId, token]);

  if (loading) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        height: '100vh', fontFamily: V.fontUi, color: V.textDim, fontSize: 14,
        background: V.bg,
      }}>Loading shared session...</div>
    );
  }
  if (error || !session) {
    return (
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        height: '100vh', fontFamily: V.fontUi, color: V.textDim, fontSize: 14,
        background: V.bg, gap: 12,
      }}>
        <span style={{ fontSize: 32, opacity: 0.3 }}>🔗</span>
        <span>{error || 'Session not found'}</span>
      </div>
    );
  }

  // Build sorted tasks and cards (same logic as ChatPage)
  const allCards = {};
  const cardTaskMap = {};
  const sortedTasks = [];

  if (session.timeline?.length > 0) {
    // Issue #1338: result.cards is the single source of truth. Timeline
    // rounds may hold {cardId: {ts}} refs — resolve each against
    // session.result.cards. Legacy sessions with full card objects inline
    // (carry `template`) are used in place for back-compat.
    const resultCardsStore = (session.result && session.result.cards) ? session.result.cards : {};
    session.timeline.forEach((round, i) => {
      const roundCards = {};
      if (round.cards) {
        for (const [cid, entry] of Object.entries(round.cards)) {
          const isLegacyFullCard = entry && typeof entry === 'object' && 'template' in entry;
          const full = isLegacyFullCard ? entry : resultCardsStore[cid];
          if (!full) continue; // ref with no resolution — skip
          const c = { ...full, status: full.status || 'finalized' };
          roundCards[cid] = c;
          allCards[cid] = c;
        }
      }
      // round.photos is an array of {url, width, height} objects (or bare
      // strings in legacy data). Normalise to plain URL strings — otherwise
      // <img src={object}> renders literally as "[object Object]" (#1462).
      // Mirrors the same normalisation ChatPage applies around line 2880.
      const task = {
        taskId: `round-${i}`, prompt: round.prompt || '', ts: new Date(round.ts || 0).getTime(),
        skillSlug: session.tasks?.[0]?.skillSlug, cards: roundCards,
        mediaUrls: (round.photos || []).map(p => typeof p === 'string' ? p : p?.url).filter(Boolean),
      };
      for (const cid of Object.keys(roundCards)) cardTaskMap[cid] = task;
      sortedTasks.push(task);
    });
  } else if (session.result?.cards) {
    const cards = {};
    for (const [cid, card] of Object.entries(session.result.cards)) {
      const c = { ...card, status: card.status || 'finalized' };
      cards[cid] = c;
      allCards[cid] = c;
    }
    const task = {
      taskId: session.tasks?.[0]?.taskId || 'task-0',
      prompt: session.tasks?.[0]?.prompt || '',
      ts: session.latestTs,
      skillSlug: session.tasks?.[0]?.skillSlug,
      cards,
    };
    for (const cid of Object.keys(cards)) cardTaskMap[cid] = task;
    sortedTasks.push(task);
  } else if (session.tasks?.length) {
    sortedTasks.push(...session.tasks.map(t => ({ ...t, cards: {} })));
  }

  // Collect debug-html cards across all tasks (Issue #1007). Rendered in a
  // collapsible DebugPanel at the bottom; main content stays clean.
  const sharedDebugCards = Object.entries(allCards)
    .filter(([, card]) => card.template === 'debug-html')
    .map(([id, card]) => ({ cardId: id, ...card }))
    .sort((a, b) => (a.ts || 0) - (b.ts || 0));

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', height: '100vh',
      fontFamily: V.fontUi, background: V.bg, color: V.text,
    }}>
      {/* Header */}
      <div style={{
        padding: '12px 24px', background: V.bgCard,
        borderBottom: `1px solid ${V.border}`,
        display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <span style={{ fontSize: 16 }}>🔗</span>
        <span style={{ fontWeight: 600, fontSize: 14 }}>Shared Session</span>
        <span style={{ fontSize: 12, color: V.textDim }}>
          {session.title || session.tasks?.[0]?.prompt?.slice(0, 60) || session.sessionId}
        </span>
        <span style={{ marginLeft: 'auto', fontSize: 11, color: V.textDim }}>
          {session.latestTs ? new Date(session.latestTs).toLocaleString() : ''}
        </span>
        <RawToggleButton active={rawMode} onClick={() => setRawMode(r => !r)} />
      </div>
      {/* Session content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px', maxWidth: 800, margin: '0 auto', width: '100%' }}>
        {sortedTasks.map((task, i) => {
          const taskCardIds = Object.keys(task.cards || {})
            // Partition out debug-html cards → rendered in DebugPanel below (Issue #1007)
            .filter(id => allCards[id]?.template !== 'debug-html')
            .sort((a, b) => {
              const tsA = allCards[a]?.ts || 0;
              const tsB = allCards[b]?.ts || 0;
              return tsA - tsB;
            });
          return (
            <div key={task.taskId || i}>
              {/* User message */}
              {task.prompt && (
                <div style={{ marginBottom: 16, maxWidth: '80%', marginLeft: 'auto' }}>
                  <div style={{
                    padding: '12px 16px', borderRadius: 12, fontSize: 14,
                    lineHeight: 1.5, wordBreak: 'break-word',
                    background: V.accent, color: '#fff', borderBottomRightRadius: 4,
                  }}>
                    {task.mediaUrls?.length > 0 && (
                      <div style={{ marginBottom: 8 }}>
                        {task.mediaUrls.map((url, j) => {
                          const safeUrl = normalizeMediaUrl(url);
                          return _isVideoUrl(safeUrl) ? (
                            <video key={j} src={safeUrl} controls playsInline preload="metadata" style={{
                              maxWidth: 200, maxHeight: 150, borderRadius: 8, margin: 2,
                            }} />
                          ) : (
                            <img key={j} src={safeUrl} alt="" style={{
                              maxWidth: 200, maxHeight: 150, borderRadius: 8, margin: 2,
                            }} onError={e => { e.target.style.display = 'none'; }} />
                          );
                        })}
                      </div>
                    )}
                    {task.prompt}
                  </div>
                  <div style={{ fontSize: 11, color: V.textDim, marginTop: 4, textAlign: 'right' }}>
                    {task.skillSlug && (
                      <span style={{
                        background: 'rgba(175,82,222,0.12)', color: V.purple,
                        padding: '2px 8px', borderRadius: 6, fontSize: 11, marginRight: 6,
                      }}>{formatSkillTag(task.skillSlug)}</span>
                    )}
                    {formatDateTime(task.ts)}
                  </div>
                </div>
              )}
              {/* Cards */}
              {taskCardIds.length > 0 && (
                <div style={{ marginBottom: 16, maxWidth: '80%', marginRight: 'auto' }}>
                  {taskCardIds.map(id => {
                    const card = allCards[id];
                    const cardTime = card.ts ? formatDateTime(card.ts) : '';

                    // Thinking + agent-speech cards — render as simple blocks without card frame
                    if (card.template === 'thinking' || card.template === 'thinking-process' || card.template === 'agent-speech') {
                      return (
                        <div key={id} style={{ marginTop: 6 }}>
                          <CardRenderer card={card} />
                          {cardTime && <div style={{ fontSize: 11, color: V.textDim, marginTop: 4, padding: '0 4px' }}>{cardTime}</div>}
                        </div>
                      );
                    }

                    // Content cards — bordered container with dynamic status badge + timestamp
                    const statusBadge = card.status === 'finalized' || card.status === 'done'
                      ? { bg: 'rgba(52,199,89,0.12)', color: V.green, text: 'done' }
                      : card.status === 'error' || card.status === 'failed'
                        ? { bg: 'rgba(255,59,48,0.12)', color: V.red, text: 'error' }
                        : card.status === 'stopped'
                          ? { bg: 'rgba(255,149,0,0.12)', color: '#ff9500', text: 'stopped' }
                          : { bg: 'rgba(52,199,89,0.12)', color: V.green, text: 'done' };
                    const showRaw = rawMode || perCardRaw[id];
                    return (
                      <div key={id} style={{
                        background: V.bg, border: `1px solid ${V.border}`,
                        borderRadius: 8, overflow: 'hidden', marginTop: 6,
                      }}>
                        <div style={{
                          display: 'flex', alignItems: 'center', gap: 8,
                          padding: '8px 12px', fontSize: 11, fontWeight: 600,
                          textTransform: 'uppercase', letterSpacing: '0.04em',
                          color: V.textDim, borderBottom: `1px solid ${V.border}`, background: V.bg,
                        }}>
                          {(() => {
                            const nodeType = card.data?.node?.type;
                            if (card.template === 'session_block' && nodeType) {
                              return (
                                <Link
                                  to={bootstrapHref(nodeType)}
                                  title={`Open ${nodeType} in the Component Bootstrap`}
                          target="_blank"
                          rel="noopener noreferrer"
                                  style={{ color: 'inherit', textDecoration: 'underline', textUnderlineOffset: 2 }}
                                >
                                  {nodeType}
                                </Link>
                              );
                            }
                            return card.template || 'card';
                          })()}
                          <span style={{ fontWeight: 400, fontSize: 10, marginLeft: 'auto' }}>
                            {cardTime}
                          </span>
                          <ProducerBadge producerModel={card.producerModel} producer={card.producer} />
                          {card.template !== 'ask-user-question' && (
                            <span style={{
                              background: statusBadge.bg, color: statusBadge.color,
                              padding: '2px 8px', borderRadius: 6, fontSize: 10,
                              fontWeight: 500,
                            }}>
                              {statusBadge.text}
                            </span>
                          )}
                          <RawToggleButton
                            active={showRaw}
                            onClick={() => setPerCardRaw(prev => ({ ...prev, [id]: !prev[id] }))}
                          />
                        </div>
                        <div style={cardBodyFrameStyle(card, showRaw)}>
                          {showRaw ? <RawDataView card={card} /> : <CardRenderer card={card} />}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              {/* Fallback: result_html */}
              {taskCardIds.length === 0 && session.result_html && i === sortedTasks.length - 1 && (
                <div style={{ marginBottom: 16, maxWidth: '80%', marginRight: 'auto' }}>
                  <PersistentHtmlRenderer streamingChunks={[session.result_html]} isStreaming={false} />
                </div>
              )}
            </div>
          );
        })}
        {/* Debug panel — agent-speak tool I/O, collapsed by default (Issue #1007) */}
        <DebugPanel cards={sharedDebugCards} />
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// LEGACY REDIRECT — /playground/widget/:widgetId → /playground/widget/studio/:widgetId
// ══════════════════════════════════════════════════════════════════════

function LegacyWidgetEditorRedirect() {
  const { widgetId } = useParams();
  return <Navigate to={`/playground/widget/studio/${widgetId}`} replace />;
}

// /playground/widget/studio — remembers the last-selected tab (Standard
// vs Templates) via localStorage and forwards to the URL-tagged route.
// Deep-links to /standard or /template skip this redirect entirely.
// Per Widget Rules.md, the URL slug is SINGULAR `template` to match the
// "Templates" UI tab; legacy `/templates` still redirects below for one
// release so deep-links survive.
function WidgetStudioTabRedirect() {
  let saved = null;
  try { saved = localStorage.getItem('vi:widget-studio-tab'); }
  catch { /* localStorage blocked — fall through to default */ }
  const tab = saved === 'template' ? 'template' : 'standard';
  return <Navigate to={`/playground/widget/studio/${tab}`} replace />;
}

// /playground/widget/studio/instance/:instanceId — instance-keyed entry into
// the studio's User Data mode. Log cards / suggestion cards / batch rows only
// know the widget_instance_id, so this resolves instance → widget and lands
// on /widget/studio/{widget_id}?data=user&instance={iid}. The old standalone
// Widget Use surface was removed; this is its one canonical replacement.
// Card-action payloads (action_navigate_to_widget/<id>) historically passed a
// WIDGET id here — on a 404 the param is treated as one.
function WidgetStudioInstanceResolver() {
  const { instanceId } = useParams();
  const navigate = useNavigate();
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const s = await widgetInstances.get(instanceId);
        if (!alive) return;
        navigate(
          `/playground/widget/studio/${s.widget_id}?data=user&instance=${encodeURIComponent(instanceId)}`,
          { replace: true },
        );
      } catch {
        if (!alive) return;
        navigate(`/playground/widget/studio/${instanceId}?data=user`, { replace: true });
      }
    })();
    return () => { alive = false; };
  }, [instanceId, navigate]);
  return (
    <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: V.textDim, fontSize: 13 }}>
      Opening widget…
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// MAIN — PlaygroundView (React Router)
// ══════════════════════════════════════════════════════════════════════

export default function PlaygroundView({ sessionStore, sseEvents, viUserId }) {
  return (
    <Routes>
      <Route path="/playground" element={<PlaygroundLayout />}>
        <Route index element={<Navigate to="chat" replace />} />
        <Route path="chat" element={<ChatPage sessionStore={sessionStore} sseEvents={sseEvents} viUserId={viUserId} />} />
        <Route path="chat/:sessionId" element={<ChatPage sessionStore={sessionStore} sseEvents={sseEvents} viUserId={viUserId} />} />
        <Route path="sessions/:sessionId/evidence" element={<CharlotteSessionEvidencePage />} />
        {/* Widget routes — Studio is BOTH the creator surface (Standard /
            Templates tabs, URL-tagged so deep-links survive) and, since the
            standalone Widget Use surface was removed, the consumer surface
            (the editor's preview runs the real runtime in ?data=user mode). */}
        <Route path="widget" element={<Navigate to="/playground/widget/studio" replace />} />
        <Route path="widget/studio" element={<WidgetStudioTabRedirect />} />
        <Route path="widget/studio/standard" element={<WidgetCatalog widgetType="standard" />} />
        <Route path="widget/studio/template" element={<WidgetCatalog widgetType="template" />} />
        {/* Host preset galleries — read-only views of the available cover/landing presets. */}
        <Route path="widget/studio/landing-presets" element={<WidgetPresetGallery kind="landing" />} />
        <Route path="widget/studio/cover-presets" element={<WidgetPresetGallery kind="cover" />} />
        {/* Legacy plural — redirect to the canonical singular URL. */}
        <Route path="widget/studio/templates" element={<Navigate to="/playground/widget/studio/template" replace />} />
        <Route path="widget/studio/instance/:instanceId" element={<WidgetStudioInstanceResolver />} />
        <Route path="widget/studio/:widgetId" element={<WidgetEditor />} />
        {/* Legacy Widget Use routes — surface removed; forward into the
            studio's User Data mode so old bookmarks keep working. */}
        <Route path="widget/use" element={<Navigate to="/playground/widget/studio" replace />} />
        <Route path="widget/use/:instanceId" element={<WidgetStudioInstanceResolver />} />
        <Route path="widget/:widgetId" element={<LegacyWidgetEditorRedirect />} />
        {/* NATIVE ViewIR studio — a sibling surface to /widget/studio, not a
            replacement: these widgets render from a blueprint + the native
            component kit instead of an HTML bundle. */}
        <Route path="widget-native" element={<Navigate to="/playground/widget-native/studio" replace />} />
        <Route path="widget-native/studio" element={<Navigate to="/playground/widget-native/studio/standard" replace />} />
        <Route path="widget-native/studio/standard" element={<WidgetNativeStudio kind="standard" />} />
        <Route path="widget-native/studio/bootstrap" element={<WidgetNativeStudio kind="bootstrap" />} />
        <Route path="intention" element={<IntentionPage />} />
        <Route path="cards" element={<CardsPage />} />
        <Route path="tools" element={<ToolsPage />} />
        <Route path="apis" element={<ApisPage />} />
        <Route path="usage" element={<UsagePage />} />
        <Route path="memory" element={<MemoryPage />} />
        <Route path="benchmark" element={<BenchmarkPage />} />
        <Route path="batch" element={<BatchProgressPage />} />
        <Route path="batch/:batchId" element={<BatchProgressPage />} />
        <Route path="errors" element={<ErrorsPage />} />
        <Route path="connections" element={<ConnectionsPage />} />
        <Route path="composio" element={<Navigate to="/playground/connections" replace />} />
        <Route path="routines" element={<RoutinesPage />} />
        <Route path="browser-handoff" element={<BrowserHandoffPage />} />
      </Route>
      <Route path="/playground/share/:token" element={<SharedSessionView />} />
      <Route path="/playground/share/:viUserId/:sessionId" element={<SharedSessionView />} />
    </Routes>
  );
}
